/**
 * Interfaz del importador mínimo (F1a·0).
 *
 * Sin marco, por ADR-0009: esto es un selector de fichero, una barra de progreso y una tabla.
 *
 * Lo que este módulo **no** importa es tan importante como lo que importa: no hay ninguna
 * referencia a `ingestion/`, así que la presentación no puede parsear ni analizar. Es la regla
 * WP-001/WP-002 sostenida por el grafo de dependencias y no por la disciplina de quien edita.
 */

import {
  PROTOCOL_VERSION,
  isCurrent,
  type AccumulationReport,
  type CaseAction,
  type CaseView,
  type CaseViews,
  type CircuitViews,
  type FromWorker,
  type MemoryViews,
  type PlanAction,
  type SourceSummary,
  type ToWorker,
} from "../application/protocol.js";
import { EXPECTED_STRUCTURE, FUNCTION_MEANING, LIST_PURPOSE } from "../domain/tag-lists.js";
import { listCleanupCsv } from "../domain/list-cleanup.js";
import { lineStopsCsv } from "../domain/line-feed.js";
import { INCIDENT_KIND_TEXT, incidentsCsv } from "../domain/incident-battery.js";
import {
  activityChart,
  coverageChart,
  hourlyChart,
  inventoryChart,
  lazyTable,
  plainTable,
  scrollBox, plural, countedLabel } from "./charts.js";
import { closeDrawer } from "./drawer.js";
import { appCss, openReportDialog, type ReportSource } from "./report.js";
import { buildCaseReportHtml, caseReportFileName } from "./case-report.js";
import { chronologyCsv } from "../domain/case-export.js";
import { statusSentence, type Status } from "./report-text.js";
import { APP_VERSION } from "../application/version.js";
import { changedTagsOf, deltaAround, evolutionDataOf, evolutionSection, ringDataFromSnapshot, sourceStatusList, timeControl } from "./evolution.js";
import type { CircuitSnapshot } from "../domain/snapshot.js";
import { FLEET_STRUCTURE } from "../domain/fleet.js";
import {
  agvTimelineChart,
  connectionHeatChart,
  deliveryHeatChart,
  driftChart,
  hourHistogramChart,
  dwellChart,
  evolutionChart,
  ringFigure,
  type RingFigure,
  fifoSlopeChart,
  fleetCountChart,
  fleetLifelineChart,
  forkChart,
  laneOccupancyChart,
  readMatrixHeatmap,
  segmentBandChart,
  trendMultiplesChart,
  type BandRow,
  type DwellRow,
  type ForkData,
  type Formats,
  type RingData,
  type RingMark,
  type TrendPanel,
} from "./diagnostic-charts.js";
import { PROVISIONAL_CONFIG } from "../domain/config.js";
import { agvFromFileName, CONNECTION_STRUCTURE } from "../domain/connection-log.js";
import { CARDS_SHOWN } from "../domain/finding-kinds.js";
import { bandsCsv } from "../domain/segment-bands.js";
import { anchorSectionsCsv } from "../domain/anchor-sections.js";
import { describeGap, gapLineFor, renderFranjas } from "./franjas-ui.js";
import { changedTags, type AnchorGapChange } from "../domain/anchor-sums.js";
import type { QuarantinedRow } from "../domain/reading.js";
import { wallClockToUtc, type FieldOrder } from "../domain/time.js";
import type { IncidentCut } from "../domain/incident-cut.js";
import { ProjectError, readIncidentsSection, readMemorySection, readPlanSection, readPlantValuesSection, readProject, writeProject } from "../persistence/agvproj.js";
import { exportProjectMemory, importProjectMemory } from "../persistence/project-memory.js";
import { exportProjectPlan, importProjectPlan } from "../persistence/project-plan.js";
import { exportProjectPlantValues, importProjectPlantValues } from "../persistence/project-plant-values.js";
import { exportProjectIncidents, importProjectIncidents } from "../persistence/project-incidents.js";
import { caseImportText } from "../domain/case-transfer.js";
import { ensureStore, isAvailable, isStorageFull, loadCircuit, loadReviews, loadSnapshots, STORE_VERSION } from "../persistence/store.js";
import { createReviewSession, type ReviewSession } from "./review-ui.js";
import { createBenchPanel, type BenchObserver } from "./bench-ui.js";
import { startUpdates } from "./update.js";
import { createStoragePanel } from "./storage-ui.js";
import { EMPTY_COLUMNS, filterReadings, readingRow, type ReadingColumns } from "../application/reading-columns.js";
import { LINEAGE_LABEL, createMemoryPanel, type FindingsStatus, type MemoryWorkingFile } from "./memory-ui.js";
import { createPlanPanel, planRelationText } from "./plan-ui.js";
import { createPlantValuesPanel, plantValuesRelationText, type PlantValueSendRequest } from "./plant-values-ui.js";
import { createCasesPanel, type CaseStart } from "./cases-ui.js";
import {
  RANK_LABEL,
  THEMES,
  THEME_LABEL,
  criticalFunctionLabel,
  findingKindOf,
  orderChangeLabel,
  type FindingRank,
  type Theme,
  orderReadingLabel,
  patternLabel,
  truthLabel,
  verdictLabel,
  zoneLabel,
} from "./labels.js";

/** Zona horaria del piloto. Es configuración: vivirá en el circuito cuando exista (F1b). */
const ZONE = "Europe/Madrid";

/** Filas que se pintan de golpe. La tabla crece al desplazarse, no de una vez. */
const PAGE_SIZE = 200;

interface State {
  worker: Worker | null;
  jobId: string | null;
  summary: SourceSummary | null;
  /** Las lecturas de la última importación, por columnas (3.84.0). */
  readings: ReadingColumns;
  /** Lo que la tabla está mostrando: todas las lecturas, o las que pasan el filtro. */
  /** Las posiciones que pasan el filtro de la tabla; `null`, todas. */
  visible: Uint32Array | null;
  quarantine: readonly QuarantinedRow[];
  warnings: readonly string[];
  shown: number;
  /** El último fichero elegido, para poder reintentar con el orden de fecha que fije el usuario. */
  file: File | null;
  /** Cobertura del circuito, que las vistas necesitan para dibujar los huecos como lo que son. */
  coverage: readonly { readonly from: number; readonly to: number }[];
  /** Últimas vistas recibidas: el expediente y el replay se consultan sobre ellas, ya calculadas. */
  views: CircuitViews | null;
  /** El último historial de flota elegido, para repetir la carga con el circuito que elija el usuario. */
  fleetFile: File | null;
  /** El circuito cuyas vistas se están enseñando, si la fuente se acumuló en uno. Sin él no se revisa. */
  circuitId: string | null;
  /** Ficheros acumulados en el circuito, para la portada: los que dice «Lo acumulado». */
  sources: number;
  /** Las instantáneas del circuito (ADR-0015), en orden de ventana, leídas del almacén tras cada importación. */
  snapshots: readonly CircuitSnapshot[];
  /** Qué operación de memoria (F4) espera respuesta del Worker, para saber a quién contarle el error. */
  memoryJob: "preview" | "commit" | "revoke" | "resolve-fork" | "compare-versions" | null;
  /** Una acción sobre el plano físico (ADR-0016) espera respuesta del Worker. */
  planJob: boolean;
  /** Un valor de planta confirmado (OQ-140) espera respuesta del Worker. */
  plantValueJob: boolean;
  /** Una acción sobre un expediente (ADR-0018) espera respuesta del Worker. */
  caseJob: boolean;
  /** Hay una importación en curso que todavía no ha empezado a escribir: se puede detener (WP-004). */
  importStoppable: boolean;
  /** Los expedientes del circuito tal como llegaron por última vez, para el informe del circuito. */
  cases: CaseViews | null;
}

const state: State = {
  worker: null,
  jobId: null,
  summary: null,
  readings: EMPTY_COLUMNS,
  visible: null,
  quarantine: [],
  warnings: [],
  shown: 0,
  file: null,
  coverage: [],
  views: null,
  fleetFile: null,
  circuitId: null,
  sources: 0,
  snapshots: [],
  memoryJob: null,
  planJob: false,
  plantValueJob: false,
  caseJob: false,
  importStoppable: false,
  cases: null,
};

const app = document.querySelector<HTMLElement>("#app");
if (app === null) throw new Error("Falta el contenedor #app");

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  // Siempre como texto: un valor importado nunca se interpreta como marcado (TH-007).
  if (text !== undefined) node.textContent = text;
  return node;
}

function formatInstant(utcMs: number): string {
  return new Intl.DateTimeFormat("es-ES", {
    timeZone: ZONE,
    dateStyle: "short",
    timeStyle: "medium",
  }).format(new Date(utcMs));
}

/** El instante corto de un eje: día de la semana y hora, en la zona del circuito. */
function formatTick(utcMs: number): string {
  return new Intl.DateTimeFormat("es-ES", {
    timeZone: ZONE,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(utcMs));
}

const FORMATS: Formats = { instant: formatInstant, tick: formatTick };

/** Un instante como valor de un campo `datetime-local` (`aaaa-mm-ddThh:mm:ss`), en la zona del circuito. */
function toDateTimeInput(utcMs: number): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcMs));
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((entry) => entry.type === type)?.value ?? "00";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}:${part("second")}`;
}

/** El valor de un campo `datetime-local`, leído en la zona del circuito; `null` si no es una fecha. */
function fromDateTimeInput(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(value);
  if (match === null) return null;
  const [year, month, day, hour, minute, second] = match.slice(1).map((field) => Number(field ?? "0")) as [number, number, number, number, number, number];
  return wallClockToUtc({ year, month, day, hour, minute, second }, ZONE).utcMs;
}

// --- Regiones de la página -------------------------------------------------

const header = element("header", "bar");
const title = element("h1", undefined, "TAG TRACE");
const subtitle = element(
  "p",
  "muted",
  "Diagnóstico de circuitos AGV · los datos no salen de este dispositivo",
);
// La versión abierta, a la vista: lo que se publica se puede identificar (NFR-016).
const versionNote = element("p", "muted app-version", `Versión ${APP_VERSION}`);
header.append(title, subtitle, versionNote);
const updates = startUpdates({ currentVersion: APP_VERSION, storeVersion: STORE_VERSION });
/** La clave de sesión del aviso «Circuito borrado», que sobrevive a la recarga tras borrarlo. */
const DELETED_NOTICE = "tag-trace-circuito-borrado";

// El banco de medida (F6): la importación de verdad, sin circuito, observada desde la página.
let benchObserver: BenchObserver | null = null;
const benchPanel = createBenchPanel({
  startImport: (file) => startImport(file, undefined, true),
  cancel: () => cancelButton.click(),
  observe: (observer) => {
    benchObserver = observer;
  },
  appVersion: APP_VERSION,
  saveDownload: (text, type, name) => saveDownload(text, type, name),
});

/**
 * Un selector de fichero que parece un botón propio. El `input` nativo sigue existiendo con su
 * `id` —es lo que usan las pruebas y los lectores de pantalla—, pero se saca de la vista y lo que
 * se ve es su etiqueta con aspecto de botón y el nombre del fichero elegido. Sin esto, el navegador
 * pinta «Choose File» en su idioma, al lado de botones en español.
 */
function filePicker(input: HTMLInputElement): HTMLElement {
  const wrapper = element("span", "file-picker");
  const button = element("label", "file-button", "Elegir fichero…");
  button.htmlFor = input.id;
  const name = element("span", "file-name", "Ningún fichero elegido");
  const show = (): void => {
    name.textContent = input.files?.[0]?.name ?? "Ningún fichero elegido";
  };
  input.addEventListener("change", show);
  input.addEventListener("click", () => queueMicrotask(show));
  wrapper.append(input, button, name);
  return wrapper;
}

const picker = element("section", "panel");
const fileInput = element("input");
fileInput.type = "file";
fileInput.accept = ".csv,.tsv,.txt,.xlsx,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
fileInput.id = "source-file";
const fileLabel = element("label", undefined, "Fichero de lecturas");
fileLabel.htmlFor = "source-file";

// El circuito es lo que convierte «importar un fichero» en «acumular». Con una ventana de servidor
// de pocos días, lo que no se acumule aquí se pierde: por eso el campo va junto al selector y no
// escondido en una pantalla aparte.
const circuitInput = element("input");
circuitInput.type = "text";
circuitInput.id = "circuit-name";
circuitInput.placeholder = "nombre del circuito";
const circuitLabel = element("label", undefined, "Circuito");
circuitLabel.htmlFor = "circuit-name";
const exportButton = element("button", undefined, "Exportar circuito (.agvproj)");
exportButton.type = "button";
const projectInput = element("input");
projectInput.type = "file";
projectInput.accept = ".agvproj";
projectInput.id = "project-file";
const projectLabel = element("label", undefined, "Abrir un circuito exportado (.agvproj)");
projectLabel.htmlFor = "project-file";
const cancelButton = element("button", "danger", "Cancelar");
cancelButton.type = "button";
cancelButton.hidden = true;
picker.append(fileLabel, filePicker(fileInput), circuitLabel, circuitInput, cancelButton);
const projectPanel = element("section", "panel");
// El espacio del dispositivo y borrar un circuito (`UX_SPEC.md` §8): junto a la copia, que es lo que
// se ofrece antes de borrar.
const storagePanel = createStoragePanel({
  circuitName: () => circuitInput.value.trim(),
  lock: (busy) => setBusy(busy),
  notify: (kind, title, lines) => showMessage(kind, title, lines),
  onDeleted: (circuitId) => {
    // Si lo que se ve es el circuito borrado, se vuelve a la portada para no enseñar datos que ya no
    // existen; el aviso sobrevive a la recarga.
    if (state.circuitId !== circuitId) return;
    try {
      sessionStorage.setItem(DELETED_NOTICE, circuitId);
    } catch {
      // Sin almacenamiento de sesión, la recarga simplemente no repite el aviso.
    }
    window.location.reload();
  },
});
projectPanel.append(element("h2", undefined, "Copia del circuito"), exportButton, projectLabel, filePicker(projectInput), storagePanel.node);

const progressPanel = element("section", "panel");
progressPanel.hidden = true;
const progressNote = element("p", "muted", "");
const progressBar = element("progress");
progressBar.max = 1;
progressBar.value = 0;
progressPanel.append(progressNote, progressBar);

const messagePanel = element("section", "panel");
messagePanel.hidden = true;

const summaryPanel = element("section", "panel");
summaryPanel.hidden = true;

/**
 * Las listas de tags del circuito, con su estructura a la vista **antes** de pedir el fichero.
 *
 * No hay forma de descargarlas del sistema de planta: se escriben a mano. Cuando el formato lo
 * decide quien escribe el fichero, enseñarle el que esperamos es la diferencia entre que acierte a
 * la primera y que adivine tres veces. Por eso el ejemplo va aquí y no en un mensaje de error.
 */
const listsPanel = element("section", "panel");
const listsInput = element("input");
listsInput.type = "file";
listsInput.accept = ".xlsx,.csv,.txt,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
listsInput.id = "lists-file";
const listsLabel = element("label", undefined, "Listas de tags");
listsLabel.htmlFor = "lists-file";
const listsNote = element("p", "muted", "");

/**
 * El historial de flota (DS-012): qué AGV está asignado al circuito y desde cuándo. Va junto a las
 * listas porque también es una declaración de planta escrita a mano, y se enseña su forma antes de
 * pedirlo por la misma razón.
 */
const fleetInput = element("input");
fleetInput.type = "file";
fleetInput.accept = ".xlsx,.csv,.txt,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
fleetInput.id = "fleet-file";
const fleetLabel = element("label", undefined, "Historial de flota");
fleetLabel.htmlFor = "fleet-file";
const fleetNote = element("p", "muted", "");

/**
 * El registro de conexiones del terminal (DS-013, ADR-0017): un fichero por AGV, varios de una vez.
 * El AGV va en el nombre del fichero y se enseña antes de confirmar, corregible (OQ-160).
 */
const connectionsInput = element("input");
connectionsInput.type = "file";
connectionsInput.multiple = true;
connectionsInput.accept = ".xlsx,.csv,.txt,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
connectionsInput.id = "connections-file";
const connectionsLabel = element("label", undefined, "Registro de conexiones (un fichero por AGV)");
connectionsLabel.htmlFor = "connections-file";
const connectionsConfirm = element("div", "connections-confirm");
connectionsConfirm.hidden = true;
const connectionsNote = element("p", "muted", "");

{
  listsPanel.append(element("h2", undefined, "Listas del circuito"), listsLabel, filePicker(listsInput));
  const structure = element("details");
  structure.append(element("summary", undefined, "Qué forma tiene que tener el fichero"));
  structure.append(
    element(
      "p",
      "muted",
      `Una fila por tag, con cabecera. Obligatorias: «${EXPECTED_STRUCTURE.header.join("», «")}»; ` +
        `opcionales: «${EXPECTED_STRUCTURE.optional.join("», «")}».`,
    ),
  );
  const example = element("pre", "mono raw", EXPECTED_STRUCTURE.example.join("\n"));
  example.style.overflowX = "auto";
  structure.append(example);
  const purposes = element("dl", "facts");
  for (const list of EXPECTED_STRUCTURE.lists) {
    purposes.append(
      element("dt", "mono", list),
      element("dd", undefined, LIST_PURPOSE[list]),
    );
  }
  structure.append(purposes);
  structure.append(
    element(
      "p",
      "muted",
      "Una lista con otro nombre se conserva y se avisa.",
    ),
  );
  listsPanel.append(structure, listsNote);

  const fleetStructure = element("details");
  fleetStructure.append(element("summary", undefined, "Qué forma tiene que tener el historial"));
  fleetStructure.append(
    element(
      "p",
      "muted",
      `Una fila por AGV y periodo, con cabecera «${FLEET_STRUCTURE.header.join(";")}». Fechas ` +
        "día/mes/año, hora opcional; «hasta» vacío = sigue asignado. Cada carga se suma a la " +
        "anterior: basta subir las filas que cambian.",
    ),
  );
  const fleetExample = element("pre", "mono raw", FLEET_STRUCTURE.example.join("\n"));
  fleetExample.style.overflowX = "auto";
  fleetStructure.append(fleetExample);
  listsPanel.append(fleetLabel, filePicker(fleetInput), fleetStructure, fleetNote);

  const connectionsStructure = element("details");
  connectionsStructure.append(element("summary", undefined, "Qué forma tiene que tener el registro"));
  connectionsStructure.append(
    element(
      "p",
      "muted",
      `La exportación del terminal de cada AGV, tal cual: una fila por evento con «Fecha» y «Conexión» ` +
        `(Desconexión, Conexión, Conexión tras apagado); «${CONNECTION_STRUCTURE.optional.join("», «")}» se conservan. ` +
        `El fichero no trae el AGV: va en su nombre (${CONNECTION_STRUCTURE.fileName}) y se enseña antes de cargar, por si hay ` +
        "que corregirlo. Se pueden elegir varios ficheros de una vez; el mismo fichero cargado otra vez sustituye a su carga anterior.",
    ),
  );
  const connectionsExample = element("pre", "mono raw", CONNECTION_STRUCTURE.example.join("\n"));
  connectionsExample.style.overflowX = "auto";
  connectionsStructure.append(connectionsExample);
  listsPanel.append(connectionsLabel, filePicker(connectionsInput), connectionsStructure, connectionsConfirm, connectionsNote);
}

/**
 * Las pestañas por pregunta (UX_SPEC §2): Resumen, Tags, AGV, Tiempos, Línea y calles, Datos. Una
 * página de cuarenta y tres secciones en el orden en que se calculan medía 36.646 px sin abrir nada
 * y «Lo que hay que mirar» era la sección 22; ahora cada pregunta tiene su pestaña y la activa va en
 * el `hash` de la URL, así que recargar la conserva.
 */
const TABS = [
  ["resumen", "Resumen"],
  ["tags", "Tags"],
  ["agv", "AGV"],
  ["wifi", "WiFi"],
  ["tiempos", "Tiempos"],
  ["linea", "Línea y calles"],
  ["memoria", "Memoria"],
  ["datos", "Datos"],
] as const;
type TabId = (typeof TABS)[number][0];
const TAB_IDS: readonly TabId[] = TABS.map(([id]) => id);

const tabsBar = element("nav", "tabs");
tabsBar.setAttribute("aria-label", "Secciones del análisis");
const tabList = element("div", "tablist");
tabList.setAttribute("role", "tablist");
const tabButtons = new Map<TabId, HTMLButtonElement>();
const tabPanels = new Map<TabId, HTMLElement>();
/** Donde escribe cada pestaña lo que sale de las vistas. Se vacían y se rehacen en cada importación. */
const viewsOf = new Map<TabId, HTMLElement>();
for (const [id, label] of TABS) {
  const button = element("button", undefined, label);
  button.type = "button";
  button.id = `tab-${id}`;
  button.setAttribute("role", "tab");
  button.setAttribute("aria-selected", "false");
  button.setAttribute("aria-controls", `panel-${id}`);
  button.tabIndex = -1;
  // La WiFi nace escondida: solo existe con un registro de conexiones cargado (UX_SPEC §2).
  button.hidden = id === "wifi";
  button.addEventListener("click", () => activateTab(id));
  tabButtons.set(id, button);
  tabList.append(button);
  const panel = element("section", "tabpanel");
  panel.id = `panel-${id}`;
  panel.setAttribute("role", "tabpanel");
  panel.setAttribute("aria-labelledby", `tab-${id}`);
  panel.hidden = true;
  tabPanels.set(id, panel);
  const views = element("div", "panel views");
  views.hidden = true;
  viewsOf.set(id, views);
}
// Flechas entre pestañas, y activación al mover el foco (es lo que espera un `tablist`).
tabList.addEventListener("keydown", (event) => {
  const tabs = visibleTabs();
  const index = tabs.indexOf(activeTab);
  const target =
    event.key === "ArrowRight"
      ? tabs[(index + 1) % tabs.length]
      : event.key === "ArrowLeft"
        ? tabs[(index - 1 + tabs.length) % tabs.length]
        : event.key === "Home"
          ? tabs[0]
          : event.key === "End"
            ? tabs[tabs.length - 1]
            : undefined;
  if (target === undefined) return;
  event.preventDefault();
  activateTab(target);
  tabButtons.get(target)?.focus();
});

/**
 * Expediente de AGV o tag: la vía de trabajo declarada como principal — «qué le pasa al 3524» — y
 * por eso se entra escribiendo un identificador en vez de navegar (UX_SPEC §4.1). El buscador vive
 * fijo en la barra de pestañas; el resultado se enseña en la pestaña AGV, que se activa al buscar.
 */
const dossierPanel = element("section", "panel");
dossierPanel.hidden = true;
const dossierInput = element("input");
dossierInput.type = "search";
dossierInput.id = "dossier-search";
dossierInput.placeholder = "número de AGV o de tag";
dossierInput.inputMode = "numeric";
dossierInput.setAttribute("aria-label", "Expediente de un AGV o tag");
const dossierLabel = element("label", undefined, "Expediente");
dossierLabel.htmlFor = "dossier-search";
const dossierResult = element("div");
dossierInput.addEventListener("input", () => {
  renderDossier();
  if (dossierInput.value.trim() !== "" && state.views !== null) activateTab("agv");
});
const dossierSearch = element("div", "dossier-search");
dossierSearch.append(dossierLabel, dossierInput);
tabsBar.append(tabList, dossierSearch);

/** La barra fija de la revisión en campo (UX_SPEC §4.3) se queda bajo las pestañas en todas ellas. */
const reviewHost = element("div", "review-host");
reviewHost.hidden = true;

let activeTab: TabId = "resumen";

/** La revisión en campo de las vistas que se están enseñando (`review-ui.ts`). Va antes de la pestaña Memoria, que la consulta al construirse. */
let reviewSession: ReviewSession | null = null;

function isTabId(value: string): value is TabId {
  return (TAB_IDS as readonly string[]).includes(value);
}

/** Las pestañas que se pueden abrir: la WiFi solo existe con un registro de conexiones cargado. */
function visibleTabs(): readonly TabId[] {
  return TAB_IDS.filter((tabId) => tabButtons.get(tabId)?.hidden !== true);
}

function activateTab(id: TabId): void {
  // Una pestaña escondida (WiFi sin registro) no se abre: el `hash` que la pida vuelve al Resumen.
  if (tabButtons.get(id)?.hidden === true) id = "resumen";
  activeTab = id;
  for (const [tabId, button] of tabButtons) {
    const selected = tabId === id;
    button.setAttribute("aria-selected", String(selected));
    button.tabIndex = selected ? 0 : -1;
    const panel = tabPanels.get(tabId);
    if (panel !== undefined) panel.hidden = !selected;
  }
  // En el `hash` sin mover la página: `location.hash` desplazaría hasta un elemento con ese id.
  if (location.hash !== `#${id}`) history.replaceState(null, "", `#${id}`);
  // En el móvil las pestañas se desplazan en horizontal: la activa se trae a la vista.
  tabButtons.get(id)?.scrollIntoView({ block: "nearest", inline: "nearest" });
}

window.addEventListener("hashchange", () => {
  const id = location.hash.slice(1);
  if (isTabId(id) && id !== activeTab) activateTab(id);
});

/**
 * Replay básico (ROADMAP F2). Un control temporal y una tabla, no un mapa: el circuito es
 * topológico y la posición es fracción temporal, nunca física (`PERFORMANCE_BUDGET.md` §6).
 */
const replayPanel = element("section", "panel");
replayPanel.hidden = true;
const replaySlider = element("input");
replaySlider.type = "range";
replaySlider.id = "replay-slider";
replaySlider.min = "0";
replaySlider.value = "0";
const replayLabel = element("label", undefined, "Instante");
replayLabel.htmlFor = "replay-slider";
const replayTime = element("p", "muted", "");
const replayTable = element("div");
replaySlider.addEventListener("input", () => renderReplayFrame());

const tablePanel = element("section", "panel");
tablePanel.hidden = true;

const agvFilter = element("input");
agvFilter.type = "search";
agvFilter.id = "filter-agv";
agvFilter.placeholder = "identificador exacto";
agvFilter.inputMode = "numeric";
const tagFilter = element("input");
tagFilter.type = "search";
tagFilter.id = "filter-tag";
tagFilter.placeholder = "identificador exacto";
tagFilter.inputMode = "numeric";
const filterNote = element("p", "muted", "");
agvFilter.addEventListener("input", () => applyFilter());
tagFilter.addEventListener("input", () => applyFilter());

/** La bandeja de hallazgos (UX_SPEC §4.5): todas las tarjetas revisables, en el Resumen. */
const trayPanel = element("section", "panel tray-panel");
trayPanel.hidden = true;
const tray = element("div", "tray");

/** La tira de cifras de la portada (UX_SPEC §2): seis tiles, cada uno lleva a la pestaña que lo explica. */
const tiles = element("div", "tiles");
tiles.setAttribute("role", "list");
tiles.setAttribute("aria-label", "Cifras del circuito");

/**
 * La pestaña «Memoria» (F4). Se construye una vez y se rehace con cada análisis (`views.memory`) y con
 * cada respuesta del Worker. Sus botones envían mensajes; ninguno consolida por su cuenta: el `commit`
 * solo sale de «Confirmar y consolidar», tras una previsualización sin bloqueos.
 */
const memoryPanel = createMemoryPanel({
  formatInstant,
  formatWindow: (window) => `${formatTick(window.from)} → ${formatTick(window.to)}`,
  formatTick,
  findings: findingsStatus,
  goToPending: () => {
    activateTab("resumen");
    trayPanel.scrollIntoView({ block: "start" });
    trayPanel.tabIndex = -1;
    trayPanel.focus({ preventScroll: true });
  },
  preview: (sourceId, cuts, released) =>
    startMemory({ type: "consolidate", sourceId, mode: "preview", ...(cuts === undefined ? {} : { cuts }), ...(released === undefined ? {} : { releasedCases: released }) }),
  commit: (sourceId, note, cuts, previewHash, released) =>
    startMemory({
      type: "consolidate",
      sourceId,
      mode: "commit",
      previewHash,
      ...(note === null ? {} : { note }),
      ...(cuts === undefined ? {} : { cuts }),
      ...(released === undefined ? {} : { releasedCases: released }),
    }),
  toDateTimeInput,
  fromDateTimeInput,
  revoke: (version, reason) => startMemory({ type: "revoke", version, reason }),
  resolveFork: (choice, reason) => startMemory({ type: "resolve-fork", choice, reason }),
  compare: (from, to) => startMemory({ type: "compare-versions", from, to }),
});

/**
 * La sección «Plano del circuito» (ADR-0016), debajo de la memoria en la misma pestaña. Se rehace con
 * cada análisis (`views.plan`) y con cada `plan-updated`. Cada cambio del plano lo pide una persona con
 * un botón y una razón escrita; el Worker lo valida y lo escribe.
 */
const planPanel = createPlanPanel({
  formatInstant,
  // «12/01»: día y mes con dos cifras, en la zona del circuito.
  formatDay: (utcMs) => {
    const parts = new Intl.DateTimeFormat("es-ES", { timeZone: ZONE, day: "numeric", month: "numeric" }).formatToParts(new Date(utcMs));
    const part = (type: string): string => (parts.find((entry) => entry.type === type)?.value ?? "").padStart(2, "0");
    return `${part("day")}/${part("month")}`;
  },
  zone: ZONE,
  send: (action) => startPlan(action),
});

/**
 * La sección «Valores de planta del circuito» (OQ-140), en la pestaña Datos. Se rehace con cada análisis
 * (`views.plantValues`) y con cada `plant-values-updated`. Cada valor lo confirma una persona con su
 * fecha efectiva y su razón; el Worker lo valida y lo escribe, y se aplica al volver a analizar.
 */
const plantValuesPanel = createPlantValuesPanel({
  formatInstant,
  zone: ZONE,
  send: (request) => startPlantValue(request),
  goTo: (section) => {
    const tab = TABS.find(([, label]) => label === section.tab)?.[0];
    if (tab !== undefined) jumpTo(tab, section.heading);
  },
});

/**
 * La sección «Expedientes de incidencia» (F5, ADR-0018), en la pestaña Línea y calles. Se rehace con
 * cada análisis (`views.cases`) y con cada `cases-updated`. Cada acción la confirma una persona con un
 * botón; el Worker la comprueba contra la revisión guardada y escribe una revisión nueva.
 */
const casesPanel = createCasesPanel({
  formatInstant,
  zone: ZONE,
  send: (action) => startCase(action),
  requestReplay: (caseId) => startCaseReplay(caseId),
  exportCase: (kind, view) => exportCase(kind, view),
});

{
  const get = (id: TabId): HTMLElement => tabPanels.get(id) as HTMLElement;
  const views = (id: TabId): HTMLElement => viewsOf.get(id) as HTMLElement;
  // Resumen: la composición del circuito y la bandeja. Tags, Tiempos y Línea y calles: solo vistas.
  get("resumen").append(views("resumen"), trayPanel);
  get("tags").append(views("tags"));
  // AGV: las vistas de la flota y, al final, el expediente que abre el buscador de la barra.
  get("agv").append(views("agv"), dossierPanel);
  // WiFi: solo vistas, y solo con registro de conexiones cargado (la pestaña nace escondida).
  get("wifi").append(views("wifi"));
  get("tiempos").append(views("tiempos"));
  // Línea y calles: sus vistas y, al final, los expedientes de incidencia (F5).
  get("linea").append(views("linea"), casesPanel.node);
  // Memoria: la memoria consolidada del circuito, sus versiones y el flujo de consolidar (F4).
  get("memoria").append(memoryPanel.node, planPanel.node);
  // Datos: lo que se carga y lo que entró, tal cual: listas, copia, fuente, cobertura y perfil,
  // replay y lecturas.
  get("datos").append(listsPanel, plantValuesPanel.node, projectPanel, summaryPanel, views("datos"), replayPanel, tablePanel, benchPanel.node);
  const empty = element("p", "muted tab-empty", "Todavía no hay análisis: elige un fichero de lecturas y escribe el circuito.");
  get("resumen").prepend(empty);
}

app.append(
  header,
  updates.node,
  tabsBar,
  reviewHost,
  // El selector de lecturas, el progreso y los mensajes se ven en todas las pestañas: son la
  // entrada y la respuesta de cada importación.
  picker,
  progressPanel,
  messagePanel,
  ...TAB_IDS.map((id) => tabPanels.get(id) as HTMLElement),
);

{
  dossierPanel.append(element("h2", undefined, "Expediente de un AGV o tag"), dossierResult);
  replayPanel.append(
    element("h2", undefined, "Replay"),
    replayLabel,
    replaySlider,
    replayTime,
    element(
      "p",
      "muted",
      "La posición dentro de un tramo se estima por el tiempo; no es una posición física.",
    ),
    replayTable,
  );
  // La altura de la barra de pestañas, para que la barra de revisión se pegue debajo y ningún salto
  // deje un título detrás de las dos.
  const measure = (): void => {
    document.documentElement.style.setProperty("--tabs-height", `${Math.ceil(tabsBar.getBoundingClientRect().height)}px`);
  };
  if (typeof ResizeObserver !== "undefined") new ResizeObserver(measure).observe(tabsBar);
  const initial = location.hash.slice(1);
  activateTab(isTabId(initial) ? initial : "resumen");
}


// --- Presentación ----------------------------------------------------------

function showMessage(kind: "error" | "warn" | "info", heading: string, lines: readonly string[]): void {
  messagePanel.replaceChildren();
  messagePanel.hidden = false;
  messagePanel.className = `panel message ${kind}`;
  messagePanel.append(element("h2", undefined, heading));
  for (const line of lines) messagePanel.append(element("p", undefined, line));
}

function clearMessages(): void {
  messagePanel.replaceChildren();
  messagePanel.hidden = true;
}

/**
 * La salida de una fuente cuya fecha no se puede resolver sola.
 *
 * Sin esto el mensaje de error pedía «indica el orden de los campos» y **no había forma de
 * indicarlo**: una promesa que el programa no cumplía, y una exportación legítima de los primeros
 * días de un mes quedaba fuera para siempre. El programa sigue sin elegir por el usuario; le da el
 * alcance de cada lectura —que ya va en el mensaje— y un control para decidir (R-DAT-014).
 */
function offerFieldOrder(file: File): void {
  const chooser = element("div", "chooser");
  const select = element("select");
  select.id = "field-order";
  for (const [value, label] of [
    ["day-first", "día/mes (31/12/2026)"],
    ["month-first", "mes/día (12/31/2026)"],
  ] as const) {
    const option = element("option", undefined, label);
    option.value = value;
    select.append(option);
  }
  const label = element("label", undefined, "Formato de fecha ");
  label.htmlFor = "field-order";
  const retry = element("button", undefined, "Importar con este orden");
  retry.type = "button";
  retry.addEventListener("click", () => {
    startImport(file, select.value as FieldOrder);
  });
  chooser.append(label, select, retry);
  messagePanel.append(chooser);
}

function renderSummary(summary: SourceSummary): void {
  summaryPanel.replaceChildren();
  summaryPanel.hidden = false;
  summaryPanel.append(element("h2", undefined, "Fuente"));

  const delimiterName =
    summary.delimiter === "\t" ? "tabulador" : summary.delimiter === ";" ? "punto y coma" : "coma";
  /**
   * Los pares de un mismo vehículo que el reloj no ordena, con su peso sobre el total.
   *
   * Se dice lo que significan, no solo cuántos son: un recuento sin su consecuencia invita a
   * leerlo como un defecto de la fuente, y no lo es — es el límite de lo que la fuente afirma.
   */
  const describeSameInstant = (facts: SourceSummary): string => {
    if (facts.vehiclePairs === 0) return "—";
    if (facts.sameInstantPairs === 0) return "ninguna";
    const share = ((facts.sameInstantPairs / facts.vehiclePairs) * 100).toFixed(1);
    return (
      `${facts.sameInstantPairs.toLocaleString("es-ES")} de ` +
      `${facts.vehiclePairs.toLocaleString("es-ES")} (${share} %), ordenadas por su posición en el fichero`
    );
  };

  const directionName =
    summary.direction === "newest-first"
      ? "lo más reciente primero"
      : summary.direction === "oldest-first"
        ? "lo más antiguo primero"
        : "indeterminado";

  // Lo que interesa de un vistazo, arriba; el detalle técnico de la lectura del fichero, plegado.
  const main: readonly (readonly [string, string])[] = [
    ["Fichero", summary.fileName],
    [
      "Periodo",
      `${formatInstant(summary.observedFrom)} → ${formatInstant(summary.observedTo)} ` +
        `(${(((summary.observedTo - summary.observedFrom) / 3_600_000) || 0).toFixed(1)} h)`,
    ],
    ["Lecturas aceptadas", `${summary.acceptedRows.toLocaleString("es-ES")} de ${summary.totalRows.toLocaleString("es-ES")} filas`],
    // Se separan a propósito: una fila sin tag no es una fila rota, y mezclarlas haría parecer
    // averiada una fuente que solo trae eventos además de lecturas.
    ["Filas que no son lecturas", summary.rowsWithoutTag.toLocaleString("es-ES")],
    ["Filas con errores", summary.quarantinedRows.toLocaleString("es-ES")],
  ];
  const rows: readonly (readonly [string, string])[] = [
    ["Hash", `${summary.sourceHash.slice(0, 16)}…`],
    ["Separador", `${delimiterName} (confianza ${(summary.delimiterConfidence * 100).toFixed(0)} %)`],
    ["Columnas", summary.header.join(" · ")],
    [
      "Formato de fecha",
      `${summary.fieldOrder === "day-first" ? "día/mes" : "mes/día"} — ${summary.fieldOrderEvidence}`,
    ],
    ["Zona horaria", summary.zone],
    ["Codificación", summary.encoding],
    [
      "Orden del fichero",
      `${directionName} (coherencia ${(summary.monotonicity.confidence * 100).toFixed(1)} %)`,
    ],
    // El reloj no ordena dentro de un instante: ahí manda la posición en el fichero, que es
    // `inferred` (R-DAT-013). Se cuenta **por vehículo**, que es donde amenaza a la topología;
    // dos AGV distintos leyendo a la vez es lo normal y no dice nada.
    ["Lecturas simultáneas del mismo AGV", describeSameInstant(summary)],
    ["Tiempo de proceso", `${summary.elapsedMs.toLocaleString("es-ES")} ms`],
  ];

  const facts = (entries: readonly (readonly [string, string])[]): HTMLElement => {
    const list = element("dl", "facts");
    for (const [term, value] of entries) {
      list.append(element("dt", undefined, term), element("dd", undefined, value));
    }
    return list;
  };
  const technical = element("details");
  technical.append(element("summary", undefined, "Detalles de lectura del fichero"), facts(rows));
  summaryPanel.append(facts(main), technical);
}

/**
 * Filtro por AGV y por tag.
 *
 * Es la vía de trabajo declarada como principal —«qué le pasa al 3524»— y sin ella una importación
 * de sesenta mil filas no responde a ninguna pregunta: son trescientas pulsaciones de «mostrar más»
 * para llegar al final.
 *
 * La comparación es **exacta sobre el texto recortado**, nunca numérica ni parcial: `0040` y `40`
 * son identificadores distintos (INV-002) y una coincidencia parcial mezclaría `2032` con `20321`.
 */
function applyFilter(): void {
  const agv = agvFilter.value.trim();
  const tag = tagFilter.value.trim();
  state.visible = filterReadings(state.readings, agv, tag);
  state.shown = 0;
  const body = tablePanel.querySelector("tbody");
  if (body !== null) body.replaceChildren();
  renderCount();
  appendRows();
}

/** Cuántas lecturas pasan el filtro. */
const visibleCount = (): number => state.visible?.length ?? state.readings.count;

function renderCount(): void {
  const total = state.readings.count.toLocaleString("es-ES");
  const shown = visibleCount().toLocaleString("es-ES");
  filterNote.textContent =
    state.visible === null
      ? `${total} lecturas`
      : state.visible.length === 0
        ? `Ninguna de las ${total} lecturas coincide (se compara el número entero, con sus ceros).`
        : `${shown} de ${total} lecturas`;
}

function renderTableSkeleton(): void {
  tablePanel.replaceChildren();
  tablePanel.hidden = false;
  tablePanel.append(element("h2", undefined, "Lecturas"));

  const filters = element("div", "filters");
  const agvLabel = element("label", undefined, "AGV");
  agvLabel.htmlFor = "filter-agv";
  const tagLabel = element("label", undefined, "Tag");
  tagLabel.htmlFor = "filter-tag";
  filters.append(agvLabel, agvFilter, tagLabel, tagFilter);
  tablePanel.append(filters, filterNote);

  const table = element("table", "readings");
  const head = element("thead");
  const headRow = element("tr");
  for (const label of ["Instante", "AGV", "Tag", "Fila origen", "Original"]) {
    headRow.append(element("th", undefined, label));
  }
  head.append(headRow);
  table.append(head, element("tbody"));
  tablePanel.append(table);

  const more = element("button", undefined, "Mostrar más");
  more.type = "button";
  more.id = "show-more";
  more.addEventListener("click", () => appendRows());
  tablePanel.append(more);
}

function appendRows(): void {
  const body = tablePanel.querySelector("tbody");
  if (body === null) return;

  const visible = visibleCount();
  const end = Math.min(state.shown + PAGE_SIZE, visible);
  for (let index = state.shown; index < end; index += 1) {
    const reading = readingRow(state.readings, state.visible === null ? index : (state.visible[index] as number));
    const row = element("tr");
    // La hora repetida resuelta por posición (ADR-0013, nota 2026-09-26) ordena como cualquier otra:
    // se marca aparte y no como sospechosa.
    if (reading.flag === "dst_by_position") row.className = "resolved";
    else if (reading.flag !== "ok") row.className = "flagged";

    row.append(element("td", undefined, formatInstant(reading.utcMs)));
    row.append(element("td", "mono", reading.agvId));
    row.append(element("td", "mono", reading.tagId));
    // La procedencia es navegable: la fila física del fichero está siempre a la vista.
    row.append(element("td", "mono", String(reading.sourceRow)));
    row.append(element("td", "mono raw", reading.raw));
    body.append(row);
  }
  state.shown = end;

  const more = tablePanel.querySelector<HTMLButtonElement>("#show-more");
  if (more !== null) {
    more.hidden = state.shown >= visible;
    more.textContent = `Mostrar más (${(visible - state.shown).toLocaleString("es-ES")} restantes)`;
  }
}

/**
 * Un solo trabajo a la vez. Mientras el Worker responde se apagan **todas** las entradas que
 * arrancarían otro —los cuatro ficheros y los botones de memoria, plano y valores de planta—, no solo
 * la del panel que lo pidió: dos trabajos a la vez se pisarían el almacén, y matar al que estaba en
 * curso dejaba una fuente acumulada sin instantánea ni archivo. «Cancelar» solo se ofrece en una
 * importación, que es la que tiene puntos de control (WP-004); las operaciones de memoria, plano y
 * valores de planta escriben de una vez y terminan solas (UX_SPEC §8).
 */
function setBusy(busy: boolean): void {
  fileInput.disabled = busy;
  projectInput.disabled = busy;
  listsInput.disabled = busy;
  fleetInput.disabled = busy;
  memoryPanel.setBusy(busy);
  planPanel.setBusy(busy);
  plantValuesPanel.setBusy(busy);
  casesPanel.setBusy(busy);
  benchPanel.setBusy(busy);
  storagePanel.setBusy(busy);
  cancelButton.hidden = !busy || state.memoryJob !== null || state.planJob || state.plantValueJob || state.caseJob;
  progressPanel.hidden = !busy;
  workerBusy = busy;
  syncUpdateBusy();
}

/**
 * Con un trabajo en curso no se activa una versión nueva (NFR-011): ni con el Worker ocupado ni
 * mientras se exporta un `.agvproj`, que lee el almacén desde aquí sin pasar por el Worker.
 */
let workerBusy = false;
let exportsInFlight = 0;
function syncUpdateBusy(): void {
  updates.setBusy(workerBusy || exportsInFlight > 0);
}

/** Lo que dice el panel cuyo botón se pulsó con otro trabajo en marcha. */
const BUSY_NOTICE = "Hay otra operación en curso; espera a que termine.";

// --- Ciclo de vida del trabajo --------------------------------------------

function disposeWorker(): void {
  state.worker?.terminate();
  state.worker = null;
  state.jobId = null;
  state.memoryJob = null;
  state.planJob = false;
  state.plantValueJob = false;
  state.caseJob = false;
  state.importStoppable = false;
}

function handleMessage(message: FromWorker): void {
  // WP-003: un mensaje de un trabajo caducado se descarta sin tocar el estado.
  if (state.jobId === null || !isCurrent(message, state.jobId)) return;

  switch (message.type) {
    case "accepted":
      progressNote.textContent = "Empezando";
      return;

    case "progress":
      progressNote.textContent =
        message.total > 1
          ? `${message.note} — ${message.done.toLocaleString("es-ES")} de ${message.total.toLocaleString("es-ES")}`
          : message.note;
      progressBar.value = message.total === 0 ? 0 : message.done / message.total;
      benchObserver?.progress(message.done, message.total);
      return;

    case "saving":
      // Desde aquí la importación escribe: ya no se detiene, termina entera (WP-004).
      state.importStoppable = false;
      cancelButton.hidden = true;
      progressNote.textContent = "Guardando en este dispositivo; ya no se puede cancelar";
      return;

    case "complete": {
      benchObserver?.arrived();
      state.summary = message.summary;
      state.readings = message.readings;
      state.visible = null;
      state.quarantine = message.quarantine;
      state.warnings = message.warnings;
      state.shown = 0;

      clearMessages();
      if (message.warnings.length > 0) {
        showMessage("warn", "Advertencias", message.warnings);
      }
      resetViews();
      renderSummary(message.summary);
      state.circuitId = message.accumulation?.accumulated === true ? message.accumulation.circuitId : null;
      if (message.accumulation !== undefined) {
        state.coverage = message.accumulation.coverage;
        state.sources = message.accumulation.sources;
        renderAccumulation(message.accumulation);
        renderAffinity(message.accumulation);
      }
      if (message.views !== undefined) renderViews(message.views);
      state.views = message.views ?? null;
      const working = message.views === undefined ? null : workingFileOf(message.views);
      memoryPanel.update({ circuitId: state.circuitId, memory: message.views?.memory ?? null, working });
      planPanel.update({ circuitId: state.circuitId, plan: message.views?.plan ?? null, working });
      plantValuesPanel.update({ circuitId: state.circuitId, view: message.views?.plantValues ?? null });
      state.cases = message.views?.cases ?? null;
      casesPanel.update({
        circuitId: state.circuitId,
        views: message.views?.cases ?? null,
        measured: message.views === undefined ? [] : measuredStarts(message.views),
        findings: findingStarts(),
      });
      if (message.views !== undefined) loadEvolution(message.views);
      renderDossier();
      renderReplaySkeleton();
      renderTableSkeleton();
      agvFilter.value = "";
      tagFilter.value = "";
      renderCount();
      appendRows();
      setBusy(false);
      disposeWorker();
      benchObserver?.rendered();
      // Tras guardar, se mira el espacio y se pide el guardado persistente si aún no lo hay.
      storagePanel.refresh(message.accumulation?.accumulated === true);
      return;
    }

    case "error": {
      // Un error de una operación de memoria se cuenta en su pestaña, no como fallo de importación.
      if (state.memoryJob !== null) {
        state.memoryJob = null;
        memoryPanel.showError(message.cause, message.recovery);
        memoryPanel.setBusy(false);
        setBusy(false);
        disposeWorker();
        return;
      }
      // Y el de un valor de planta, en la suya.
      if (state.plantValueJob) {
        finishPlantValueJob();
        plantValuesPanel.showError(message.cause, message.recovery);
        return;
      }
      // Y el de un expediente, en el suyo.
      if (state.caseJob) {
        finishCaseJob();
        casesPanel.showError(message.cause, message.recovery);
        return;
      }
      // Y el de una acción del plano, en su sección.
      if (state.planJob) {
        finishPlanJob();
        planPanel.showError(message.cause, message.recovery);
        return;
      }
      // WP-005: cero filas es una respuesta legítima, y llega siempre con causa y recuperación.
      const lines = [message.cause, message.recovery];
      if (message.detectedSchema !== undefined) {
        lines.push(`Esquema detectado: ${message.detectedSchema.join(" · ")}`);
      }
      if (message.sampleRows !== undefined) {
        lines.push(...message.sampleRows.map((row) => `Ejemplo: ${row}`));
      }
      showMessage("error", `No se pudo importar (${message.code})`, lines);
      benchObserver?.failed(message.cause);
      // Una fuente ambigua no es una fuente rota: es una que el programa no puede resolver solo.
      // Se ofrece la salida en lugar de dejar al usuario con una instrucción que no puede seguir.
      if (message.code === "DATE_AMBIGUOUS" && state.file !== null) offerFieldOrder(state.file);
      setBusy(false);
      disposeWorker();
      return;
    }

    case "fleet-choose-circuit": {
      setBusy(false);
      disposeWorker();
      showMessage("warn", "El historial trae varios circuitos", [
        "Elige cuál es este circuito. Se recordará para las próximas cargas.",
      ]);
      const chooser = element("div", "chooser");
      const select = element("select");
      select.id = "fleet-circuit";
      for (const option of message.options) {
        const entry = element("option", undefined, `${option.name} (${option.rows} filas)`);
        entry.value = option.name;
        select.append(entry);
      }
      const label = element("label", undefined, "Circuito ");
      label.htmlFor = "fleet-circuit";
      const load = element("button", undefined, "Cargar las filas de este circuito");
      load.type = "button";
      const file = state.fleetFile;
      load.addEventListener("click", () => {
        if (file !== null) startFleet(file, message.circuitId, select.value);
      });
      chooser.append(label, select, load);
      messagePanel.append(chooser);
      return;
    }

    case "fleet-loaded": {
      fleetNote.textContent =
        `Circuito «${message.circuitId}»${message.circuitName === null ? "" : ` (historial «${message.circuitName}»)`} — ` +
        `${message.periods.toLocaleString("es-ES")} periodos guardados.`;
      const lines = [
        `${message.accepted.toLocaleString("es-ES")} filas: ${message.added} periodos nuevos y ` +
          `${message.replaced} actualizados.`,
        ...message.rejected.map((entry) => `${entry.rows} ${entry.rows === 1 ? "fila no cargada" : "filas no cargadas"}: ${entry.reason}.`),
        ...message.warnings,
        "Se verá al volver a importar las lecturas del circuito.",
      ];
      showMessage(
        message.warnings.length > 0 || message.rejected.length > 0 ? "warn" : "info",
        "Historial de flota cargado",
        lines,
      );
      setBusy(false);
      disposeWorker();
      return;
    }

    case "connections-loaded": {
      const ok = message.files.filter((file) => file.failure === null);
      const failed = message.files.filter((file) => file.failure !== null);
      connectionsNote.textContent =
        `Circuito «${message.circuitId}» — ${message.totalEvents.toLocaleString("es-ES")} eventos de ${message.vehicles} AGV guardados.`;
      const lines = [
        ...ok.map(
          (file) =>
            `${file.fileName} → AGV ${file.agvId}: ${file.events.toLocaleString("es-ES")} eventos` +
            (file.ips.length > 0 ? ` (terminal ${file.ips.join(", ")})` : "") +
            (file.rejected.length > 0 ? `; ${file.rejected.map((entry) => `${entry.rows} ${entry.rows === 1 ? "fila no cargada" : "filas no cargadas"}: ${entry.reason}`).join("; ")}` : "") +
            ".",
        ),
        ...ok.flatMap((file) => file.warnings.map((warning) => `${file.fileName}: ${warning}`)),
        ...failed.map((file) => `${file.fileName}: no se cargó. ${file.failure ?? ""}`),
        "Se verá al volver a importar las lecturas del circuito.",
      ];
      showMessage(failed.length > 0 || ok.some((file) => file.warnings.length > 0 || file.rejected.length > 0) ? "warn" : "info", "Registro de conexiones cargado", lines);
      setBusy(false);
      disposeWorker();
      return;
    }

    case "lists-loaded": {
      const detail = message.lists
        .map((entry) => `${entry.list}: ${entry.tags.toLocaleString("es-ES")} ${entry.tags === 1 ? "tag" : "tags"}`)
        .join(" · ");
      listsNote.textContent = `Circuito «${message.circuitId}» — ${detail}`;
      const lines = [
        `${message.accepted.toLocaleString("es-ES")} filas en ${message.lists.length} ${message.lists.length === 1 ? "lista" : "listas"}.`,
        ...message.warnings,
      ];
      if (message.rejected > 0) {
        lines.push(`${message.rejected} ${message.rejected === 1 ? "fila sin lista o sin tag no se ha cargado" : "filas sin lista o sin tag no se han cargado"}.`);
      }
      lines.push(
        "Se aplicarán al volver a importar las lecturas del circuito.",
      );
      showMessage(message.warnings.length > 0 ? "warn" : "info", "Listas cargadas", lines);
      setBusy(false);
      disposeWorker();
      return;
    }

    case "cancelled":
      // Una operación de memoria cancelada se dice en su pestaña; la importación, en el aviso general.
      if (state.memoryJob !== null) {
        memoryPanel.notice("Operación cancelada. No se ha cambiado nada.");
      } else {
        showMessage("info", "Importación cancelada", [
          "No se ha cambiado nada.",
        ]);
        benchObserver?.cancelled();
      }
      setBusy(false);
      disposeWorker();
      return;

    // --- Memoria consolidada (F4): las respuestas se pintan en su pestaña ---------------------------
    case "consolidation-preview":
      finishMemoryJob();
      memoryPanel.showPreview(message.preview, message.previewHash, message.cutWarnings ?? []);
      return;

    case "consolidated":
      finishMemoryJob();
      memoryPanel.showConsolidated(message.version, message.memory);
      if (message.plantProposals !== undefined) plantValuesPanel.proposalsChanged(message.plantProposals);
      planPanel.memoryChanged(message.memory);
      renderMemoryTile(message.memory);
      return;

    case "revoked":
      finishMemoryJob();
      memoryPanel.showRevoked(message.version, message.memory);
      if (message.plantProposals !== undefined) plantValuesPanel.proposalsChanged(message.plantProposals);
      planPanel.memoryChanged(message.memory);
      renderMemoryTile(message.memory);
      return;

    case "fork-resolved":
      finishMemoryJob();
      memoryPanel.showForkResolved(message.memory);
      if (message.plantProposals !== undefined) plantValuesPanel.proposalsChanged(message.plantProposals);
      planPanel.memoryChanged(message.memory);
      renderMemoryTile(message.memory);
      return;

    case "versions-compared":
      finishMemoryJob();
      memoryPanel.showComparison(message.comparison);
      return;

    // --- Plano físico (ADR-0016): la respuesta se pinta en su sección --------------------------------
    case "plan-updated":
      finishPlanJob();
      planPanel.showUpdated(message.written, message.plan);
      return;

    // --- Valores de planta (OQ-140): la respuesta se pinta en su sección ----------------------------
    case "plant-values-updated":
      finishPlantValueJob();
      plantValuesPanel.showUpdated(message.written, message.appliesToWorking, message.plantValues);
      return;

    // --- Expedientes de incidencia (ADR-0018): la respuesta se pinta en su sección ------------------
    case "cases-updated":
      finishCaseJob();
      state.cases = message.cases;
      casesPanel.showUpdated(message.written, message.caseId, message.cases);
      return;

    case "case-evidence-csv-ready": {
      finishCaseJob();
      saveDownload(`\ufeff${message.csv}`, "text/csv;charset=utf-8", caseReportFileName(message.caseId, new Date(), "csv").replace(/^expediente-/, "lecturas-"));
      casesPanel.notice(`Lecturas del recorte de ${message.caseId} (revisión ${message.revision}) descargadas.`);
      return;
    }

    case "case-replay-ready":
      finishCaseJob();
      casesPanel.showReplay(message.caseId, message.revision, message.replay);
      return;
  }
}

/** Las incidencias de la batería de mediciones (R-AGV-021) como puntos de partida de un expediente. */
function measuredStarts(views: CircuitViews): readonly CaseStart[] {
  return (views.incidents?.records ?? []).map((record) => {
    const { incident, battery } = record;
    const key = `${incident.agvId} ${incident.fromTagId} ${incident.fromUtcMs}`;
    const label = `${INCIDENT_KIND_TEXT[record.kind]}: AGV ${incident.agvId} en ${incident.fromTagId}, ${formatInstant(incident.fromUtcMs)}`;
    return {
      origin: { kind: "incidencia-medida", key, label },
      title: label,
      agvId: incident.agvId,
      tagIds: [incident.fromTagId],
      from: incident.fromUtcMs,
      to: incident.toUtcMs ?? incident.fromUtcMs + battery.durationMs,
    };
  });
}

/** Los hallazgos con tarjeta en la bandeja como puntos de partida; la ventana la pone la persona. */
function findingStarts(): readonly CaseStart[] {
  return (reviewSession?.findings() ?? []).map((finding) => {
    const label = finding.figure === "" ? finding.title : `${finding.title} (${finding.figure})`;
    return { origin: { kind: "hallazgo", key: finding.key, label }, title: label, agvId: null, tagIds: [], from: null, to: null };
  });
}

function finishCaseJob(): void {
  state.caseJob = false;
  casesPanel.setBusy(false);
  setBusy(false);
  disposeWorker();
}

/**
 * Envía una acción sobre un expediente al Worker (ADR-0018). Como `startPlantValue`: mismo Worker,
 * `jobId` propio, y la presentación solo pide. No vuelve a analizar nada ni toca la memoria.
 */
/** Descarga un fichero generado en la página: un `Blob` local, nada sale del dispositivo (NFR-001). */
function saveDownload(content: string, type: string, fileName: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * El informe y la exportación de un expediente (ADR-0018 D11). El informe y la cronología se montan aquí
 * desde la vista que ya está en pantalla; las lecturas del recorte las prepara el Worker, que las tiene.
 */
function exportCase(kind: "informe" | "cronologia" | "lecturas", view: CaseView): void {
  const current = view.current;
  if (kind === "informe") {
    const name = caseReportFileName(current.caseId, new Date());
    saveDownload(buildCaseReportHtml(view, { formatInstant, css: appCss(), appVersion: APP_VERSION, now: Date.now() }), "text/html;charset=utf-8", name);
    casesPanel.notice(`Informe de ${current.caseId} descargado como «${name}». «Imprimir → Guardar como PDF» lo convierte en PDF.`);
    return;
  }
  if (kind === "cronologia") {
    if (view.chronology === null) return;
    const name = caseReportFileName(current.caseId, new Date(), "csv").replace(/^expediente-/, "cronologia-");
    saveDownload(`\ufeff${chronologyCsv(view.chronology, formatInstant, { caseId: current.caseId, revision: current.revision, hash: current.hash })}`, "text/csv;charset=utf-8", name);
    casesPanel.notice(`Cronología de ${current.caseId} descargada como «${name}».`);
    return;
  }
  const circuitId = state.circuitId;
  if (circuitId === null) return;
  if (state.worker !== null) {
    casesPanel.notice(BUSY_NOTICE);
    return;
  }
  const jobId = crypto.randomUUID();
  const worker = new Worker(new URL("../../workers/import.worker.ts", import.meta.url), {
    type: "module",
  });
  state.worker = worker;
  state.jobId = jobId;
  state.caseJob = true;
  worker.onmessage = (event: MessageEvent<FromWorker>) => handleMessage(event.data);
  worker.onerror = () => {
    finishCaseJob();
    casesPanel.showError("el proceso auxiliar se detuvo.", "No se ha cambiado nada. Vuelve a intentarlo.");
  };
  setBusy(true);
  casesPanel.setBusy(true);
  progressNote.textContent = "Preparando las lecturas del recorte";
  progressBar.value = 0;
  const message: ToWorker = { type: "case-evidence-csv", protocolVersion: PROTOCOL_VERSION, jobId, circuitId, caseId: current.caseId };
  worker.postMessage(message);
}

/** Pide el replay de un expediente (ALG-016). Mismo Worker y `jobId` propio; solo lee. */
function startCaseReplay(caseId: string): void {
  const circuitId = state.circuitId;
  if (circuitId === null) {
    casesPanel.showError("no hay circuito en pantalla.", "Importa las lecturas del circuito y vuelve a intentarlo.");
    return;
  }
  if (state.worker !== null) {
    casesPanel.notice(BUSY_NOTICE);
    return;
  }
  const jobId = crypto.randomUUID();
  const worker = new Worker(new URL("../../workers/import.worker.ts", import.meta.url), {
    type: "module",
  });
  state.worker = worker;
  state.jobId = jobId;
  state.caseJob = true;
  worker.onmessage = (event: MessageEvent<FromWorker>) => handleMessage(event.data);
  worker.onerror = () => {
    finishCaseJob();
    casesPanel.showError("el proceso auxiliar se detuvo.", "No se ha cambiado nada. Vuelve a intentarlo.");
  };
  setBusy(true);
  casesPanel.setBusy(true);
  progressNote.textContent = "Preparando el replay del expediente";
  progressBar.value = 0;
  const message: ToWorker = { type: "case-replay", protocolVersion: PROTOCOL_VERSION, jobId, circuitId, caseId };
  worker.postMessage(message);
}

function startCase(action: CaseAction): void {
  const circuitId = state.circuitId;
  if (circuitId === null) {
    casesPanel.showError("no hay circuito en pantalla.", "Importa las lecturas del circuito y vuelve a intentarlo.");
    return;
  }
  // Nunca se mata el trabajo en curso para empezar este (WP-004): se rechaza y se dice.
  if (state.worker !== null) {
    casesPanel.notice(BUSY_NOTICE);
    return;
  }
  const jobId = crypto.randomUUID();
  const worker = new Worker(new URL("../../workers/import.worker.ts", import.meta.url), {
    type: "module",
  });
  state.worker = worker;
  state.jobId = jobId;
  state.caseJob = true;
  worker.onmessage = (event: MessageEvent<FromWorker>) => handleMessage(event.data);
  worker.onerror = () => {
    finishCaseJob();
    casesPanel.showError("el proceso auxiliar se detuvo.", "No se ha guardado nada. Vuelve a intentarlo.");
  };
  setBusy(true);
  casesPanel.setBusy(true);
  progressNote.textContent = action.kind === "crear" ? "Creando el expediente y copiando las lecturas de su ventana" : "Guardando el expediente";
  progressBar.value = 0;
  const message: ToWorker = { type: "case-action", protocolVersion: PROTOCOL_VERSION, jobId, circuitId, action };
  worker.postMessage(message);
}

function finishPlantValueJob(): void {
  state.plantValueJob = false;
  plantValuesPanel.setBusy(false);
  setBusy(false);
  disposeWorker();
}

/**
 * Envía un valor de planta confirmado al Worker (OQ-140). Como `startPlan`: mismo Worker, `jobId`
 * propio, y la presentación solo pide. No vuelve a analizar nada: el valor se aplica al volver a
 * cargar los ficheros de su vigencia.
 */
function startPlantValue(request: PlantValueSendRequest): void {
  const circuitId = state.circuitId;
  if (circuitId === null) {
    plantValuesPanel.showError("no hay circuito en pantalla.", "Importa las lecturas del circuito y vuelve a intentarlo.");
    return;
  }
  // Nunca se mata el trabajo en curso para empezar este (WP-004): se rechaza y se dice.
  if (state.worker !== null) {
    plantValuesPanel.notice(BUSY_NOTICE);
    return;
  }
  const jobId = crypto.randomUUID();
  const worker = new Worker(new URL("../../workers/import.worker.ts", import.meta.url), {
    type: "module",
  });
  state.worker = worker;
  state.jobId = jobId;
  state.plantValueJob = true;
  worker.onmessage = (event: MessageEvent<FromWorker>) => handleMessage(event.data);
  worker.onerror = () => {
    finishPlantValueJob();
    plantValuesPanel.showError("el proceso auxiliar se detuvo.", "No se ha guardado nada. Vuelve a intentarlo.");
  };
  setBusy(true);
  plantValuesPanel.setBusy(true);
  progressNote.textContent = "Guardando el valor de planta";
  progressBar.value = 0;
  const working = state.views === null ? null : workingFileOf(state.views);
  const message: ToWorker = {
    type: "plant-value",
    protocolVersion: PROTOCOL_VERSION,
    jobId,
    circuitId,
    ...request,
    workingSourceId: working === null ? null : working.sourceId,
  };
  worker.postMessage(message);
}

function finishPlanJob(): void {
  state.planJob = false;
  planPanel.setBusy(false);
  setBusy(false);
  disposeWorker();
}

/**
 * Envía una acción sobre el plano físico al Worker (ADR-0016): crear el plano, confirmar una
 * propuesta o registrar un cambio a mano. Como `startMemory`: mismo Worker, `jobId` propio, y la
 * presentación solo pide. Va con el fichero de trabajo para que la respuesta traiga el plano leído
 * contra él.
 */
function startPlan(action: PlanAction): void {
  const circuitId = state.circuitId;
  if (circuitId === null) {
    planPanel.showError("no hay circuito en pantalla.", "Importa las lecturas del circuito y vuelve a intentarlo.");
    return;
  }
  if (state.worker !== null) {
    planPanel.notice(BUSY_NOTICE);
    return;
  }
  const jobId = crypto.randomUUID();
  const worker = new Worker(new URL("../../workers/import.worker.ts", import.meta.url), {
    type: "module",
  });
  state.worker = worker;
  state.jobId = jobId;
  state.planJob = true;
  worker.onmessage = (event: MessageEvent<FromWorker>) => handleMessage(event.data);
  worker.onerror = () => {
    finishPlanJob();
    planPanel.showError("el proceso auxiliar se detuvo.", "El plano no se ha modificado. Vuelve a intentarlo.");
  };
  setBusy(true);
  planPanel.setBusy(true);
  progressNote.textContent =
    action.kind === "crear-plano" ? "Creando el plano" : action.kind === "aceptar-propuesta" ? "Escribiendo el cambio confirmado" : "Registrando el cambio del plano";
  progressBar.value = 0;
  const working = state.views === null ? null : workingFileOf(state.views);
  const message: ToWorker = {
    type: "plan-action",
    protocolVersion: PROTOCOL_VERSION,
    jobId,
    circuitId,
    action,
    workingSourceId: working === null ? null : working.sourceId,
  };
  worker.postMessage(message);
}

function finishMemoryJob(): void {
  state.memoryJob = null;
  memoryPanel.setBusy(false);
  setBusy(false);
  disposeWorker();
}

/**
 * Envía una operación de memoria al Worker (F4): previsualizar o confirmar una consolidación, revocar
 * una versión, elegir linaje o comparar dos versiones. Mismo Worker, mismo protocolo y `jobId` propio, como las listas y la
 * flota: la memoria se lee y se escribe fuera del hilo principal, y la presentación solo pide.
 */
function startMemory(
  request:
    | {
        readonly type: "consolidate";
        readonly sourceId: string;
        readonly mode: "preview" | "commit";
        readonly note?: string;
        readonly cuts?: readonly IncidentCut[];
        readonly releasedCases?: readonly string[];
        readonly previewHash?: string;
      }
    | { readonly type: "revoke"; readonly version: number; readonly reason: string }
    | { readonly type: "resolve-fork"; readonly choice: "conservar-local" | "adoptar-entrante"; readonly reason: string }
    | { readonly type: "compare-versions"; readonly from: number; readonly to: number },
): void {
  const circuitId = state.circuitId;
  if (circuitId === null) {
    memoryPanel.showError("no hay circuito en pantalla.", "Importa las lecturas del circuito y vuelve a intentarlo.");
    return;
  }
  if (state.worker !== null) {
    memoryPanel.notice(BUSY_NOTICE);
    return;
  }
  const jobId = crypto.randomUUID();
  const worker = new Worker(new URL("../../workers/import.worker.ts", import.meta.url), {
    type: "module",
  });
  state.worker = worker;
  state.jobId = jobId;
  state.memoryJob = request.type === "consolidate" ? request.mode : request.type;
  worker.onmessage = (event: MessageEvent<FromWorker>) => handleMessage(event.data);
  worker.onerror = () => {
    state.memoryJob = null;
    memoryPanel.showError("el proceso auxiliar se detuvo.", "La memoria no se ha modificado. Vuelve a intentarlo.");
    memoryPanel.setBusy(false);
    setBusy(false);
    disposeWorker();
  };
  setBusy(true);
  memoryPanel.setBusy(true);
  progressNote.textContent =
    request.type === "consolidate"
      ? request.mode === "preview"
        ? "Previsualizando la consolidación"
        : "Consolidando"
      : request.type === "revoke"
        ? "Revocando la versión"
        : request.type === "compare-versions"
          ? "Comparando versiones"
          : "Registrando la elección de linaje";
  progressBar.value = 0;
  const message: ToWorker = { ...request, protocolVersion: PROTOCOL_VERSION, jobId, circuitId };
  worker.postMessage(message);
}

/** El fichero de trabajo del análisis en pantalla, con si tiene instantánea, según lo que dicen las vistas. */
function workingFileOf(views: CircuitViews): MemoryWorkingFile | null {
  const summary = state.summary;
  if (summary === null) return null;
  const list = views.snapshots.list;
  // El mismo fichero cargado dos veces es una fuente nueva sin instantánea propia (R-DAT-005): vale la
  // del primero, que es el mismo contenido.
  const entry = list.find((item) => item.sourceId === summary.sourceId) ?? [...list].reverse().find((item) => item.fileName === summary.fileName && item.hasSnapshot);
  if (entry === undefined) return { sourceId: summary.sourceId, fileName: summary.fileName, hasSnapshot: false };
  return { sourceId: entry.sourceId, fileName: entry.fileName, hasSnapshot: entry.hasSnapshot };
}

/** Lo que la bandeja dice de la revisión, contado en sus tarjetas: es la unidad de revisión (R-EVI-007). */
function findingsStatus(): FindingsStatus | null {
  if (reviewSession === null) return null;
  const cards = [...tray.querySelectorAll<HTMLElement>(".finding.reviewable")];
  const stateOf = (card: HTMLElement): string => card.dataset["review"] ?? "pendiente";
  return {
    total: cards.length,
    pending: cards.filter((card) => stateOf(card) === "pendiente").length,
    confirmedCritical: cards.filter((card) => stateOf(card) === "confirmado" && card.closest<HTMLElement>(".tray-group")?.dataset["rank"] === "1").length,
  };
}

/**
 * Arranca una importación. `withoutCircuit` la hace sin circuito aunque el campo tenga uno: se
 * analiza y se dibuja, pero no se acumula ni se guarda nada (el banco de medida).
 */
function startImport(file: File, fieldOrder?: FieldOrder, withoutCircuit = false): void {
  disposeWorker();
  clearMessages();
  summaryPanel.hidden = true;
  tablePanel.hidden = true;
  // El título «Circuito «X»» del Resumen es el de la importación anterior hasta que llegue la nueva:
  // se esconde, como la fuente, para que nadie lo lea como si ya fuera el resultado.
  (viewsOf.get("resumen") as HTMLElement).hidden = true;

  const jobId = crypto.randomUUID();
  const worker = new Worker(new URL("../../workers/import.worker.ts", import.meta.url), {
    type: "module",
  });
  state.worker = worker;
  state.jobId = jobId;

  worker.onmessage = (event: MessageEvent<FromWorker>) => handleMessage(event.data);
  worker.onerror = () => {
    // No hay cálculo de repuesto en el hilo principal (WP-002): si el Worker cae, se informa.
    showMessage("error", "El proceso auxiliar se detuvo", [
      "La importación no llegó a completarse.",
      "Vuelve a seleccionar el fichero. El estado anterior no se ha modificado.",
    ]);
    setBusy(false);
    disposeWorker();
    benchObserver?.failed("El proceso auxiliar se detuvo.");
  };

  setBusy(true);
  progressNote.textContent = "Preparando";
  progressBar.value = 0;

  const circuitName = withoutCircuit ? "" : circuitInput.value.trim();
  const start: ToWorker = {
    type: "start",
    protocolVersion: PROTOCOL_VERSION,
    jobId,
    file,
    zone: ZONE,
    // Solo viaja si el usuario lo fijó: sin él, la detección manda y puede declararse incapaz.
    ...(fieldOrder === undefined ? {} : { fieldOrder }),
    // Solo el identificador: las lecturas ya guardadas las lee el Worker del almacén, para no
    // clonarlas entre hilos (defecto P4 del prototipo).
    ...(circuitName === "" ? {} : { circuitId: circuitName, circuitName }),
  };
  state.file = file;
  worker.postMessage(start);
  state.importStoppable = true;
}

/** Arranca la carga de listas. Mismo Worker y mismo protocolo: el parseo no vive aquí. */
function startFleet(file: File, circuitId: string, circuitName?: string): void {
  disposeWorker();
  clearMessages();
  const jobId = crypto.randomUUID();
  const worker = new Worker(new URL("../../workers/import.worker.ts", import.meta.url), {
    type: "module",
  });
  state.worker = worker;
  state.jobId = jobId;
  worker.onmessage = (event: MessageEvent<FromWorker>) => handleMessage(event.data);
  worker.onerror = () => {
    showMessage("error", "El proceso auxiliar se detuvo", [
      "El historial no llegó a cargarse y el circuito no se ha modificado.",
    ]);
    setBusy(false);
    disposeWorker();
  };
  setBusy(true);
  progressNote.textContent = "Leyendo el historial de flota";
  progressBar.value = 0;
  state.fleetFile = file;
  const load: ToWorker = {
    type: "fleet",
    protocolVersion: PROTOCOL_VERSION,
    jobId,
    file,
    circuitId,
    ...(circuitName === undefined ? {} : { circuitName }),
  };
  worker.postMessage(load);
}

function startLists(file: File, circuitId: string): void {
  disposeWorker();
  clearMessages();

  const jobId = crypto.randomUUID();
  const worker = new Worker(new URL("../../workers/import.worker.ts", import.meta.url), {
    type: "module",
  });
  state.worker = worker;
  state.jobId = jobId;
  worker.onmessage = (event: MessageEvent<FromWorker>) => handleMessage(event.data);
  worker.onerror = () => {
    showMessage("error", "El proceso auxiliar se detuvo", [
      "Las listas no llegaron a cargarse y el circuito no se ha modificado.",
    ]);
    setBusy(false);
    disposeWorker();
  };

  setBusy(true);
  progressNote.textContent = "Leyendo las listas";
  progressBar.value = 0;
  const load: ToWorker = {
    type: "lists",
    protocolVersion: PROTOCOL_VERSION,
    jobId,
    file,
    circuitId,
  };
  worker.postMessage(load);
}

function startConnections(files: readonly { readonly file: File; readonly agvId: string }[], circuitId: string): void {
  disposeWorker();
  clearMessages();
  const jobId = crypto.randomUUID();
  const worker = new Worker(new URL("../../workers/import.worker.ts", import.meta.url), {
    type: "module",
  });
  state.worker = worker;
  state.jobId = jobId;
  worker.onmessage = (event: MessageEvent<FromWorker>) => handleMessage(event.data);
  worker.onerror = () => {
    showMessage("error", "El proceso auxiliar se detuvo", [
      "El registro de conexiones no llegó a cargarse y el circuito no se ha modificado.",
    ]);
    setBusy(false);
    disposeWorker();
  };
  setBusy(true);
  progressNote.textContent = `Leyendo ${files.length} ${files.length === 1 ? "fichero" : "ficheros"} del registro de conexiones`;
  progressBar.value = 0;
  const load: ToWorker = { type: "connections", protocolVersion: PROTOCOL_VERSION, jobId, circuitId, files };
  worker.postMessage(load);
}

/**
 * Antes de cargar el registro de conexiones se enseña a qué AGV va cada fichero —sacado de su nombre—
 * y se puede corregir (OQ-160). Un fichero cuyo nombre no lo lleva queda en blanco y hay que escribirlo.
 */
function confirmConnections(files: readonly File[], circuitId: string): void {
  connectionsConfirm.replaceChildren();
  connectionsConfirm.hidden = false;
  connectionsConfirm.append(
    element("p", "muted", `${files.length} ${files.length === 1 ? "fichero" : "ficheros"} para el circuito «${circuitId}». Comprueba el AGV de cada uno y confirma.`),
  );
  const list = element("ul", "connections-files");
  const inputs: { file: File; input: HTMLInputElement }[] = [];
  files.forEach((file, index) => {
    const item = element("li");
    const name = element("span", "mono", file.name);
    const label = element("label", undefined, " AGV ");
    const input = element("input");
    input.type = "text";
    input.id = `connections-agv-${index}`;
    input.value = agvFromFileName(file.name) ?? "";
    input.placeholder = "escribe el AGV";
    input.setAttribute("aria-label", `AGV del fichero ${file.name}`);
    label.htmlFor = input.id;
    item.append(name, label, input);
    list.append(item);
    inputs.push({ file, input });
  });
  const load = element("button", undefined, `Cargar ${files.length === 1 ? "el fichero" : `los ${files.length} ficheros`}`);
  load.type = "button";
  load.addEventListener("click", () => {
    const missing = inputs.filter((entry) => entry.input.value.trim() === "");
    if (missing.length > 0) {
      showMessage("warn", "Falta el AGV de algún fichero", [
        `${missing.map((entry) => entry.file.name).join(", ")}: el nombre no lleva el AGV. Escríbelo antes de cargar.`,
      ]);
      return;
    }
    connectionsConfirm.hidden = true;
    startConnections(
      inputs.map((entry) => ({ file: entry.file, agvId: entry.input.value.trim() })),
      circuitId,
    );
  });
  const cancel = element("button", undefined, "Cancelar");
  cancel.type = "button";
  cancel.addEventListener("click", () => {
    connectionsConfirm.hidden = true;
    connectionsConfirm.replaceChildren();
  });
  connectionsConfirm.append(list, load, cancel);
}

/**
 * Permite volver a elegir **el mismo fichero** y que vuelva a importarse.
 *
 * Sin esto, `change` no se dispara la segunda vez y el usuario ve que no pasa nada. No es un caso
 * raro en este producto: los solapes entre exportaciones son deliberados y reimportar una que ya
 * está cargada es una operación normal.
 *
 * El valor se reinicia **al abrir el selector**, no al procesar el fichero. Limpiarlo después de
 * elegir vacía la `FileList` y con ella el `File` que aún no se ha leído: `arrayBuffer()` falla y
 * la carga se rechaza sola. Es exactamente el defecto que introdujo el primer intento de arreglo
 * de esto, y lo destapó la prueba de navegador en la misma ejecución.
 */
for (const input of [fileInput, projectInput, listsInput, fleetInput, connectionsInput]) {
  input.addEventListener("click", () => {
    input.value = "";
  });
}

listsInput.addEventListener("change", () => {
  const file = listsInput.files?.[0];
  if (file === undefined) return;
  const circuitId = circuitInput.value.trim();
  if (circuitId === "") {
    showMessage("warn", "Falta el circuito", [
      "Las listas pertenecen a un circuito concreto: escribe cuál antes de cargarlas.",
    ]);
    return;
  }
  startLists(file, circuitId);
});

fleetInput.addEventListener("change", () => {
  const file = fleetInput.files?.[0];
  if (file === undefined) return;
  const circuitId = circuitInput.value.trim();
  if (circuitId === "") {
    showMessage("warn", "Falta el circuito", [
      "El historial pertenece a un circuito concreto: escribe cuál antes de cargarlo.",
    ]);
    return;
  }
  startFleet(file, circuitId);
});

connectionsInput.addEventListener("change", () => {
  const files = [...(connectionsInput.files ?? [])];
  if (files.length === 0) return;
  const circuitId = circuitInput.value.trim();
  if (circuitId === "") {
    showMessage("warn", "Falta el circuito", [
      "El registro de conexiones pertenece a un circuito concreto: escribe cuál antes de cargarlo.",
    ]);
    return;
  }
  confirmConnections(files, circuitId);
});

fileInput.addEventListener("change", () => {
  const file = fileInput.files?.[0];
  if (file !== undefined) startImport(file);
});

/**
 * Plazo de cortesía de la cancelación (WP-004, `WORKER_PROTOCOL.md` §5). La lectura de filas es
 * síncrona y no atiende `cancel` hasta terminar; si en este plazo no llega `cancelled` y la
 * importación todavía no ha empezado a escribir, se detiene el Worker: no había escrito nada. Es un
 * tiempo de la interfaz, no un valor de planta, y deja la confirmación por debajo del segundo del
 * presupuesto (`PERFORMANCE_BUDGET.md` §3).
 */
const CANCEL_GRACE_MS = 500;

cancelButton.addEventListener("click", () => {
  if (state.worker === null || state.jobId === null) return;
  progressNote.textContent = "Cancelando…";
  const jobId = state.jobId;
  const cancel: ToWorker = {
    type: "cancel",
    protocolVersion: PROTOCOL_VERSION,
    jobId,
    reason: "petición del usuario",
  };
  state.worker.postMessage(cancel);
  if (!state.importStoppable) return;
  setTimeout(() => {
    // Ya contestó, terminó o empezó a escribir: no se toca.
    if (state.jobId !== jobId || !state.importStoppable) return;
    disposeWorker();
    showMessage("info", "Importación cancelada", [
      "No se ha cambiado nada.",
      "El proceso auxiliar estaba leyendo las filas y no podía atender la petición, así que se ha detenido.",
    ]);
    setBusy(false);
    benchObserver?.cancelled();
  }, CANCEL_GRACE_MS);
});

// --- Proyecto: acumular en el dispositivo y llevárselo ----------------------

function formatSpan(from: number, to: number): string {
  return `${formatInstant(from)} → ${formatInstant(to)}`;
}

/**
 * Lo que la acumulación añade al resumen.
 *
 * La cobertura va **siempre** junto a cualquier cifra temporal (R-DAT-007): fuera de ella no hay
 * silencio, hay ausencia de datos, y dar una cifra sin decir qué periodo cubre invita justo a la
 * confusión que la regla prohíbe.
 */
function renderAccumulation(report: {
  readonly circuitId: string;
  readonly coverage: readonly { readonly from: number; readonly to: number }[];
  readonly totalReadings: number;
  readonly sources: number;
  readonly shared: number;
  readonly disagreements: number;
  readonly retained: { readonly sourceIds: readonly string[]; readonly distinctSources: number };
  readonly snapshots: { readonly withSnapshot: number; readonly withoutSnapshot: number };
}): void {
  // El nombre del circuito abre el Resumen, que es lo primero que se ve; en Datos, lo acumulado.
  const resumen = viewsOf.get("resumen") as HTMLElement;
  resumen.append(element("h2", undefined, `Circuito «${report.circuitId}»`));
  resumen.hidden = false;
  summaryPanel.append(element("h2", undefined, `Lo acumulado en «${report.circuitId}»`));
  const rows: readonly (readonly [string, string])[] = [
    ["Ficheros cargados", String(report.sources)],
    // Las lecturas en crudo solo viven en la ventana de trabajo (ADR-0015 §2, R-DAT-023): el
    // expediente y el replay alcanzan justo esto, y lo demás queda como instantánea.
    [
      "Lecturas retenidas",
      `${report.totalReadings.toLocaleString("es-ES")} lecturas de ${report.retained.sourceIds.length} ` +
        `${report.retained.sourceIds.length === 1 ? "fichero" : "ficheros"} de ${report.retained.distinctSources}`,
    ],
    [
      "Instantáneas",
      `${report.snapshots.withSnapshot} de ${plural(report.retained.distinctSources, "fichero", "ficheros")}` +
        (report.snapshots.withoutSnapshot === 0
          ? ""
          : `; ${report.snapshots.withoutSnapshot} sin instantánea (anteriores a esta versión o sin construir): vuelve a cargarlos para crearla`),
    ],
    [
      "Cobertura",
      report.coverage.length === 0
        ? "todavía ninguna"
        : report.coverage.map((span) => formatSpan(span.from, span.to)).join("  ·  "),
    ],
    [
      "Repetido con lo ya cargado",
      report.shared === 0
        ? "nada"
        : `${report.shared.toLocaleString("es-ES")} eventos ya estaban y se cuentan una vez`,
    ],
  ];
  const list = element("dl", "facts");
  for (const [term, value] of rows) {
    list.append(element("dt", undefined, term), element("dd", undefined, value));
  }
  summaryPanel.append(list);

  if (report.coverage.length > 1) {
    summaryPanel.append(
      element(
        "p",
        "muted",
        "Entre esos tramos no hay datos cargados: no es un silencio del circuito, es tiempo sin exportar.",
      ),
    );
  }
  if (report.disagreements > 0) {
    summaryPanel.append(
      element(
        "p",
        "muted",
        `${report.disagreements} eventos del tramo común los trae solo una de las dos ` +
          "exportaciones. Se conservan los dos.",
      ),
    );
  }
}

/**
 * El veredicto de afinidad, que es lo que impide el error que no avisa.
 *
 * Cargar en un circuito la exportación de otro no produce ningún mensaje: las columnas son las
 * mismas y las fechas también. Lo que produce es un circuito con dos anillos superpuestos y
 * conclusiones equivocadas meses después, cuando ya no hay forma de separarlos.
 */
function renderAffinity(report: AccumulationReport): void {
  const { affinity } = report;
  if (!report.accumulated) {
    showMessage("error", "No se ha acumulado: parece de otro circuito", [
      affinity.reason,
      `Coinciden ${affinity.sharedTags} de los ${affinity.sourceTags} tags de la fuente.`,
      "Las lecturas se muestran abajo para comprobarlo, pero el circuito no se ha tocado. Revisa el " +
        "nombre del circuito o cárgalo en el suyo.",
    ]);
    return;
  }
  if (affinity.verdict === "partially-compatible") {
    summaryPanel.append(element("p", "muted", affinity.reason));
  }
  if (affinity.verdict === "unknown") {
    summaryPanel.append(element("p", "muted", affinity.reason));
  }
}

/** Donde escribe la función `render*` en curso: el contenedor de vistas de una pestaña. */
let out: HTMLElement = viewsOf.get("resumen") as HTMLElement;

/** Vacía las pestañas, la bandeja y la barra de revisión: cada importación las rehace enteras. */
function resetViews(): void {
  for (const panel of viewsOf.values()) {
    panel.replaceChildren();
    panel.hidden = true;
  }
  tabPanels.get("resumen")?.querySelector(".tab-empty")?.remove();
  tray.replaceChildren();
  trayPanel.replaceChildren();
  trayPanel.hidden = true;
  reviewHost.replaceChildren();
  reviewHost.hidden = true;
  reviewSession?.dispose();
  reviewSession = null;
  tiles.replaceChildren();
  closeDrawer();
}

/**
 * Las vistas, repartidas por pregunta en las pestañas (UX_SPEC §2). Se construyen todas al llegar,
 * cada una en su pestaña oculta: la bandeja del Resumen necesita todas las tarjetas, y las tarjetas
 * nacen dentro de la sección que las explica. Los gráficos no pagan por estar ocultos: miden su
 * ancho con `ResizeObserver` y se dibujan la primera vez que su pestaña se enseña.
 */
function renderViews(views: CircuitViews): void {
  currentTagInfo = views.tagInfo ?? {};
  currentBatteries = views.incidents?.batteries ?? {};
  // La revisión en campo solo existe sobre un circuito: sin él, una marca no tendría dónde guardarse.
  reviewSession?.dispose();
  reviewSession =
    state.circuitId === null
      ? null
      : createReviewSession(
          state.circuitId,
          tray,
          formatInstant,
          (reason) => showMessage("error", "Revisión sin guardar", [reason]),
          (card) => {
            activateTab("resumen");
            // Limpieza y contexto va plegado (UX_SPEC §4.5): la siguiente pendiente puede estar dentro.
            const fold = card.closest<HTMLDetailsElement>("details.tray-fold");
            if (fold !== null) fold.open = true;
          },
        );
  reviewHost.hidden = reviewSession === null;
  if (reviewSession !== null) reviewHost.append(reviewSession.bar);

  // --- Resumen: de qué está hecho el circuito (el título lo puso `renderAccumulation`) -----------
  out = viewsOf.get("resumen") as HTMLElement;
  if (out.childElementCount === 0) out.append(element("h2", undefined, "Resumen"));
  // Cohortes (R-DAT-012): un solo grupo es lo esperado; más de uno avisa de que el fichero mezcla
  // circuitos, que es justo el error que la comparación por cohorte existe para impedir.
  //
  // El recuento de tags va junto al de vehículos porque responde la misma pregunta —de qué está
  // hecho esto— y sale del ciclo dominante, no de contar identificadores distintos: un tag leído
  // una vez desde una rama no forma parte del anillo.
  const shapeOf = (cohortId: number): CircuitViews["shapes"][number] | undefined =>
    views.shapes.find((shape) => shape.cohortId === cohortId);
  const describe = (cohort: { readonly id: number; readonly vehicles: readonly string[] }): string => {
    const shape = shapeOf(cohort.id);
    return shape === undefined
      ? `${cohort.vehicles.length} vehículos, sin anillo reconocible`
      : `${cohort.vehicles.length} vehículos y ${shape.tags.length} tags en el anillo`;
  };
  const cohortLine =
    views.cohorts.length === 1 && views.cohorts[0] !== undefined
      ? `1 circuito de ${describe(views.cohorts[0])}`
      : `${views.cohorts.length} circuitos detectados en el mismo fichero: ` +
        views.cohorts.map(describe).join("; ");
  // La portada (UX_SPEC §2): la conclusión en una frase y los saltos (§4.8), la tira de cifras, la
  // composición y el anillo con capas. Las cifras y la frase se rellenan al final, cuando la bandeja ya
  // existe: cuentan sus tarjetas.
  statusHost = element("div", "status-host");
  out.append(statusHost);
  out.append(tiles);
  out.append(element("h3", undefined, "Composición del circuito"));
  out.append(element("p", "muted", cohortLine));
  // Los avisos (UX_SPEC §4.6) se rellenan al final, cuando todas las pestañas existen y sus secciones
  // tienen identificador: cada aviso lleva a su sitio.
  noticesHost = element("div", "notices-host");
  out.append(noticesHost);
  renderRingFigure(views);
  // La evolución (ADR-0015 §3) se rellena cuando las instantáneas llegan del almacén (`loadEvolution`).
  evolutionHost = element("div", "evolution-host");
  out.append(evolutionHost);

  // --- Datos: lo cargado, tal cual --------------------------------------------------------------
  out = viewsOf.get("datos") as HTMLElement;
  out.append(element("h2", undefined, "Lo cargado"));
  if (state.coverage.length > 0) out.append(coverageChart(state.coverage, formatInstant));
  out.append(hourlyChart({ counts: views.hourly.counts, days: views.hourly.days }));
  out.append(
    activityChart(
      {
        rows: views.activity.rows,
        binStarts: views.activity.binStarts,
        uncoveredBins: views.activity.uncoveredBins,
        maxPerBin: views.activity.maxPerBin,
      },
      formatInstant,
    ),
  );
  // Qué queda de cada fichero en este dispositivo (R-DAT-023): lecturas, instantánea o nada.
  if (views.snapshots.list.length > 0) out.append(sourceStatusList(views.snapshots, formatTick));

  // --- Tags: los problemas primero, y el inventario como contexto al final (UX_SPEC §4.7) ---------
  out = viewsOf.get("tags") as HTMLElement;
  out.append(element("h2", undefined, "Tags"));
  renderReadMatrix(views);
  renderTagChanges(views);
  renderUndeclaredTags(views);
  renderListCleanup(views);
  if (views.vsystemContrast !== undefined) {
    out.append(element("h3", undefined, "Contraste contra Vsystem"));
    out.append(
      element(
        "p",
        "muted",
        "La lista del circuito frente al orden en que leen los AGV. Manda lo leído: la lista puede " +
          "tener erratas al transcribir o un orden distinto al real, así que una diferencia es algo que " +
          "corregir en la lista, no un fallo del circuito. Solo se listan las diferencias; una «posible " +
          "sustitución o número mal escrito» se comprueba en planta.",
      ),
    );
    out.append(
      plainTable(
        ["Declarado", "Observado", "Veredicto", "Estado", "Evidencia"],
        views.vsystemContrast
          .filter((row) => row.verdict !== "coincide")
          .map((row) => [
            row.declaredTag ?? "—",
            row.observedTag ?? "—",
            verdictLabel(row.verdict),
            truthLabel(row.truth),
            row.evidence,
          ]),
      ),
    );
  }
  renderCircuitOrder(views);
  renderDrift(views);
  out.append(
    inventoryChart(
      (views.inventory?.counts ?? []).map((entry) => ({
        label: entry.tagClass,
        count: entry.count,
        truth: entry.truth,
        action: entry.action,
      })),
    ),
  );

  // --- AGV: la flota, quién lee qué, el ritmo. Después de Tags: las tarjetas de AGV que no leen
  // los mismos tags que un grupo de «Lo que hay que mirar» se pliegan en él. ----------------------
  out = viewsOf.get("agv") as HTMLElement;
  out.append(element("h2", undefined, "AGV"));
  // Los fallos de cada AGV primero (UX_SPEC §4.7): su lectura, los que dejan de leer, su ritmo; la
  // flota y la vida de cada uno, que son contexto, detrás.
  renderVehicleReadingSection(views);
  renderAbandonedVehicles(views);
  renderPace(views);
  renderFleet(views);

  // --- Tiempos: el estado normal, las secciones, los ficheros, los críticos y el anillo ----------
  out = viewsOf.get("tiempos") as HTMLElement;
  out.append(element("h2", undefined, "Tiempos"));
  renderCircuitState(views);
  renderAnchorSections(views);
  renderFranjas(out, views, { finding, formatInstant, formatTick, duration, circuitId: state.circuitId });
  timesEvolutionHost = element("div", "evolution-host");
  out.append(timesEvolutionHost);
  renderCriticalPoints(views);
  renderRing(views);

  // --- WiFi: el estado de conexión observado (DS-013, ADR-0017), solo con registro cargado --------
  out = viewsOf.get("wifi") as HTMLElement;
  out.append(element("h2", undefined, "WiFi"));
  const wifiTab = tabButtons.get("wifi") as HTMLButtonElement;
  wifiTab.hidden = views.connections === undefined;
  if (views.connections !== undefined) renderWifi(out, views);
  else if (activeTab === "wifi") activateTab("resumen");

  // --- Línea y calles ---------------------------------------------------------------------------
  out = viewsOf.get("linea") as HTMLElement;
  out.append(element("h2", undefined, "Línea y calles"));
  renderLineFeed(views);
  renderAbandoned(views);
  renderCharging(views);
  renderFifo(views);

  renderUncardedFindings(views);
  for (const panel of viewsOf.values()) panel.hidden = panel.childElementCount === 0;
  buildTray();
  renderNotices(views);
  reviewSession?.refresh();
  renderTiles(views);
  renderStatus(views);
  reviewSession?.onChange(() => {
    renderFindingsTile();
    renderStatus(views);
    // La lista de condiciones de «Consolidar periodo» cuenta las tarjetas de la bandeja.
    memoryPanel.refresh();
  });
}

/**
 * El orden del circuito según las lecturas, tag a tag contra la lista (R-GRA-015). Es la lista que
 * dictan los AGV: lo leído en su sitio leído, y lo que no se lee donde lo pone la lista, dicho así.
 */
function renderCircuitOrder(views: CircuitViews): void {
  const order = views.circuitOrder;
  if (order === undefined) return;
  const s = order.summary;
  const count = (value: number, one: string, many: string) => `${value} ${value === 1 ? one : many}`;
  out.append(element("h3", undefined, "Orden del circuito según las lecturas"));
  out.append(
    element(
      "p",
      undefined,
      `Según las lecturas: ${count(s.igual, "tag en el orden de la lista", "tags en el orden de la lista")}, ` +
        `${count(s["otro-sitio"], "que la lista pone en otro sitio", "que la lista pone en otro sitio")}, ` +
        `${count(s["sin-lecturas"], "sin lecturas", "sin lecturas")} (su posición es solo la de la lista), ` +
        `${count(s["no-en-la-lista"], "leído que la lista no tiene", "leídos que la lista no tiene")} y ` +
        `${count(s["fuera-del-recorrido"], "leído fuera del recorrido principal", "leídos fuera del recorrido principal")}.`,
    ),
  );
  out.append(
    lazyTable(`Ver el orden del circuito según las lecturas (${order.rows.length} tags)`, () =>
      plainTable(
        ["Posición", "Tag", "Lectura", "En la lista", "Diferencia", "Según las lecturas", "Según la lista", "Declarado en planta"],
        order.rows.map((row) => [
          String(row.position),
          row.tagId,
          orderReadingLabel(row.reading),
          row.listPosition === null ? "—" : String(row.listPosition),
          orderChangeLabel(row.change),
          row.readBetween ?? "—",
          row.listBetween ?? "—",
          currentTagInfo[row.tagId] ?? "",
        ]),
      ),
    ),
  );
}

/**
 * AGV que dejan de leer antes del final de lo cargado (R-AGV-021), cada uno con su batería: dónde
 * leyó por última vez, si la línea seguía, qué hicieron los de delante y los de detrás, y si empezó a
 * leer otro AGV después (cambio de AGV).
 */
function renderAbandoned(views: CircuitViews): void {
  const records = views.incidents?.records ?? [];
  if (records.length > 0) {
    out.append(element("h3", undefined, "Incidencias y sus mediciones"));
    out.append(
      element(
        "p",
        "muted",
        `${records.length} incidencias —paradas sin explicación, primeros de cola sin avanzar y AGV que dejan de leer— con la ` +
          "misma batería de mediciones: última lectura, la línea, el de delante, los de detrás y el cambio de AGV.",
      ),
    );
    const download = element("button", undefined, "Descargar las incidencias con sus mediciones (CSV)");
    download.setAttribute("type", "button");
    download.addEventListener("click", () => {
      const url = URL.createObjectURL(new Blob([`\ufeff${incidentsCsv(records, formatInstant)}`], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `incidencias-${state.circuitId ?? "circuito"}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    });
    out.append(download);
  }
}

/**
 * Los AGV que dejan de leer (R-AGV-021), en la pestaña AGV desde 3.71.0: es un fallo del vehículo y
 * ahí es donde se detallan (propietario, 2026-10-17). Sus mediciones siguen en el CSV de incidencias.
 */
function renderAbandonedVehicles(views: CircuitViews): void {
  const abandoned = views.incidents?.abandoned ?? [];
  if (abandoned.length === 0) return;
  out.append(element("h3", undefined, "AGV que dejan de leer"));
  out.append(
    element(
      "p",
      "muted",
      `${abandoned.length} ${abandoned.length === 1 ? "AGV deja" : "AGV dejan"} de leer antes del final de lo cargado, más tiempo que su ` +
        "propio hueco más largo del que sí volvió. De cada uno, las mismas mediciones.",
    ),
  );
  const cards = element("div", "findings");
  for (const { incident, battery } of abandoned.slice(0, HIGHLIGHTS)) {
    const card = finding(
      `${incident.agvId}: deja de leer en ${incident.fromTagId}`,
      `Desde ${formatInstant(incident.fromUtcMs)}${battery.swap === null ? "" : ` · empieza ${battery.swap.agvId}`}`,
      battery.reading === "adelantado"
        ? "Los de detrás lo adelantaron: no se movía en la guía."
        : battery.reading === "fuera-o-sin-registrar"
        ? "Los de detrás pasaron su sitio y dieron vueltas enteras: salió de la guía o no registra lecturas."
        : battery.reading === "no-registra"
        ? "Los de detrás pasaron su sitio: seguía avanzando sin registrar lecturas."
        : battery.reading === "parado-con-cola"
          ? "Los de detrás no pasaron de su sitio: estaba parado de verdad."
          : "Sin AGV detrás para saber si avanzaba.",
      ["deja-de-leer", `${incident.agvId} ${incident.fromTagId} ${incident.fromUtcMs}`],
    );
    const list = document.createElement("ol");
    list.className = "battery";
    for (const text of battery.lines) list.append(element("li", undefined, text));
    card.append(element("p", "finding-figure", "Mediciones"), list);
    cards.append(card);
  }
  out.append(cards);
}

/**
 * Alimentación de la línea (R-FLO-010): cadencia en la entrada, pulmón medido y cada parada de la
 * línea, con AGV esperando o sin ellos. Hechos con sus tiempos, sin causa.
 */
function renderLineFeed(views: CircuitViews): void {
  const feed = views.lineFeed;
  if (feed === undefined) return;
  out.append(element("h3", undefined, "Alimentación de la línea"));
  if (!feed.evaluated || feed.cadence === null) {
    out.append(element("p", "muted", feed.reason ?? "Sin medir."));
    return;
  }
  const c = feed.cadence;
  const seconds = (ms: number) => duration(ms);
  const faltaron = feed.stops.filter((stop) => stop.kind === "sin-agv");
  const sinMedir = feed.stops.filter((stop) => stop.kind === "sin-medir").length;
  out.append(
    element(
      "p",
      undefined,
      `Entrada ${feed.entryTagId}: ${feed.passes.toLocaleString("es-ES")} pasos. Entre dos AGV, en producción, la mitad de las veces ` +
        `${seconds(c.p50Ms)} o menos, el 95 % ${seconds(c.p95Ms)}; por encima de ${seconds(c.fenceMs)} la línea está sin paso. ` +
        (sinMedir > 0
          ? `${feed.stops.length} paradas, sin pulmón medido para saber si había AGV esperando.`
          : `${feed.stops.length} paradas: ${feed.stops.length - faltaron.length} con AGV esperando y ${faltaron.length} en que le faltaron AGV.`),
    ),
  );
  // El ritmo en cada régimen: el ciclo no es fijo, así que se mide contra el ciclo local.
  const minutes = (ms: number) => `${Math.round(ms / 60_000).toLocaleString("es-ES")} min`;
  for (const rhythm of feed.rhythm) {
    const share = rhythm.observedMs === 0 ? 0 : Math.round((rhythm.lostMs / rhythm.observedMs) * 100);
    out.append(
      element(
        "p",
        undefined,
        `${rhythm.regime === "produccion" ? "Producción" : "Noche"}: un AGV cada ${seconds(rhythm.cycleMs)} de mediana; el ciclo ` +
          `anda entre ${seconds(rhythm.cycleLowMs)} y ${seconds(rhythm.cycleHighMs)} según el periodo. Sin paso (por encima de la ` +
          `valla), ${minutes(rhythm.aboveFenceMs)}; por encima de su ciclo local, ${minutes(rhythm.lostMs)} de ${minutes(rhythm.observedMs)} (${share} %)` +
          (rhythm.lostWithAgvMs === null || rhythm.lostWithoutAgvMs === null
            ? "."
            : `: ${minutes(rhythm.lostWithAgvMs)} con AGV esperando (la línea no los tomaba) y ` +
              `${minutes(rhythm.lostWithoutAgvMs)} sin AGV (le faltaron).`),
      ),
    );
  }
  const zone = feed.zone;
  out.append(
    element(
      "p",
      zone === null ? "muted" : undefined,
      zone === null
        ? (feed.reason ?? "Pulmón sin medir.")
        : `Pulmón medido: desde ${zone.fromTagId} (a ${seconds(zone.etaMs)} de la entrada) hasta la entrada. En las paradas ` +
          `esperaban ${zone.typical} AGV de mediana y hasta ${zone.capacity}. ` +
          `Minutos de producción con cada número de AGV en el pulmón: ` +
          feed.occupancy.map((entry) => `${entry.agvs} AGV, ${entry.minutes} min`).join("; ") +
          ". Cada AGV en el pulmón son unos " +
          `${seconds(c.p50Ms)} de línea asegurada.`,
    ),
  );
  const cards = element("div", "findings");
  for (const stop of [...faltaron, ...feed.stops.filter((stop) => stop.kind === "con-pulmon")].slice(0, HIGHLIGHTS)) {
    cards.append(
      finding(
        `${formatInstant(stop.fromUtcMs)}${stop.regime === "noche" ? " (noche)" : ""}: ${stop.kind === "sin-agv" ? "le faltaron AGV" : stop.kind === "con-pulmon" ? "parada con AGV esperando" : "parada, sin pulmón medido"}`,
        `${seconds(stop.durationMs)} sin paso · entró antes ${stop.before}, después ${stop.after}`,
        stop.evidence,
        ["linea", stop.after],
      ),
    );
  }
  out.append(cards);
  // Cada paso por la línea (R-FLO-011): quién no lee sus tags, quién no sigue y quién no para.
  const passages = feed.passages;
  if (passages !== null) {
    out.append(
      element(
        "p",
        undefined,
        `${passages.total.toLocaleString("es-ES")} pasos por la línea; se leen ${passages.expected.join(", ") || "—"} en la mitad o más. ` +
          (passages.exit === null ? "" : `De la línea al siguiente tag, lo habitual hasta ${seconds(passages.exit.fenceMs)}. `) +
          (passages.parallel
            ? "Es una línea de vinculación: el AGV sigue en paralelo y no se busca si hizo la parada."
            : passages.minGapMs === null
              ? "Sin muestras bastantes para decir cuánto deja la línea, como mínimo, entre dos AGV."
              : `La línea no deja menos de ${seconds(passages.minGapMs)} entre dos AGV.`),
      ),
    );
    if (passages.readers.length > 0) {
      out.append(
        element(
          "p",
          undefined,
          "AGV que hacen la parada con la cadencia normal pero no leen algún tag de la línea (revisar su lector o su memoria): " +
            passages.readers
              .map((reader) => `${reader.agvId}, sin ${reader.tags.join(" ni ")} en ${reader.missedPasses} de ${reader.passes} pasos`)
              .join("; ") +
            ".",
        ),
      );
    }
    const passCards = element("div", "findings");
    for (const issue of passages.issues.slice(0, HIGHLIGHTS)) {
      passCards.append(
        finding(
          `${formatInstant(issue.fromUtcMs)}: ${issue.kind === "no-sigue" ? "no sigue tras la línea" : "pasó sin la parada"}`,
          issue.agvId,
          issue.evidence,
          ["linea-paso", issue.agvId],
        ),
      );
    }
    out.append(passCards);
  }
  const download = element("button", undefined, "Descargar paradas de la línea (CSV)");
  download.setAttribute("type", "button");
  download.addEventListener("click", () => {
    const url = URL.createObjectURL(new Blob([`\ufeff${lineStopsCsv(feed, formatInstant)}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `linea-${state.circuitId ?? "circuito"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  });
  out.append(download);
}

/**
 * Limpieza de la lista del circuito (R-GRA-017): declarados que no están en el físico, en otra
 * posición, y refuerzos declarados contra el recorrido. Preguntas para planta, con su acción.
 */
function renderListCleanup(views: CircuitViews): void {
  const cleanup = views.listCleanup;
  if (cleanup === undefined) return;
  const critical = cleanup.notPhysical.filter((tag) => tag.funcion !== null).length;
  const byStatus = (status: string) => cleanup.reinforcements.filter((group) => group.status === status).length;
  out.append(element("h3", undefined, "Limpieza de la lista del circuito"));
  out.append(
    element(
      "p",
      undefined,
      `${plural(cleanup.notPhysical.length, "declarado no está", "declarados no están")} en el circuito físico (${critical} con función crítica: ` +
        "comprobar si hay que colocar una copia o eliminarlos de Vsystem); " +
        `${cleanup.moved.length} están en otra posición que en la lista; de ${plural(cleanup.reinforcements.length, "refuerzo declarado", "refuerzos declarados")}, ` +
        `${plural(byStatus("comprobado"), "comprobado", "comprobados")} en el recorrido, ${plural(byStatus("incompleto"), "incompleto", "incompletos")} y ${plural(byStatus("separado"), "separado", "separados")}.`,
    ),
  );
  const download = element("button", undefined, "Descargar la limpieza (CSV)");
  download.setAttribute("type", "button");
  download.addEventListener("click", () => {
    // Con BOM y `;`, como las horquillas: se abre en una hoja de cálculo en español tal cual.
    const url = URL.createObjectURL(new Blob([`\ufeff${listCleanupCsv(cleanup)}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `limpieza-${state.circuitId ?? "circuito"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  });
  out.append(download);
  const fn = (funcion: string | null) => (funcion === null ? "—" : criticalFunctionLabel(funcion));
  out.append(
    lazyTable(countedLabel("Ver", cleanup.notPhysical.length, false, "declarado que no está en el físico", "declarados que no están en el físico"), () =>
      plainTable(
        ["Tag", "Función", "En la lista", "Según la lista", "Acción"],
        cleanup.notPhysical.map((tag) => [tag.tagId, fn(tag.funcion), String(tag.listPosition), tag.listBetween ?? "—", tag.action]),
      ),
    ),
    lazyTable(countedLabel("Ver", cleanup.moved.length, false, "que está en otra posición", "en otra posición"), () =>
      plainTable(
        ["Tag", "Función", "En la lista", "Según las lecturas", "Según la lista", "Leído entre"],
        cleanup.moved.map((tag) => [tag.tagId, fn(tag.funcion), String(tag.listPosition), String(tag.position), tag.listBetween ?? "—", tag.readBetween ?? "—"]),
      ),
    ),
    lazyTable(countedLabel("Ver", cleanup.reinforcements.length, false, "refuerzo declarado", "refuerzos declarados"), () =>
      plainTable(
        ["Tags", "Función", "Estado", "Detalle"],
        cleanup.reinforcements.map((group) => [group.tags.join(" + "), fn(group.funcion), group.status, group.detail]),
      ),
    ),
  );
}

/**
 * Tags que se leen y no están en la lista del circuito (R-DAT-022): dónde se leen y si solo de noche.
 * Candidato a una posición, tag de noche o posiblemente de noche; nunca una causa.
 */
function renderUndeclaredTags(views: CircuitViews): void {
  const tags = views.undeclaredTags;
  if (tags === undefined || tags.length === 0) return;
  const label = {
    posicion: "candidato a una posición",
    noche: "tag de noche",
    "noche-probable": "posiblemente de noche",
    "noche-declarado": "tag de noche declarado",
  } as const;
  out.append(element("h3", undefined, "Tags leídos fuera de la lista del circuito"));
  out.append(
    element(
      "p",
      "muted",
      `${tags.length} ${tags.length === 1 ? "tag se lee" : "tags se leen"} y no ${tags.length === 1 ? "está" : "están"} en la lista del circuito. De cada uno, ` +
        "dónde se lee y si de día o solo de noche. Son candidatos para comprobar en planta.",
    ),
  );
  const cards = element("div", "findings");
  // Su sitio en el orden que dictan las lecturas, no en el de la lista (R-GRA-015).
  const positionOf = new Map((views.circuitOrder?.rows ?? []).map((row) => [row.tagId, row.position]));
  for (const tag of tags.slice(0, HIGHLIGHTS)) {
    const position = positionOf.get(tag.tagId);
    cards.append(
      finding(
        `${tag.tagId}: ${label[tag.verdict]}`,
        `${(tag.dayReadings + tag.nightReadings).toLocaleString("es-ES")} lecturas` +
          (position === undefined ? "" : ` · posición ${position} según las lecturas`),
        tag.evidence,
        ["tag-fuera-del-circuito", tag.tagId],
      ),
    );
  }
  out.append(cards);
  if (tags.length > HIGHLIGHTS) {
    out.append(
      lazyTable(countedLabel("Ver", tags.length, false, "tag", "tags"), () =>
        plainTable(
          ["Tag", "Veredicto", "Día", "Noche", "Entre", "Y", "Pasadas de día (leído)", "Pasadas de noche (leído)"],
          tags.map((tag) => [
            tag.tagId,
            label[tag.verdict],
            String(tag.dayReadings),
            String(tag.nightReadings),
            tag.predecessor ?? "—",
            tag.successor ?? "—",
            `${tag.dayPasses} (${tag.dayHits})`,
            `${tag.nightPasses} (${tag.nightHits})`,
          ]),
        ),
      ),
    );
  }
}


/**
 * La flota del circuito a lo largo del tiempo (DS-012, R-AGV-014, R-AGV-015): el recuento N de M y
 * la vida de cada AGV. Sin historial, M son los vehículos que aparecen en las lecturas, y se dice.
 */
function renderFleet(views: CircuitViews): void {
  const fleet = views.fleet;
  if (fleet.vehicles.length === 0) return;
  out.append(element("h3", undefined, "Flota del circuito"));
  const assigned = fleet.vehicles.filter((vehicle) => vehicle.assignedEver);
  out.append(
    element(
      "p",
      "muted",
      fleet.historyLoaded
        ? `${assigned.length} AGV asignados según el historial de flota.`
        : fleet.historySource === "historial-vacio"
          ? "El historial de flota cargado no tiene ningún periodo válido: se cuentan solo los AGV que aparecen " +
            "en las lecturas. Revisa sus filas rechazadas en «Listas del circuito»."
          : "Sin historial de flota se cuentan solo los AGV que aparecen en las lecturas. Cárgalo en " +
            "«Listas del circuito» para ver también los asignados que no leen.",
    ),
  );
  out.append(fleetCountChart(fleet, state.coverage, FORMATS));
  renderFlowStops(fleet);

  if (fleet.historyLoaded) {
    const neverRead = assigned.filter((vehicle) => vehicle.readings === 0).map((vehicle) => vehicle.agvId);
    if (neverRead.length > 0) {
      out.append(
        finding(
          neverRead.length === 1
            ? "1 AGV asignado no leyó nada en toda la ventana"
            : `${neverRead.length} AGV asignados no leyeron nada en toda la ventana`,
          neverRead.join(", "),
          "Asignados y sin ninguna lectura: parados, fuera de servicio, en otro circuito o con el " +
            "historial desactualizado.",
          ["flota-sin-lecturas", "circuito"],
        ),
      );
    }
    const unassigned = fleet.vehicles
      .filter((vehicle) => vehicle.segments.some((segment) => segment.state === "leyendo-sin-asignar"))
      .map((vehicle) => vehicle.agvId);
    if (unassigned.length > 0) {
      out.append(
        finding(
          unassigned.length === 1
            ? "1 AGV lee en el circuito sin estar asignado"
            : `${unassigned.length} AGV leen en el circuito sin estar asignados`,
          unassigned.join(", "),
          "No cuentan en el circuito. O el historial no recoge un cambio, o vienen de otro circuito.",
          ["flota-sin-asignar", "circuito"],
        ),
      );
    }
  }
  out.append(fleetLifelineChart(fleet, FORMATS));
}

/**
 * Las paradas leídas contra el flujo (R-AGV-018): cuándo estuvo parada la producción y si todos
 * siguieron después por su sitio y en orden, y los primeros de cola que no avanzaron sin nada que lo
 * explique. Hechos con su evidencia; la causa la pone una persona (R-EVI-006).
 */
function renderFlowStops(fleet: CircuitViews["fleet"]): void {
  const { production, blockages } = fleet;
  const basisText =
    production.basis === "criticos"
      ? `ninguna lectura en los ${production.basisTags} tags críticos`
      : "ninguna lectura de toda la flota: no hay tags críticos declarados con que contrastarlo";
  if (production.stops.length > 0) {
    const when = production.stops.map(
      (stop) =>
        `${formatTick(stop.fromUtcMs)} a ${formatTick(stop.toUtcMs)}` +
        (stop.sameTimeOn.length > 0 ? " (se repite a esa hora otro día)" : ""),
    );
    const exceptions = production.stops.flatMap((stop) => stop.notInPlace);
    const orderBroken = production.stops.filter((stop) => stop.orderKept === false);
    const vehicles = Math.max(...production.stops.map((stop) => stop.vehicles));
    const resumed =
      exceptions.length === 0 && orderBroken.length === 0
        ? `En todas, los AGV parados siguieron por su tag y en el mismo orden (hasta ${vehicles} a la vez): ` +
          "no salieron del circuito."
        : [
            exceptions.length === 0
              ? ""
              : `No siguieron por su sitio: ${exceptions
                  .slice(0, 5)
                  .map((entry) => `${entry.agvId} (de ${entry.fromTagId} a ${entry.toTagId})`)
                  .join(", ")}${exceptions.length > 5 ? "…" : ""}.`,
            orderBroken.length === 0
              ? ""
              : `El orden cambió: ${orderBroken
                  .flatMap((stop) => stop.orderChanges)
                  .slice(0, 4)
                  .map((change) => `${change.agvId} aparece delante de ${change.passed}`)
                  .join(", ")}.`,
          ]
            .filter((part) => part !== "")
            .join(" ");
    out.append(
      finding(
        production.stops.length === 1
          ? "La producción se paró 1 vez"
          : `La producción se paró ${production.stops.length} veces`,
        when.join(" · "),
        `Tramos con ${basisText}, más largos de lo que el azar explica en ese turno. ${resumed}`.trim(),
        ["produccion-parada", "circuito"],
      ),
    );
  }
  const shown = blockages.slice(0, PER_KIND);
  for (const blockage of shown) {
    const where = blockage.functionAtTag === null ? blockage.tagId : `${blockage.tagId} (${blockage.functionAtTag})`;
    out.append(
      finding(
        `${blockage.agvId}: el primero de la cola, sin avanzar ${duration(blockage.toUtcMs - blockage.fromUtcMs)}`,
        `En ${where}, de ${formatTick(blockage.fromUtcMs)} a ${formatTick(blockage.toUtcMs)}; lo habitual hasta ` +
          `${blockage.nextTagId}: ${duration(blockage.usualMs)}`,
        `Nadie delante que lo retuviera y la producción en marcha: ${blockage.basisReads} lecturas ` +
          `${production.basis === "criticos" ? "en los tags críticos" : "de la flota"} mientras tanto. ` +
          (blockage.behind.length === 0
            ? "Nadie quedó detrás."
            : `${blockage.behind.length} AGV quedaron detrás: ${blockage.behind.slice(0, 6).join(", ")}` +
              `${blockage.behind.length > 6 ? "…" : ""}.`),
        ["bloqueo", `${blockage.agvId} ${blockage.tagId} ${blockage.fromUtcMs}`],
      ),
    );
    const blockCard = out.lastElementChild;
    if (blockCard instanceof HTMLElement) withBattery(blockCard, `${blockage.agvId} ${blockage.tagId} ${blockage.fromUtcMs}`);
  }
  if (blockages.length > shown.length) {
    out.append(
      lazyTable(countedLabel("Ver", blockages.length, false, "primero de cola sin avanzar", "primeros de cola sin avanzar"), () =>
        plainTable(
          ["AGV", "Tag", "Desde", "Hasta", "Lo habitual", "Detrás", "Lecturas mientras tanto"],
          blockages.map((blockage) => [
            blockage.agvId,
            blockage.tagId,
            formatTick(blockage.fromUtcMs),
            formatTick(blockage.toUtcMs),
            duration(blockage.usualMs),
            String(blockage.behind.length),
            String(blockage.basisReads),
          ]),
        ),
      ),
    );
  }
}

/**
 * El estado normal del circuito (R-TIM-009): lo que se mide con la horquilla de cada tramo en
 * producción —cuellos de botella, puntos conflictivos, zonas oscuras y paradas sin explicación—, la
 * noche aparte, y el cambio de la horquilla entre el primer y el último periodo cargados. Nada de
 * esto nombra causas (R-EVI-006).
 */
function renderCircuitState(views: CircuitViews): void {
  const { circuitState } = views;
  if (circuitState.cohorts.length === 0) return;
  const pad = (hour: number): string => String(hour).padStart(2, "0");
  const nightLabel = `de ${pad(circuitState.night.fromHour)}:00 a ${pad(circuitState.night.toHour)}:00`;
  const hours = (ms: number): string => `${(ms / 3_600_000).toFixed(1).replace(".", ",")} h`;
  const exposure = circuitState.exposure;
  out.append(element("h3", undefined, "Estado normal del circuito"));
  out.append(
    element(
      "p",
      "muted",
      `Tiempo cargado: ${hours(exposure.produccionMs)} de producción, ${hours(exposure.nocheMs)} de noche ` +
        `(${nightLabel}) y ${hours(exposure.paradaMs)} con la producción parada. Las horquillas y los ` +
        "hallazgos de esta sección usan solo producción: la noche se mide aparte y las paradas no miden nada.",
    ),
  );
  const few = (ids: readonly string[]): string => (ids.length > 4 ? `${ids.slice(0, 4).join(", ")}…` : ids.join(", "));
  const chance = (expected: number): string =>
    expected < 0.1 ? "menos de 0,1" : expected.toFixed(1).replace(".", ",");

  for (const cohort of circuitState.cohorts) {
    const shape = views.shapes.find((entry) => entry.cohortId === cohort.cohortId);
    const measured = cohort.state;
    if (circuitState.cohorts.length > 1) out.append(element("p", "muted", `Circuito ${cohort.cohortId}:`));

    for (const bottleneck of measured.bottlenecks.slice(0, PER_KIND)) {
      out.append(
        finding(
          `Cuello de botella en ${bottleneck.tagId}`,
          `${bottleneck.retentions} esperas detrás de un AGV que no avanzaba, en ${bottleneck.episodes} colas ` +
            `(la más larga, de ${bottleneck.longestQueue}); ${duration(bottleneck.waitMs)} de espera en total`,
          `El azar daría ${chance(bottleneck.expected)} con el tiempo que los AGV pasan ahí. ` +
            (bottleneck.blockages === 0
              ? "La cola fluye: el primero siempre acaba avanzando. "
              : `${bottleneck.blockages} veces el primero no avanzó en dos minutos o más. `) +
            "Una cola que fluye es saturación o un pulmón, no una avería.",
          ["cuello-de-botella", bottleneck.tagId],
        ),
      );
    }
    for (const point of measured.conflictPoints.slice(0, PER_KIND)) {
      const where = point.tags.join(" y ");
      out.append(
        finding(
          point.ofOneVehicle === null ? `Punto conflictivo en ${where}` : `${point.ofOneVehicle} para una y otra vez en ${where}`,
          `${point.stops} paradas sin explicación` +
            (point.ofOneVehicle === null ? ` de ${point.vehicles.length} AGV (${few(point.vehicles)})` : ", todas del mismo AGV"),
          `El azar daría ${chance(point.expected)} con las pasadas de ese sitio. En cada una, ` +
            "nadie delante lo retenía y la producción seguía. " +
            (point.ofOneVehicle === null
              ? "Dice dónde, no por qué."
              : "Siendo de un solo AGV, es de ese AGV y no del sitio."),
          ["punto-conflictivo", point.tags.join("+")],
        ),
      );
    }
    for (const zone of measured.darkZones.slice(0, PER_KIND)) {
      const first = zone.tags[0] ?? "—";
      const last = zone.tags[zone.tags.length - 1] ?? "—";
      out.append(
        finding(
          `Zona oscura de ${first} a ${last}`,
          `${duration(zone.gapMs)} entre dos lecturas al pasar por ahí; lo típico del circuito, ${duration(zone.typicalMs)}`,
          (zone.cause === "tag-sin-lecturas"
            ? `La lista del circuito declara ahí ${zone.missingTags.join(", ")}, que nadie lee: la información que falta es la ` +
              `${zone.missingTags.length === 1 ? "suya" : "de esos tags"} (ver la limpieza de la lista). `
            : zone.cause === "salta-tag"
              ? `El ${Math.round(zone.skipShare * 100)} % de las pasadas se salta algún tag de la zona: falta información ` +
                "porque esos tags se leen poco. "
              : "Los tags se leen, pero el tramo tarda: ahí un AGV pasa mucho tiempo sin dar señal. ") +
            "Una parada en esta zona se ve tarde.",
          ["zona-oscura", first],
        ),
      );
    }
    if (measured.explainedSlow.length > 0) {
      out.append(
        element(
          "p",
          "muted",
          "No cuentan como zona oscura, porque su espera la explica una parada precisa o un semáforo: " +
            measured.explainedSlow.map((entry) => `${entry.tagId} (${criticalFunctionLabel(entry.function)})`).join(", ") +
            ".",
        ),
      );
    }

    // Las paradas sin explicación, una a una: el ejemplo del propietario. Las de un punto conflictivo
    // ya tienen su tarjeta; aquí van las sueltas.
    const inPoints = new Set(measured.conflictPoints.flatMap((point) => point.tags));
    const unexplained = measured.unexplained.produccion.filter((stop) => !inPoints.has(stop.fromTagId));
    for (const stop of unexplained.slice(0, PER_KIND)) {
      const ahead = stop.aheadEvidence;
      out.append(
        finding(
          `${stop.agvId}: ${duration(stop.excessMs)} de más en ${stop.fromTagId}`,
          `De ${formatTick(stop.fromUtcMs)} a ${formatTick(stop.toUtcMs)}, hasta ${stop.toTagId}; lo normal en ese tramo, ` +
            `${duration(stop.usualMs)}`,
          (ahead === null
            ? "Nadie delante en medio circuito. "
            : `Nadie delante que lo retuviera: ${ahead.agvId} iba ${
                ahead.distanceAtStart === 0 ? "en su mismo tag, por delante" : ahead.distanceAtStart === 1 ? "1 tag por delante" : `${ahead.distanceAtStart} tags por delante`
              }` +
              (ahead.tagsAdvanced === null ? " y siguió. " : ` y avanzó ${ahead.tagsAdvanced} mientras tanto. `)) +
            "La producción seguía. Qué lo paró no lo dice el dato.",
          ["parada-sin-explicacion", `${stop.agvId} ${stop.fromTagId} ${stop.fromUtcMs}`],
        ),
      );
      const stopCard = out.lastElementChild;
      if (stopCard instanceof HTMLElement) withBattery(stopCard, `${stop.agvId} ${stop.fromTagId} ${stop.fromUtcMs}`);
    }
    const allUnexplained = [...measured.unexplained.produccion, ...measured.unexplained.noche];
    if (allUnexplained.length > PER_KIND) {
      out.append(
        lazyTable(countedLabel("Ver", allUnexplained.length, true, "parada sin explicación", "paradas sin explicación"), () =>
          plainTable(
            ["AGV", "Tramo", "Desde", "Hasta", "Lo normal", "De más", "Régimen"],
            allUnexplained.map((stop) => [
              stop.agvId,
              `${stop.fromTagId} → ${stop.toTagId}`,
              formatTick(stop.fromUtcMs),
              formatTick(stop.toUtcMs),
              duration(stop.usualMs),
              duration(stop.excessMs),
              stop.regime === "noche" ? "noche" : "producción",
            ]),
          ),
        ),
      );
    }

    // Lecturas que llegaron juntas al servidor (R-DAT-020): primero dónde se concentran, lo demás plegado.
    const grouped = cohort.groupedDelivery;
    const deliveryText = (delivery: (typeof grouped.deliveries)[number]): string =>
      `de ${delivery.fromTagId} a ${delivery.tags[delivery.tags.length - 1] ?? "—"} (${formatTick(delivery.fromUtcMs)}): ` +
      `tras ${duration(delivery.gapMs)} sin nada, ${delivery.tags.length} lecturas en ${duration(delivery.spreadMs)}; ` +
      `el recorrido entero tardó ${duration(delivery.totalMs)}, lo normal ${duration(delivery.usualMs)}: ` +
      (delivery.kind === "sin-parada" ? "no paró" : "hubo una espera en algún punto de ese tramo, sin poder situarla");
    if (!grouped.evaluated) {
      out.append(
        element(
          "p",
          "muted",
          `Lecturas que llegaron juntas al servidor: sin evaluar (${grouped.reason ?? "—"}). Sin ellas no hay nada ` +
            "con que dibujar su mapa de calor.",
        ),
      );
    } else if (grouped.total === 0) {
      out.append(
        element(
          "p",
          "muted",
          "Ninguna lectura llegó junta con otras tras un hueco: no hay nada con que dibujar su mapa de calor. Eso no " +
            "dice nada de la cobertura (R-OPP-003): el estado de conexión observado está en el registro de conexiones.",
        ),
      );
    } else {
      out.append(
        element(
          "p",
          "muted",
          `La hora del fichero es la de llegada al servidor. ${grouped.total} ${grouped.total === 1 ? "vez" : "veces"} un AGV ` +
            "pasó un rato sin dar señal y después llegaron varias lecturas casi a la vez: se hicieron antes y llegaron " +
            "juntas. Para medir tiempos se toma el recorrido entero, del tag de antes del hueco al último que llegó; el " +
            "hueco no cuenta como parada.",
        ),
      );
      // El mapa de calor de las lecturas que llegaron juntas (inferido): solo con evidencia (hay
      // ráfagas), en el orden del anillo, y con los sitios que el Worker ya destacó. Dónde, no por qué.
      out.append(deliveryHeatChart(grouped.heat, shape?.tags ?? [], new Set(grouped.sites.map((site) => site.id)), grouped.total, views.sections));
      for (const vehicle of grouped.vehicles.slice(0, PER_KIND)) {
        const latest = [...grouped.deliveries].reverse().find((delivery) => delivery.agvId === vehicle.id);
        out.append(
          finding(
            `${vehicle.id}: le llegan lecturas juntas`,
            `${vehicle.count} veces, cuando el azar daría ${chance(vehicle.expected)} con los tramos que recorre`,
            (latest === undefined ? "" : `La última, ${deliveryText(latest)}. `) +
              "Apunta a la comunicación de ese AGV; la causa no la dice el dato.",
            ["entrega-agrupada-agv", vehicle.id],
          ),
        );
      }
      for (const site of grouped.sites.slice(0, PER_KIND)) {
        const vehicles = [...new Set(grouped.deliveries.filter((delivery) => delivery.fromTagId === site.id).map((delivery) => delivery.agvId))];
        out.append(
          finding(
            `Lecturas juntas al pasar por ${site.id}`,
            `${site.count} veces, de ${vehicles.length} AGV (${few(vehicles)}), cuando el azar daría ${chance(site.expected)} ` +
              "con las pasadas de ese sitio",
            "Apunta a la comunicación en ese punto del circuito; la causa no la dice el dato.",
            ["entrega-agrupada-sitio", site.id],
          ),
        );
      }
      out.append(
        lazyTable(
          `Ver las ${grouped.total} veces que llegaron lecturas juntas` +
            (grouped.deliveries.length < grouped.total ? ` (las ${grouped.deliveries.length} más recientes)` : ""),
          () =>
            plainTable(
              ["AGV", "Desde", "Llegaron juntas", "Hueco", "En", "Recorrido", "Lo normal", "Lectura"],
              grouped.deliveries.map((delivery) => [
                delivery.agvId,
                `${delivery.fromTagId} (${formatTick(delivery.fromUtcMs)})`,
                delivery.tags.join(" → "),
                duration(delivery.gapMs),
                duration(delivery.spreadMs),
                duration(delivery.totalMs),
                duration(delivery.usualMs),
                delivery.kind === "sin-parada" ? "no paró" : "espera sin situar",
              ]),
            ),
        ),
      );
    }

    // El estado de conexión observado (DS-013, ADR-0017): el registro de conexiones del terminal,
    // situado con las lecturas. Es del circuito entero; va con el primer cohorte, con su anillo.
    if (cohort === circuitState.cohorts[0]) renderConnectionsPointer(out, views);

    // La noche, medida aparte.
    const nightDiffers = measured.night.filter(
      (entry) => Math.abs(Math.log(entry.nocheP50Ms / Math.max(1, entry.produccionP50Ms))) >= Math.log(1.5),
    );
    out.append(
      element(
        "p",
        "muted",
        `De noche (${nightLabel}): ` +
          (nightDiffers.length === 0
            ? "ningún tramo cambia la mitad o más respecto a producción. "
            : `${nightDiffers.length} tramos cambian la mitad o más respecto a producción (` +
              nightDiffers
                .slice(0, 4)
                .map((entry) => `${entry.from} → ${entry.to}: de ${duration(entry.produccionP50Ms)} a ${duration(entry.nocheP50Ms)}`)
                .join("; ") +
              `${nightDiffers.length > 4 ? "…" : ""}). `) +
          `${measured.unexplained.noche.length} paradas sin explicación, medidas contra la horquilla de noche.`,
      ),
    );

    // La horquilla de cada tramo del anillo, dibujada, y en CSV para guardarla.
    if (shape !== undefined) {
      const marksOf = new Map<string, string[]>();
      const mark = (tagId: string, label: string): void => {
        marksOf.set(tagId, [...(marksOf.get(tagId) ?? []), label]);
      };
      for (const entry of measured.bottlenecks) mark(entry.tagId, "cuello de botella");
      for (const entry of measured.conflictPoints) for (const tagId of entry.tags) mark(tagId, "punto conflictivo");
      for (const entry of measured.darkZones) for (const tagId of entry.tags.slice(0, -1)) mark(tagId, "zona oscura");
      const ringRows: BandRow[] = shape.tags.map((tagId, index) => {
        const next = shape.tags[(index + 1) % shape.tags.length] as string;
        const pair = cohort.bands.find((entry) => entry.from === tagId && entry.to === next);
        return {
          from: tagId,
          to: next,
          produccion: pair?.produccion ?? null,
          noche: pair?.noche ?? null,
          marks: marksOf.get(tagId) ?? [],
          section: views.sections?.[tagId] ?? null,
        };
      });
      out.append(segmentBandChart(ringRows, nightLabel));
    }
    const download = element("button", undefined, "Descargar horquillas (CSV)");
    download.setAttribute("type", "button");
    download.addEventListener("click", () => {
      // Con BOM y `;`: lo abre una hoja de cálculo en español sin preguntar ni romper tildes.
      const csv = `\ufeff${bandsCsv(cohort.bands)}`;
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `horquillas-${state.circuitId ?? "circuito"}-${cohort.cohortId}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    });
    out.append(
      download,
      element(
        "p",
        "muted",
        `Resolución de la fuente: ${cohort.resolutionMs >= 60_000 ? "un minuto" : "un segundo"}; la valla nunca queda a ` +
          `menos de ${duration(cohort.marginMs)} del 95 %. Guardar una horquilla como referencia y compararla con las ` +
          "siguientes es la memoria del circuito (F4): hoy se compara el primer periodo cargado con el último.",
      ),
    );

    // El cambio de la horquilla entre el primer y el último periodo (R-TIM-010).
    if (cohort.changes !== null) {
      const { earlyPeriod, latePeriod, changes } = cohort.changes;
      const periods = `${formatTick(earlyPeriod.from)}–${formatTick(earlyPeriod.to)} frente a ${formatTick(latePeriod.from)}–${formatTick(latePeriod.to)}`;
      if (changes.length === 0) {
        out.append(element("p", "muted", `Entre ${periods}, ningún tramo se sale de su horquilla.`));
      }
      for (const change of changes.slice(0, PER_KIND)) {
        out.append(
          finding(
            `${change.from} → ${change.to}, ${change.kind === "mas-lento" ? "más lento" : "más rápido"}` +
              (change.regime === "noche" ? " de noche" : ""),
            `La mitad de las pasadas tardaba ${duration(change.early.p50Ms)} y ahora ${duration(change.late.p50Ms)}`,
            `Comparado dentro del mismo régimen, ${periods}. ` +
              (change.kind === "mas-lento"
                ? "La mitad de las pasadas de ahora tarda más que el 80 % de las de antes."
                : "El 80 % de las pasadas de ahora tarda menos que la mitad de las de antes."),
            ["cambio-de-horquilla", `${change.from} ${change.to} ${change.regime}`],
          ),
        );
      }
    }
  }
}

/**
 * Ritmo de cada AGV y quién retiene a otros (R-AGV-019, R-AGV-020): hechos con su cifra, sin causa.
 * Es la parte por AGV del estado normal del circuito; lo que es por sitio —cuellos, puntos
 * conflictivos, zonas oscuras, lecturas juntas al pasar por un tag— se queda en Tiempos.
 */
function renderPace(views: CircuitViews): void {
  const { circuitState } = views;
  if (circuitState.cohorts.length === 0 || circuitState.cohorts.every((cohort) => cohort.pace.vehicles.length === 0)) return;
  const few = (ids: readonly string[]): string => (ids.length > 4 ? `${ids.slice(0, 4).join(", ")}…` : ids.join(", "));
  const chance = (expected: number): string =>
    expected < 0.1 ? "menos de 0,1" : expected.toFixed(1).replace(".", ",");
  out.append(element("h3", undefined, "Ritmo de cada AGV y quién retiene"));
  out.append(
    element(
      "p",
      "muted",
      "Contra la horquilla de cada tramo, en producción y sin contar paradas ni esperas detrás de otro. " +
        "Quien retiene no tiene por qué pararse: basta con que tarde más donde otros vienen detrás.",
    ),
  );
  for (const cohort of circuitState.cohorts) {
    if (circuitState.cohorts.length > 1) out.append(element("p", "muted", `Circuito ${cohort.cohortId}:`));
    // Ritmo de cada AGV y quién retiene a otros (R-AGV-019, R-AGV-020): hechos con su cifra, sin causa.
    const pace = cohort.pace;
    const whereText = (where: (typeof pace.vehicles)[number]["where"]): string =>
      where === null || where === "toda-la-linea" ? "en toda la línea" : `solo en la zona ${where.map((zone) => `«${zone}»`).join(" y ")}`;
    const paced = pace.vehicles.filter((vehicle) => vehicle.verdict !== null);
    for (const vehicle of paced.slice(0, PER_KIND)) {
      const shift = Math.abs(vehicle.ratio / vehicle.fleetRatio - 1);
      out.append(
        finding(
          `${vehicle.agvId} va un ${Math.round(shift * 100)} % más ${vehicle.verdict === "mas-lento" ? "lento" : "rápido"} que la flota, ${whereText(vehicle.where)}`,
          `La mitad de sus tramos tarda ${vehicle.ratio.toFixed(2).replace(".", ",")} veces lo habitual del tramo; la de la flota, ` +
            `${vehicle.fleetRatio.toFixed(2).replace(".", ",")} (${vehicle.samples} tramos)`,
          "Contra la horquilla de cada tramo, en producción y sin contar paradas ni esperas detrás de otro. Con tantos " +
            "tramos una diferencia pequeña ya sale por encima del azar: solo se enseña desde un 5 %. La causa no la dice " +
            "el dato.",
          ["ritmo-agv", vehicle.agvId],
        ),
      );
    }
    const holding = pace.holders.filter((holder) => holder.expected !== null);
    for (const holder of holding.slice(0, PER_KIND)) {
      out.append(
        finding(
          `${holder.agvId} retiene a otros AGV`,
          `${holder.retentions} veces, a ${holder.retained.length} AGV distintos (${few(holder.retained)}), cuando el azar daría ` +
            `${chance(holder.expected as number)} con sus pasadas`,
          `Iba delante, más despacio de lo habitual en ese tramo, y los de detrás esperaron ${duration(holder.waitMs)} en total, ` +
            `en ${few(holder.sites)}. Quien retiene no tiene por qué pararse: basta con que tarde más donde otros vienen ` +
            "detrás. La causa no la dice el dato.",
          ["retiene-agv", holder.agvId],
        ),
      );
    }
    if (pace.vehicles.length > 0) {
      if (paced.length === 0 && holding.length === 0) {
        out.append(
          element("p", "muted", "Ningún AGV va un 5 % o más lento o más rápido que la flota, y nadie retiene a otros más de lo que da el azar."),
        );
      }
      const holderOf = new Map(pace.holders.map((holder) => [holder.agvId, holder]));
      out.append(
        lazyTable(`Ver el ritmo de los ${pace.vehicles.length} AGV`, () =>
          plainTable(
            ["AGV", "Tramos", "Ritmo frente a la flota", "Lectura", "Retenciones", "Esperas detrás"],
            pace.vehicles.map((vehicle) => {
              const holder = holderOf.get(vehicle.agvId);
              return [
                vehicle.agvId,
                String(vehicle.samples),
                percent(vehicle.ratio / vehicle.fleetRatio),
                vehicle.verdict === null ? "a su paso" : `${vehicle.verdict === "mas-lento" ? "más lento" : "más rápido"}, ${whereText(vehicle.where)}`,
                String(holder?.retentions ?? 0),
                holder === undefined ? "—" : duration(holder.waitMs),
              ];
            }),
          ),
        ),
      );
    }

  }
}

/**
 * Tiempos por sección entre anclas (R-TIM-012): todas las anclas declaradas que están en el anillo
 * del cohorte principal cortan secciones consecutivas —kitting, cruce, línea…— y cada una lleva su
 * horquilla por régimen y su p50 por fichero. Es la medida que el propietario pidió (OQ-141); no
 * diagnostica nada por sí sola.
 */
function renderAnchorSections(views: CircuitViews): void {
  const { anchorSections } = views;
  out.append(element("h3", undefined, "Tiempos por sección entre anclas"));
  if (anchorSections.onRing.length < 2) {
    out.append(
      element(
        "p",
        "muted",
        anchorSections.declared < 2
          ? "Declara dos o más anclas en la lista `ancla` para medir por secciones: cada ancla del anillo corta una " +
              "sección (kitting, cruce, línea…) y se mide el tiempo entre una y la siguiente."
          : `De las ${anchorSections.declared} anclas declaradas, ${anchorSections.onRing.length === 0 ? "ninguna está" : "solo una está"} ` +
              "en el anillo reconstruido: hacen falta dos o más en el anillo para medir por secciones.",
      ),
    );
    return;
  }
  const { sections } = anchorSections;
  const named = sections.filter((section) => section.namedByList).length;
  out.append(
    element(
      "p",
      "muted",
      `${sections.length} secciones entre las anclas ${anchorSections.onRing.join(", ")}, en el orden del anillo. Un paso es el tiempo ` +
        "de un AGV desde su lectura de un ancla hasta la siguiente, sin otra ancla ni una calle de carga en medio y sin cruzar un " +
        "hueco de cobertura ni una parada de la producción; las relecturas seguidas del ancla cuentan una vez. La horquilla sigue el " +
        `criterio de los tramos y la valla nunca queda a menos de ${duration(anchorSections.marginMs)} del 95 %.` +
        (named > 0 ? ` ${named} de las secciones toman el nombre de la lista \`tramo\`.` : ""),
    ),
  );
  const rows: string[][] = [];
  const regimeLabel = { produccion: "producción", noche: "noche" } as const;
  // En la tabla, solo cuántos tags tiene la sección y de cuál a cuál: la lista entera (decenas de
  // identificadores por fila) partía las cabeceras letra a letra y en el móvil destrozaba la tabla.
  // La lista completa va en el detalle plegado de abajo.
  const tagSpan = (tags: readonly string[]): string =>
    tags.length === 0 ? "—" : tags.length === 1 ? `1 tag, ${tags[0]}` : `${tags.length} tags, de ${tags[0]} a ${tags[tags.length - 1]}`;
  for (const section of sections) {
    const bands = [
      ["produccion", section.produccion],
      ["noche", section.noche],
    ] as const;
    const withBand = bands.filter(([, band]) => band !== null);
    if (withBand.length === 0) {
      rows.push([section.name, tagSpan(section.tags), "—", "sin muestras suficientes", "—", "—", "—", "—"]);
      continue;
    }
    for (const [regime, band] of withBand) {
      if (band === null) continue;
      rows.push([
        section.name,
        tagSpan(section.tags),
        regimeLabel[regime],
        String(band.samples),
        duration(band.p50Ms),
        duration(band.p80Ms),
        duration(band.p95Ms),
        duration(band.fenceMs),
      ]);
    }
  }
  // En su caja desplazable, como las otras tablas anchas: las cabeceras cortas no se parten.
  out.append(scrollBox(plainTable(["Sección", "Tags", "Régimen", "Muestras", "p50", "p80", "p95", "Valla"], rows)));

  // La tendencia: el p50 de producción de cada sección en cada fichero (R-TIM-011). Delante, la
  // lista completa de tags de cada sección, que la tabla de arriba ya no lleva.
  const fileNameOf = new Map(views.franjas.sources.map((source) => [source.sourceId, source.fileName]));
  const sourceIds = [...new Set(sections.flatMap((section) => section.bySource.map((entry) => entry.sourceId)))];
  const sectionTags = (): HTMLElement => {
    const list = element("dl", "facts");
    for (const section of sections) {
      list.append(element("dt", undefined, section.name), element("dd", "mono", section.tags.join(" ")));
    }
    return list;
  };
  if (sourceIds.length > 0) {
    out.append(
      lazyTable(`Ver el p50 de cada sección por fichero (${plural(sourceIds.length, "fichero", "ficheros")}) y los tags de cada sección`, () => {
        const body = element("div");
        body.append(
          element("p", "muted", "Tags de cada sección, en el orden del anillo:"),
          sectionTags(),
          plainTable(
            ["Fichero", "Sección", "Muestras", "p50 (producción)"],
            sourceIds.flatMap((sourceId) =>
              sections.map((section) => {
                const entry = section.bySource.find((item) => item.sourceId === sourceId);
                return [
                  fileNameOf.get(sourceId) ?? sourceId,
                  section.name,
                  String(entry?.samples ?? 0),
                  entry === undefined || entry.p50Ms === null ? "—" : duration(entry.p50Ms),
                ];
              }),
            ),
          ),
        );
        return body;
      }),
    );
  } else {
    out.append(lazyTable("Ver los tags de cada sección", sectionTags));
  }
  const download = element("button", undefined, "Descargar secciones (CSV)");
  download.setAttribute("type", "button");
  download.addEventListener("click", () => {
    // Con BOM y `;`, como las horquillas: se abre en una hoja de cálculo en español tal cual.
    const url = URL.createObjectURL(new Blob([`\ufeff${anchorSectionsCsv(sections)}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `secciones-${state.circuitId ?? "circuito"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  });
  out.append(download);
}

type Matrix = CircuitViews["readMatrices"][number];
type TagRow = Matrix["tags"][number];

/** Cuántos casos se enseñan de entrada. Es un parámetro de pantalla, no una magnitud de planta. */
const HIGHLIGHTS = CARDS_SHOWN.highlights;
/** Tarjetas de AGV por cada tipo de diferencia de lectura. Parámetro de pantalla. */
const PER_KIND = CARDS_SHOWN.perKind;

/** Una probabilidad pequeña, legible: «1 vez de cada 1.000» o «menos de 1 de cada millón». */
function formatChance(chance: number): string {
  if (chance >= 0.1) return `${Math.round(chance * 100)} de cada 100 veces`;
  if (chance < 1e-6) return "menos de 1 vez de cada millón";
  return `1 vez de cada ${Math.round(1 / chance).toLocaleString("es-ES")}`;
}

function percent(rate: number | null): string {
  return rate === null ? "—" : `${Math.round(rate * 100)} %`;
}

/** Cuántos paneles de tendencia, horquillas y filas de permanencia se dibujan. Parámetros de pantalla. */
const TREND_PANELS = 9;
const FORK_DIAGRAMS = 4;
const DWELL_ROWS = 5;
/** Marcas numeradas alrededor del anillo, como mucho: con más, los números se pisan. */
const RING_MARKS = 20;

/**
 * Lo que el anillo radial necesita de un cohorte: su forma, la omisión de cada tag según la matriz,
 * y las marcas — primero los tags con función declarada (dato de planta), después los candidatos por
 * firma, en orden del anillo.
 */
/**
 * El estado de conexión observado (DS-013, ADR-0017), en Tiempos: lo que trae el registro, el mapa
 * de estado de conexión, dónde y a quién le pasa más de lo que da el azar, el contraste con las
 * ráfagas (OQ-159) y las tablas. Sin registro cargado, una línea que lo dice.
 */
/**
 * En Tiempos, donde vivía el estado de conexión hasta 3.68.0: sin registro, por qué no hay nada que
 * enseñar; con registro, el enlace a la pestaña WiFi, que es suya desde entonces (UX_SPEC §2).
 */
function renderConnectionsPointer(out: HTMLElement, views: CircuitViews): void {
  out.append(element("h3", undefined, "Estado de conexión (registro del terminal)"));
  if (views.connections === undefined) {
    out.append(
      element(
        "p",
        "muted",
        "Sin registro de conexiones cargado: el mapa de estado de conexión (WiFi) necesita la exportación del terminal de " +
          "cada AGV, un fichero por vehículo, en «Listas del circuito» (Datos). Hasta entonces no hay evidencia directa de la señal.",
      ),
    );
    return;
  }
  const elsewhere = element("p", "findings-elsewhere");
  const link = element("a", undefined, "El estado de conexión está en WiFi");
  link.href = "#wifi";
  link.addEventListener("click", (event) => {
    event.preventDefault();
    jumpTo("wifi", "Estado de conexión (registro del terminal)");
  });
  elsewhere.append(link, ": cortes por sitio y por AGV, terminales ruidosos, los que no reconectan y el contraste con las ráfagas.");
  out.append(elsewhere);
}

/** La pestaña WiFi: el estado de conexión del circuito entero, con el anillo del cohorte mayor. */
function renderWifi(out: HTMLElement, views: CircuitViews): void {
  const main = views.circuitState.cohorts[0];
  const shape = main === undefined ? undefined : views.shapes.find((entry) => entry.cohortId === main.cohortId);
  const few = (ids: readonly string[]): string => (ids.length > 4 ? `${ids.slice(0, 4).join(", ")}…` : ids.join(", "));
  const chance = (expected: number): string =>
    expected < 0.1 ? "menos de 0,1" : expected.toFixed(1).replace(".", ",");
  renderConnections(out, views, shape?.tags ?? [], few, chance);
}

function renderConnections(
  out: HTMLElement,
  views: CircuitViews,
  ringTags: readonly string[],
  few: (ids: readonly string[]) => string,
  chance: (expected: number) => string,
): void {
  const connections = views.connections;
  out.append(element("h3", undefined, "Estado de conexión (registro del terminal)"));
  if (connections === undefined) return;
  const { summary, thresholds } = connections;
  const minutes = (ms: number): string => (ms >= 3_600_000 ? `${(ms / 3_600_000).toFixed(1).replace(".", ",")} h` : ms >= 60_000 ? `${Math.round(ms / 60_000)} min` : `${Math.round(ms / 1000)} s`);
  const classLabels = {
    microcorte: `de hasta ${minutes(thresholds.microcutMaxMs)}`,
    corte: `de hasta ${minutes(thresholds.cutMaxMs)}`,
    caida: `de más de ${minutes(thresholds.cutMaxMs)}`,
  };
  const n = plural;
  // Lo que no entra en el mapa, sin solapes: los apagados (colectivos o no), los colectivos que no
  // son apagados, los propios de terminales ruidosos y los propios sin lecturas con que situarlos.
  const collectiveOthers = summary.collectives.reduce((sum, entry) => sum + entry.byClass.microcorte + entry.byClass.corte + entry.byClass.caida, 0);
  const unlocated = summary.vehicles.reduce((sum, own) => sum + own.unlocated, 0);
  const excludedParts = [
    ...(summary.byClass.apagado > 0 ? [n(summary.byClass.apagado, "apagado", "apagados")] : []),
    ...(collectiveOthers > 0 ? [n(collectiveOthers, "colectivo", "colectivos")] : []),
    ...(summary.excludedNoisy > 0 ? [`${summary.excludedNoisy} de ${n(summary.noisyVehicles.length, "terminal ruidoso", "terminales ruidosos")} (${summary.noisyVehicles.join(", ")})`] : []),
    ...(unlocated > 0 ? [`${unlocated} sin lecturas con que situarlos`] : []),
  ];
  const excluded = excludedParts.join(", ");
  // Una línea corta: qué hay y qué no entra en el mapa. El porqué de cada exclusión está en las
  // tarjetas y en la leyenda del mapa, no aquí: la sección ya es larga.
  out.append(
    element(
      "p",
      "muted",
      `Registro de conexiones de ${n(connections.vehicles, "AGV", "AGV")} (${n(connections.files, "fichero", "ficheros")}): ` +
        `${n(summary.total, "corte", "cortes")} en la ventana cargada (${n(summary.byClass.microcorte, "microcorte", "microcortes")} ${classLabels.microcorte}, ` +
        `${n(summary.byClass.corte, "corte", "cortes")} ${classLabels.corte}, ${n(summary.byClass.caida, "caída", "caídas")} ${classLabels.caida}, ` +
        `${n(summary.byClass.apagado, "apagado", "apagados")}), ${minutes(summary.withoutSignalMs)} sin señal en total` +
        (connections.outsideWindow > 0 ? `; ${n(connections.outsideWindow, "corte", "cortes")} del registro fuera de la ventana, sin contar` : "") +
        ". Cada corte se sitúa entre la última lectura anterior y la primera posterior del mismo AGV" +
        (excluded === "" ? "." : `; no entran en el mapa: ${excluded}.`),
    ),
  );
  if (summary.total === 0) {
    out.append(element("p", "muted", "El registro no trae ningún corte dentro de la ventana cargada: nada que situar."));
    return;
  }
  out.append(
    connectionHeatChart(
      summary.heat,
      ringTags,
      new Set(summary.sites.map((site) => site.id)),
      { cuts: summary.total, byClass: summary.byClass, excluded, withoutSignalMs: summary.withoutSignalMs, vehicles: connections.vehicles, files: connections.files },
      classLabels,
      views.sections,
    ),
  );
  // Las horas: un patrón horario es un apagado o un turno, no un sitio (propietario, 2026-10-07).
  out.append(
    hourHistogramChart(
      summary.byHour,
      "Cortes por hora del día",
      "Solo los cortes que miden cobertura (sin apagados ni colectivos), por la hora local en que se perdió la señal. Un pico a una " +
        "misma hora todos los días apunta a un apagado o a un turno, no a un sitio; los apagados y los colectivos van aparte, abajo.",
      "viz-senal",
    ),
  );
  if (summary.collectives.length > 0) {
    const biggest = Math.max(...summary.collectives.map((entry) => entry.vehicles.length));
    out.append(
      finding(
        `Cortes colectivos: ${summary.collectives.length} ${summary.collectives.length === 1 ? "vez" : "veces"}`,
        `hasta ${biggest} AGV a la vez, de ${connections.vehicles} con registro` +
          (summary.collectives.some((entry) => entry.byClass.apagado > 0) ? "; alguno cerrado por un encendido: apagado" : ""),
        "Varios AGV pierden la señal a la vez: apagado o infraestructura común (R-COM-003), no la cobertura de un sitio. No entran en el mapa.",
        ["conexion-colectivo", String(summary.collectives[0]?.fromUtcMs ?? 0)],
      ),
    );
    out.append(
      lazyTable(countedLabel("Ver", summary.collectives.length, false, "corte colectivo", "cortes colectivos"), () =>
        plainTable(
          ["Desde", "Hasta", "AGV", "Microcortes", "Cortes medios", "Caídas", "Apagados"],
          summary.collectives.map((entry) => [
            formatTick(entry.fromUtcMs),
            formatTick(entry.toUtcMs),
            `${entry.vehicles.length}: ${entry.vehicles.join(", ")}`,
            String(entry.byClass.microcorte),
            String(entry.byClass.corte),
            String(entry.byClass.caida),
            String(entry.byClass.apagado),
          ]),
        ),
      ),
    );
  }
  // El contraste con las ráfagas (OQ-159): si la hora del fichero fuera la de recepción por este
  // enlace, ninguna lectura tendría fecha dentro de una caída y cada ráfaga coincidiría con un corte.
  const contrast = summary.contrast;
  out.append(
    element(
      "p",
      "muted",
      `Contraste con las lecturas que llegaron juntas (OQ-159): ${contrast.bursts} ${contrast.bursts === 1 ? "ráfaga" : "ráfagas"}, ` +
        `${contrast.burstsWithCut} con un corte del registro a menos de ${minutes(thresholds.contrastToleranceMs)}; ${contrast.cuts} cortes, ` +
        `${contrast.cutsWithBurst} con ráfaga. Lecturas con fecha dentro de una caída: ${contrast.readingsInsideFalls.toLocaleString("es-ES")} ` +
        `en ${contrast.falls} ${contrast.falls === 1 ? "caída" : "caídas"}` +
        (contrast.readingsInsideFalls > 0 && contrast.falls > 0
          ? ". Con la hora de recepción por este enlace serían cero: la hora del fichero o el enlace de las lecturas es otro. La pregunta sigue abierta."
          : "."),
    ),
  );
  for (const site of summary.sites.slice(0, PER_KIND)) {
    const cell = summary.heat.find((entry) => entry.tagId === site.id);
    out.append(
      finding(
        `La señal se pierde al salir de ${site.id}`,
        `${site.count} veces de ${cell?.passes ?? 0} pasadas, de ${cell?.vehicles.length ?? 0} AGV (${few(cell?.vehicles ?? [])}), cuando el azar daría ${chance(site.expected)}`,
        "Apunta a la cobertura en ese punto del circuito; la causa no la dice el dato.",
        ["conexion-sitio", site.id],
      ),
    );
  }
  // Los terminales ruidosos (R-COM-009): un hallazgo de ese terminal, apartado del mapa.
  for (const agvId of summary.noisyVehicles.slice(0, PER_KIND)) {
    const own = summary.vehicles.find((entry) => entry.agvId === agvId);
    out.append(
      finding(
        `${agvId}: terminal ruidoso, apartado del mapa`,
        `${own?.rate.toFixed(1).replace(".", ",") ?? "—"} cortes propios por mil pasadas, ${own?.rateRatio.toFixed(1).replace(".", ",") ?? "—"} veces la mediana de la flota ` +
          `(${summary.fleetMedianRate.toFixed(1).replace(".", ",")})` +
          (own === undefined ? "" : ` · ${own.byClass.microcorte} microcortes, ${own.byClass.corte} cortes, ${own.byClass.caida} caídas` + (own.perDay.length > 1 ? ` · por día: ${own.perDay.map((entry) => entry.cuts).join(" → ")}` : "")),
        "Conecta y desconecta sin parar: sus cortes y sus pasadas no entran en el mapa ni en la concentración por sitio, para que no " +
          "oculten la evidencia del resto. Apunta a su terminal; la causa no la dice el dato.",
        ["conexion-ruidoso", agvId],
      ),
    );
  }
  // Los que no reconectan (R-COM-010).
  for (const entry of summary.notReconnecting.slice(0, PER_KIND)) {
    out.append(
      finding(
        `${entry.agvId}: no reconectó`,
        `Desde ${formatTick(entry.fromUtcMs)}, ${entry.end === "abierto" ? `al menos ${minutes(entry.durationMs)} sin volver en la ventana` : `${minutes(entry.durationMs)} hasta que se apagó y encendió`}` +
          (entry.lastTagId === null ? "" : ` · última lectura con señal en ${entry.lastTagId}`),
        entry.end === "abierto"
          ? "Perdió la señal y no consta ninguna conexión después dentro de la ventana cargada. Apunta al terminal o a una expulsión; la causa no la dice el dato."
          : "Solo volvió al apagar y encender el AGV, fuera de un apagado colectivo: un terminal que no reconecta solo. La causa no la dice el dato.",
        ["conexion-no-reconecta", `${entry.agvId} ${entry.fromUtcMs}`],
      ),
    );
  }
  if (summary.notReconnecting.length > PER_KIND) {
    out.append(
      lazyTable(countedLabel("Ver", summary.notReconnecting.length, false, "que no reconectó", "que no reconectaron"), () =>
        plainTable(
          ["AGV", "Desde", "Hasta", "Duración", "Cierre", "Última lectura"],
          summary.notReconnecting.map((entry) => [
            entry.agvId,
            formatTick(entry.fromUtcMs),
            entry.toUtcMs === null ? "no vuelve en la ventana" : formatTick(entry.toUtcMs),
            `${entry.end === "abierto" ? "al menos " : ""}${minutes(entry.durationMs)}`,
            entry.end === "abierto" ? "abierto" : "tras apagado (encendido)",
            entry.lastTagId ?? "—",
          ]),
        ),
      ),
    );
  }
  for (const vehicle of summary.concentratedVehicles.filter((entry) => !summary.noisyVehicles.includes(entry.id)).slice(0, PER_KIND)) {
    const own = summary.vehicles.find((entry) => entry.agvId === vehicle.id);
    out.append(
      finding(
        `${vehicle.id}: pierde la señal más que el resto`,
        `${vehicle.count} cortes propios con los tramos que recorre, cuando el azar daría ${chance(vehicle.expected)}` +
          (own === undefined
            ? ""
            : ` · ${own.byClass.microcorte} microcortes, ${own.byClass.corte} cortes, ${own.byClass.caida} caídas; ${minutes(own.withoutSignalMs)} sin señal` +
              (own.perDay.length > 1 ? ` · por día: ${own.perDay.map((entry) => entry.cuts).join(" → ")}` : "")),
        "Apunta al terminal de ese AGV, no al sitio; la causa no la dice el dato.",
        ["conexion-agv", vehicle.id],
      ),
    );
  }
  out.append(
    lazyTable(`Ver el registro por AGV (${summary.vehicles.length})`, () =>
      plainTable(
        ["AGV", "Eventos", "Cortes", "Microcortes", "Cortes medios", "Caídas", "Apagados", "Colectivos", "Por mil pasadas", "Frente a la flota", "Sin señal", "Sin situar", "Propios por día", "Terminal"],
        summary.vehicles.map((own) => [
          own.noisy ? `${own.agvId} (ruidoso, fuera del mapa)` : own.agvId,
          String(own.events),
          String(own.cuts),
          String(own.byClass.microcorte),
          String(own.byClass.corte),
          String(own.byClass.caida),
          String(own.byClass.apagado),
          String(own.collective),
          own.rate.toFixed(1).replace(".", ","),
          `${own.rateRatio.toFixed(1).replace(".", ",")}×`,
          minutes(own.withoutSignalMs),
          String(own.unlocated),
          own.perDay.map((entry) => `${entry.day.slice(5)}: ${entry.cuts}`).join(" · "),
          own.ips.join(", "),
        ]),
      ),
    ),
  );
  out.append(
    lazyTable(
      `Ver los ${summary.total} cortes` + (connections.cuts.length < summary.total ? ` (los ${connections.cuts.length} más recientes)` : ""),
      () =>
        plainTable(
          ["AGV", "Desde", "Hasta", "Duración", "Clase", "Cierre", "Última lectura", "Siguiente lectura", "Situado", "Lecturas dentro"],
          connections.cuts.map((cut) => [
            cut.agvId,
            formatTick(cut.fromUtcMs),
            cut.toUtcMs === null ? "no vuelve en la ventana" : formatTick(cut.toUtcMs),
            `${cut.end === "abierto" ? "al menos " : ""}${minutes(cut.durationMs)}`,
            (cut.cutClass === "caida" ? "caída" : cut.cutClass) + (cut.collective ? " (colectivo)" : ""),
            cut.end === "conexion" ? "conexión" : cut.end === "tras-apagado" ? "tras apagado (encendido)" : "abierto",
            cut.lastTagId === null ? "—" : `${cut.lastTagId} (${cut.lastReadUtcMs === null ? "" : formatTick(cut.lastReadUtcMs)})`,
            cut.nextTagId === null ? "—" : `${cut.nextTagId} (${cut.nextReadUtcMs === null ? "" : formatTick(cut.nextReadUtcMs)})`,
            cut.location === "tramo" ? "en el tramo" : cut.location === "entre" ? "entre las dos, sin situar más" : "sin situar",
            String(cut.readingsInside),
          ]),
        ),
    ),
  );
}

function ringDataOf(shape: CircuitViews["shapes"][number], matrix: Matrix | undefined, views: CircuitViews): RingData {
  const rowOf = new Map((matrix?.tags ?? []).map((row) => [row.tagId, row]));
  const declared = new Map(
    views.tagDossiers
      .filter((dossier) => dossier.criticalFunction !== null)
      .map((dossier) => [dossier.tagId, dossier.criticalFunction as string]),
  );
  const candidates = views.criticalPoints.find((cohort) => cohort.cohortId === shape.cohortId)?.candidates ?? [];
  const declaredMarks: RingMark[] = [];
  const candidateMarks: RingMark[] = [];
  for (const tagId of shape.tags) {
    const fn = declared.get(tagId);
    if (fn !== undefined) {
      declaredMarks.push({ tagId, label: criticalFunctionLabel(fn), declared: true });
      continue;
    }
    const candidate = candidates.find((entry) => entry.tagId === tagId);
    if (candidate !== undefined) candidateMarks.push({ tagId, label: criticalFunctionLabel(candidate.kind), declared: false });
  }
  const order = new Map(shape.tags.map((tagId, index) => [tagId, index]));
  const marks = [...declaredMarks, ...candidateMarks]
    .slice(0, RING_MARKS)
    .sort((a, b) => (order.get(a.tagId) ?? 0) - (order.get(b.tagId) ?? 0));
  // La capa «Paradas»: lo que el estado normal (R-TIM-009) mide en cada tag, ya calculado. Las paradas
  // de un punto conflictivo son paradas sin explicación, así que se cuentan una vez, por su tag.
  const measured = views.circuitState.cohorts.find((cohort) => cohort.cohortId === shape.cohortId)?.state;
  const stopsAt = new Map<string, number>();
  for (const stop of measured?.unexplained.produccion ?? []) stopsAt.set(stop.fromTagId, (stopsAt.get(stop.fromTagId) ?? 0) + 1);
  const bottleneckAt = new Map((measured?.bottlenecks ?? []).map((entry) => [entry.tagId, entry.episodes]));
  const conflictTags = new Set((measured?.conflictPoints ?? []).flatMap((entry) => entry.tags));
  const darkTags = new Set((measured?.darkZones ?? []).flatMap((entry) => entry.tags.slice(0, -1)));
  // La capa «Señal»: el mapa de calor de comunicación (R-DAT-020), ya calculado por el Worker. Sin
  // evaluar, ningún tag lleva `signal`, y la capa lo dice.
  const grouped = views.circuitState.cohorts.find((cohort) => cohort.cohortId === shape.cohortId)?.groupedDelivery;
  const heatOf = new Map((grouped?.evaluated ? grouped.heat : []).map((cell) => [cell.tagId, cell]));
  const flaggedSites = new Set((grouped?.sites ?? []).map((site) => site.id));
  // La capa «Conexión»: el registro de conexiones del terminal (DS-013), observado, ya resumido.
  const connections = views.connections;
  const connectionOf = new Map((connections?.summary.heat ?? []).map((cell) => [cell.tagId, cell]));
  const connectionSites = new Set((connections?.summary.sites ?? []).map((site) => site.id));
  return {
    tags: shape.tags.map((tagId, index) => {
      const row = rowOf.get(tagId);
      return {
        tagId,
        omission: row === undefined || row.rate === null ? null : 1 - row.rate,
        passes: row?.passes ?? 0,
        pattern: row?.pattern ?? "sin datos",
        zone: shape.zones?.[index] ?? null,
        isAnchor: tagId === shape.anchorTagId,
        section: views.sections?.[tagId] ?? null,
        ...(measured === undefined
          ? {}
          : {
              incidents: {
                stops: stopsAt.get(tagId) ?? 0,
                bottleneckEpisodes: bottleneckAt.get(tagId) ?? 0,
                conflict: conflictTags.has(tagId),
                dark: darkTags.has(tagId),
              },
            }),
        ...(grouped?.evaluated
          ? {
              signal: {
                passes: heatOf.get(tagId)?.passes ?? 0,
                late: heatOf.get(tagId)?.late ?? 0,
                lostAfter: heatOf.get(tagId)?.lostAfter ?? 0,
                vehicles: heatOf.get(tagId)?.vehicles.length ?? 0,
                flagged: flaggedSites.has(tagId),
              },
            }
          : {}),
        ...(connections === undefined
          ? {}
          : {
              connection: {
                passes: connectionOf.get(tagId)?.passes ?? 0,
                cuts: connectionOf.get(tagId)?.cuts ?? 0,
                falls: connectionOf.get(tagId)?.byClass.caida ?? 0,
                vehicles: connectionOf.get(tagId)?.vehicles.length ?? 0,
                flagged: connectionSites.has(tagId),
              },
            }),
      };
    }),
    anchorTagId: shape.anchorTagId,
    anchorDeclared: shape.anchorTruth === "observed",
    marks,
    junctions: shape.laneJunctions ?? [],
  };
}

/** Tags que la matriz ya destaca: los mismos que «Lo que hay que mirar», más los que cambian. */
function flaggedTagsOf(matrix: Matrix): ReadonlySet<string> {
  return new Set(
    matrix.tags
      .filter(
        (tag) =>
          tag.pattern === "bimodal-candidato" ||
          tag.pattern === "uniforme-bajo" ||
          tag.changedAtUtcMs !== undefined ||
          tag.trend === "bajando",
      )
      .map((tag) => tag.tagId),
  );
}

/** Vehículos que la matriz ya destaca: los que no leen un tag bimodal, o cuyo lector cambia. */
function flaggedVehiclesOf(matrix: Matrix): ReadonlySet<string> {
  const flagged = new Set<string>();
  for (const tag of matrix.tags) if (tag.pattern === "bimodal-candidato") for (const agvId of tag.lowReaders) flagged.add(agvId);
  for (const vehicle of matrix.vehicles) if (vehicle.changedAtUtcMs !== undefined || vehicle.trend === "bajando") flagged.add(vehicle.agvId);
  return flagged;
}

/** Un panel de tendencia con su caída, para ordenar los más marcados primero. */
function trendPanel(
  who: "Tag" | "AGV",
  id: string,
  row: {
    readonly changedAtUtcMs?: number;
    readonly rateBefore?: number;
    readonly rateAfter?: number;
    readonly segmentRates?: readonly number[];
    readonly trendSeries?: TrendPanel["series"];
  },
): { readonly panel: TrendPanel; readonly drop: number } | null {
  if (row.trendSeries === undefined) return null;
  const rates = row.segmentRates ?? [];
  const drop =
    row.changedAtUtcMs !== undefined
      ? (row.rateBefore ?? 0) - (row.rateAfter ?? 0)
      : (rates[0] ?? 0) - (rates[rates.length - 1] ?? 0);
  return {
    drop,
    panel: {
      who,
      id,
      kind: row.changedAtUtcMs !== undefined ? "rotura" : "degradación",
      series: row.trendSeries,
      ...(row.changedAtUtcMs === undefined ? {} : { changedAtUtcMs: row.changedAtUtcMs }),
      ...(row.segmentRates === undefined ? {} : { segmentRates: row.segmentRates }),
    },
  };
}

type VehicleReadingView = CircuitViews["vehicleReading"][number];

/** La matriz de lectura de un cohorte, si la tiene. */
function matrixOf(views: CircuitViews, cohortId: number): Matrix | undefined {
  return views.readMatrices.find((entry) => entry.cohortId === cohortId);
}

/**
 * El anillo dibujado, en el Resumen (UX_SPEC §2, §4.2): la forma del circuito con sus capas. Tocar un
 * tag abre su expediente por la misma vía que escribirlo en el buscador de la barra.
 */
function renderRingFigure(views: CircuitViews): void {
  ringToday = null;
  ringHandle = null;
  for (const shape of views.shapes) {
    const matrix = matrixOf(views, shape.cohortId);
    const data = ringDataOf(shape, matrix, views);
    const handle = ringFigure(data, { onTagOpen: openDossier });
    // El control de tiempo recorre el anillo del cohorte principal: el primero, que es el de las instantáneas.
    if (ringHandle === null) {
      ringHandle = handle;
      ringToday = data;
    }
    out.append(handle.node);
  }
}

/** El anillo de la portada y sus datos de hoy, para que el control de tiempo vuelva a ellos. */
let ringHandle: RingFigure | null = null;
let ringToday: RingData | null = null;
/** Donde la portada y Tiempos escriben la evolución cuando llegan las instantáneas. */
let evolutionHost: HTMLElement | null = null;
let timesEvolutionHost: HTMLElement | null = null;
/** Una importación nueva invalida la carga de instantáneas de la anterior. */
let evolutionToken = 0;

/**
 * Lee las instantáneas del almacén y monta la evolución (ADR-0015 §3): el control de tiempo sobre el
 * anillo, la sección «Evolución» debajo y el gráfico de tiempos en Tiempos. Va después de las vistas
 * porque las instantáneas no viajan en ellas —`views.snapshots` lleva la lista y los deltas, que
 * pesan poco; el grafo entero se lee del almacén, que ya lo tiene cuando el Worker responde.
 */
function loadEvolution(views: CircuitViews): void {
  const token = ++evolutionToken;
  state.snapshots = [];
  const circuitId = state.circuitId;
  const mount = (snapshots: readonly CircuitSnapshot[]): void => {
    if (token !== evolutionToken) return;
    state.snapshots = snapshots;
    renderEvolution(views, snapshots);
  };
  if (circuitId === null || !isAvailable()) {
    mount([]);
    return;
  }
  void loadSnapshots(circuitId).then(mount, () => mount([]));
}

/** La fecha de una instantánea es la ventana de su fichero, no cuándo se calculó: dos cargadas hoy seguidas distan meses de datos. */
const windowOf = (window: { readonly from: number; readonly to: number }): string => `${formatTick(window.from)} → ${formatTick(window.to)}`;

function renderEvolution(views: CircuitViews, snapshots: readonly CircuitSnapshot[]): void {
  const host = evolutionHost;
  const handle = ringHandle;
  const today = ringToday;
  const last = snapshots.length - 1;
  const select = (index: number): void => {
    if (handle === null || today === null) return;
    const snapshot = snapshots[index];
    const around = snapshot === undefined ? null : deltaAround(views.snapshots.deltas, snapshot.sourceId);
    const changed = around === null ? new Map<string, string>() : changedTagsOf(around.delta);
    const latest = snapshots[last];
    // La última instantánea es la de hoy: se vuelve al anillo de las vistas, que lleva la zona, el
    // patrón y las incidencias medidas que la instantánea no guarda.
    if (index === last || snapshot === undefined) {
      handle.update(today, { changed, note: null });
    } else {
      handle.update(ringDataFromSnapshot(snapshot), {
        changed,
        note: `Instantánea de ${snapshot.fileName}, ${windowOf(snapshot.window)}; hoy: ${latest?.fileName ?? "—"}. La zona y las paradas sin explicación no viajan en la instantánea.`,
      });
    }
    if (host !== null) {
      host.replaceChildren(evolutionSection({ snapshots, deltas: views.snapshots.deltas, selected: index, formatWindow: windowOf }));
    }
  };
  if (handle !== null && snapshots.length > 1) {
    const control = timeControl(
      snapshots.map((snapshot) => ({ label: snapshot.fileName, date: windowOf(snapshot.window) })),
      last,
      select,
    );
    handle.node.querySelector(".ring-layers")?.before(control);
  }
  select(last);

  const times = timesEvolutionHost;
  if (times !== null) {
    const data = evolutionDataOf(snapshots, windowOf);
    times.replaceChildren(
      data === null
        ? element(
            "p",
            "muted",
            snapshots.length === 0
              ? "El circuito a lo largo de los ficheros: sin instantáneas guardadas todavía; se dibuja con la segunda."
              : "El circuito a lo largo de los ficheros: con una sola instantánea no hay línea que dibujar; se dibuja con la segunda.",
          )
        : evolutionChart(data),
    );
  }
}

/** Abre el expediente de un identificador: rellena el buscador de la barra y activa AGV, como al escribir. */
function openDossier(id: string): void {
  dossierInput.value = id;
  dossierInput.dispatchEvent(new Event("input", { bubbles: true }));
}

/**
 * El anillo del circuito (pestaña Tiempos): su forma en palabras, la lista ordenada y los tags fuera
 * de él. La lista ordenada es la que se contrasta con el circuito virtual y con la memoria; el dibujo
 * vive en el Resumen desde 3.49.0, y aquí queda el enlace.
 */
function renderRing(views: CircuitViews): void {
  if (views.shapes.length === 0 && (views.lapAnchorProblems ?? []).length === 0) return;
  out.append(element("h3", undefined, "El anillo del circuito"));
  for (const shape of views.shapes) {
    const matrix = matrixOf(views, shape.cohortId);
    const anchorPhrase =
      shape.anchorTruth === "observed"
        ? `vuelta cortada en «${shape.anchorTagId}» (declarado)`
        : `vuelta cortada en «${shape.anchorTagId}» (deducido del recorrido)`;
    out.append(
      element(
        "p",
        "muted",
        `Anillo de ${shape.tags.length} tags, ${anchorPhrase}. El orden es el que siguen los AGV, ` +
          `con un ${Math.round(shape.weakestShare * 100)} % de coincidencia en el tramo más débil.`,
      ),
    );
    const elsewhere = element("p", "findings-elsewhere");
    const link = element("a", undefined, "El anillo está en Resumen");
    link.href = "#resumen";
    link.addEventListener("click", (event) => {
      event.preventDefault();
      jumpTo("resumen", "Anillo del circuito");
    });
    elsewhere.append(link, ", con sus capas: omisión, tramos, paradas y calles.");
    out.append(elsewhere);

    out.append(
      lazyTable(`${countedLabel("Ver", shape.tags.length, false, "tag del anillo", "tags del anillo")}, en orden`, () =>
        plainTable(
          ["Posición", "Tag", ...(shape.zones === undefined ? [] : ["Zona"]), "Leído por pasada", "Lectura"],
          shape.tags.map((tagId, index) => {
            const row = matrix?.tags.find((entry) => entry.tagId === tagId);
            return [
              String(index + 1),
              tagId,
              ...(shape.zones === undefined ? [] : [zoneLabel(shape.zones[index] ?? "—")]),
              row?.isAnchor === true ? "— (corte de vuelta)" : percent(row?.rate ?? null),
              row?.isAnchor === true ? "corte de vuelta" : row?.pattern === undefined ? "sin datos" : patternLabel(row.pattern),
            ];
          }),
        ),
      ),
    );

    if (shape.offRingTags.length > 0) {
      out.append(
        element(
          "p",
          "muted",
          (shape.offRingTags.length === 1
            ? "1 tag se lee y no está en el anillo. "
            : `${shape.offRingTags.length} tags se leen y no están en el anillo. `) +
            "Pueden ser ramas que solo recorren algunos AGV o tags de la línea que se leen muy poco; " +
            "estos últimos merecen revisarse.",
        ),
      );
      out.append(
        lazyTable(
          shape.offRingTags.length === 1
            ? "Ver el tag que queda fuera del anillo"
            : `Ver los ${shape.offRingTags.length} tags fuera del anillo`,
          () =>
          plainTable(
            ["Tag", "Vehículos que lo leen"],
            shape.offRingTags.map((tag) => [tag.tagId, String(tag.readers)]),
          ),
        ),
      );
    }
  }
  for (const problem of views.lapAnchorProblems ?? []) {
    out.append(finding("Corte de vuelta declarado que no se pudo usar", "—", problem));
  }
}

/**
 * La lectura de cada tag (pestaña Tags): a cuáles mirar, el mapa de omisión y la matriz completa.
 *
 * El orden importa tanto como el contenido: primero **a cuáles mirar**, y solo después el conjunto
 * entero, plegado. Enseñar de golpe ciento cincuenta tags por cincuenta vehículos no es informar,
 * es esconder el hallazgo dentro de una cuadrícula (`UX_SPEC.md` §5.1).
 */
function renderReadMatrix(views: CircuitViews): void {
  for (const shape of views.shapes) {
    const matrix = matrixOf(views, shape.cohortId);
    out.append(element("h3", undefined, "Lo que hay que mirar"));
    if (matrix === undefined || !matrix.supported) {
      out.append(
        element(
          "p",
          "muted",
          "Todavía no hay vueltas completas: hace falta que los AGV pasen más de una vez por el corte " +
            "de vuelta para medir la lectura.",
        ),
      );
      continue;
    }
    // Cuánto puede apoyarse la prueba por tiempo: si pocos segmentos tienen mediana medida, un
    // tramo largo sin leer no se puede contrastar y quedará sin sostener con más frecuencia.
    out.append(
      element(
        "p",
        "muted",
        `${matrix.segmentsWithTime} de ${matrix.ring.length} tramos del anillo tienen tiempo de ` +
          "recorrido medido.",
      ),
    );
    renderHighlights(matrix, views.vehicleReading.find((entry) => entry.cohortId === matrix.cohortId));
    out.append(readMatrixHeatmap(matrix, flaggedTagsOf(matrix), flaggedVehiclesOf(matrix)));
    renderFullMatrix(matrix);
  }
}

/** El conjunto de AGV que no leen nunca un tag, como texto ordenado: es lo que agrupa tarjetas gemelas. */
function neverSetOf(ids: readonly string[]): string {
  return [...ids].sort().join(" ");
}

/** Un tag destacado o un grupo de tags gemelos: los que comparten el mismo conjunto de AGV que no los leen nunca. */
interface TagHighlight {
  readonly tags: readonly TagRow[];
  readonly readers: readonly VehicleReadingView["tags"][number][];
  /** Los AGV que no leen ninguno de estos tags, ordenados por pasadas (los que más pasan, primero). */
  readonly never: readonly string[];
}

/** Los AGV cuya tarjeta «no lee nunca» quedó plegada en un grupo de tags gemelos, en la última matriz dibujada. */
const foldedVehicles = new Map<string, string>();

/**
 * Agrupa los tags destacados que comparten exactamente el mismo conjunto de AGV que no los leen nunca
 * (UX_SPEC §4.3): «60180 y 60183: 5 AGV no los leen nunca» es un hecho, no siete tarjetas.
 */
function groupHighlights(notable: readonly TagRow[], readersOf: ReadonlyMap<string, VehicleReadingView["tags"][number]>): TagHighlight[] {
  const groups = new Map<string, { tags: TagRow[]; readers: VehicleReadingView["tags"][number][] }>();
  const singles: TagHighlight[] = [];
  const order: TagHighlight[] = [];
  for (const tag of notable) {
    const readers = readersOf.get(tag.tagId);
    const never = readers === undefined ? [] : readers.never.map((entry) => entry.agvId);
    if (tag.pattern !== "bimodal-candidato" || readers === undefined || never.length === 0) {
      const single: TagHighlight = { tags: [tag], readers: readers === undefined ? [] : [readers], never: [] };
      singles.push(single);
      order.push(single);
      continue;
    }
    const key = neverSetOf(never);
    const group = groups.get(key);
    if (group === undefined) {
      const created = { tags: [tag], readers: [readers] };
      groups.set(key, created);
      // Se coloca en el orden del primer tag del grupo; el objeto se completa después.
      order.push({ tags: created.tags, readers: created.readers, never });
    } else {
      group.tags.push(tag);
      group.readers.push(readers);
    }
  }
  return order.map((item) => {
    const first = item.readers[0];
    if (item.tags.length < 2 || first === undefined) return { ...item, never: [] };
    const passesOf = new Map(first.never.map((entry) => [entry.agvId, entry.passes]));
    return { ...item, never: [...item.never].sort((a, b) => (passesOf.get(b) ?? 0) - (passesOf.get(a) ?? 0)) };
  });
}

/** Lo que hay que mirar, visible de entrada: los tags y los vehículos que se salen de lo normal. */
function renderHighlights(matrix: Matrix, reading: VehicleReadingView | undefined): void {
  const notable = matrix.tags
    .filter((tag) => tag.pattern === "bimodal-candidato" || tag.pattern === "uniforme-bajo")
    .sort((a, b) => (a.rate ?? 1) - (b.rate ?? 1));
  const readersOf = new Map((reading?.tags ?? []).map((entry) => [entry.tagId, entry]));

  out.append(
    element(
      "p",
      "muted",
      "Porcentaje sobre las veces que el AGV pasó por el punto. No es una tasa de salud: para eso " +
        "haría falta saber qué lleva cada AGV en memoria y qué tags siguen instalados.",
    ),
  );

  foldedVehicles.clear();
  if (notable.length === 0) {
    out.append(element("p", "muted", "Ningún tag destacable: todos se leen al pasar."));
  } else {
    const items = groupHighlights(notable, readersOf);
    for (const item of items.slice(0, HIGHLIGHTS)) {
      const [tag] = item.tags;
      if (tag === undefined) continue;
      if (item.tags.length === 1) {
        out.append(
          finding(
            `Tag ${tag.tagId} · posición ${tag.position + 1} de ${matrix.ring.length}`,
            `${percent(tag.rate)} de las pasadas · ${patternLabel(tag.pattern)}`,
            explain(tag, readersOf.get(tag.tagId)),
            ["tag-lectura", tag.tagId],
          ),
        );
        continue;
      }
      out.append(groupedTagCard(item, matrix));
    }
    if (items.length > HIGHLIGHTS) {
      out.append(element("p", "muted", `Y ${items.length - HIGHLIGHTS} más en la matriz completa.`));
    }
  }
}

/** Una tarjeta para un grupo de tags gemelos, con los AGV y sus cifras dentro y la tabla al tocar. */
function groupedTagCard(item: TagHighlight, matrix: Matrix): HTMLElement {
  const ids = item.tags.map((tag) => tag.tagId);
  const positions = item.tags.map((tag) => tag.position + 1).sort((a, b) => a - b);
  const first = positions[0] ?? 0;
  const last = positions[positions.length - 1] ?? 0;
  const consecutive = last - first === positions.length - 1;
  const where = consecutive ? `posiciones ${first}–${last}` : `posiciones ${positions.join(", ")}`;
  const passesOf = (agvId: string): string =>
    item.readers.map((readers) => `0 de ${readers.never.find((entry) => entry.agvId === agvId)?.passes ?? 0}`).join(" y ");
  const listed = item.never.slice(0, 4).map((agvId) => `${agvId}: ${passesOf(agvId)}`);
  const rest = item.never.length - listed.length;
  const goods = item.readers.map((readers) => readers.good);
  const sameGood = goods.every((value) => value === goods[0]);
  const card = finding(
    `${joinIds(ids)} (${where} de ${matrix.ring.length}): ${item.never.length} AGV no los leen nunca`,
    `${item.tags.map((tag) => percent(tag.rate)).join(" y ")} de las pasadas · ${patternLabel("bimodal-candidato")}`,
    `${listed.join("; ")}${rest > 0 ? `; y ${rest} más` : ""}. ` +
      (sameGood ? `${goods[0] ?? 0} AGV los leen bien.` : `Los leen bien ${goods.join(" y ")} AGV.`) +
      " Contado sobre las veces que cada AGV pasó por cada punto.",
    ["tag-lectura", ...[...ids].sort()],
  );
  // La tarjeta de cada AGV «no lee nunca N tags» de estos mismos AGV se pliega aquí (no sale aparte).
  const key = neverSetOf(ids);
  for (const agvId of item.never) foldedVehicles.set(agvId, key);
  card.append(
    lazyTable(`${countedLabel("Ver", item.never.length, false, "AGV", "AGV")} con sus pasadas`, () =>
      plainTable(
        ["AGV", ...ids],
        item.never.map((agvId) => [
          agvId,
          ...item.readers.map((readers) => `0 de ${readers.never.find((entry) => entry.agvId === agvId)?.passes ?? 0}`),
        ]),
      ),
    ),
  );
  return card;
}

/** «60180 y 60183», «7100, 7108 y 7116». */
function joinIds(ids: readonly string[]): string {
  if (ids.length <= 1) return ids.join("");
  return `${ids.slice(0, -1).join(", ")} y ${ids[ids.length - 1] ?? ""}`;
}

/** Lista corta de tags con su cifra: los cuatro primeros y cuántos más. */
function tagList(items: readonly string[]): string {
  return items.length > 4 ? `${items.slice(0, 4).join("; ")}; y ${items.length - 4} más` : items.join("; ");
}

const tagWord = (count: number): string => (count === 1 ? "tag" : "tags");

/**
 * La lectura de cada AGV (pestaña AGV): quién no lee lo que el resto sí, y cuya lectura cae o baja.
 * Es la parte por AGV de «Lo que hay que mirar»; se enseña con su propio título.
 */
function renderVehicleReadingSection(views: CircuitViews): void {
  for (const shape of views.shapes) {
    const matrix = matrixOf(views, shape.cohortId);
    if (matrix === undefined || !matrix.supported) continue;
    out.append(element("h3", undefined, "Lectura de cada AGV"));
    out.append(
      element(
        "p",
        "muted",
        "Sobre los tags que el resto de la flota lee bien: quién no los lee nunca, quién dejó de leerlos y quién " +
          "los lee poco. Porcentaje sobre las pasadas de cada AGV por cada tag; tampoco es una tasa de salud.",
      ),
    );
    renderVehicleReading(matrix, views.vehicleReading.find((entry) => entry.cohortId === matrix.cohortId));
    renderVehicleTrends(matrix);
  }
}

/**
 * La lectura de cada AGV sobre los tags que el resto lee bien (R-AGV-016): nunca, desde una hora,
 * poco en muchos tags, poco en pocos. La diferencia medida, sin causa.
 */
function renderVehicleReading(matrix: Matrix, reading: VehicleReadingView | undefined): void {
  const vehicles = reading?.vehicles ?? [];
  if (vehicles.length === 0) {
    out.append(element("p", "muted", "Ningún AGV se aparta de lo que lee el resto de la flota."));
    return;
  }
  const unprovenOf = new Map(matrix.vehicles.map((vehicle) => [vehicle.agvId, vehicle.unproven]));
  const extra = (agvId: string): string => {
    const unproven = unprovenOf.get(agvId) ?? 0;
    return unproven > 0 ? ` Además, ${plural(unproven, "tramo recorrido", "tramos recorridos")} en menos tiempo de lo normal.` : "";
  };

  // Por tipo, para que ninguno quede escondido detrás de los demás: nunca, dejó de leer, poco en
  // muchos tags y poco en pocos.
  const groups: Record<"nunca" | "desde" | "muchos" | "pocos", HTMLElement[]> = { nunca: [], desde: [], muchos: [], pocos: [] };
  // «No lee nunca»: los AGV que no leen exactamente los mismos tags son una sola tarjeta con el conjunto
  // como sujeto; y si esos tags ya tienen su tarjeta de grupo en «Lo que hay que mirar», ninguna.
  const neverGroups = new Map<string, { agvIds: string[]; tags: VehicleReadingView["vehicles"][number]["never"][] }>();
  let folded = 0;
  for (const vehicle of vehicles) {
    if (vehicle.never.length === 0) continue;
    const key = neverSetOf(vehicle.never.map((entry) => entry.tagId));
    if (foldedVehicles.get(vehicle.agvId) === key) {
      folded += 1;
      continue;
    }
    const group = neverGroups.get(key);
    if (group === undefined) neverGroups.set(key, { agvIds: [vehicle.agvId], tags: [vehicle.never] });
    else {
      group.agvIds.push(vehicle.agvId);
      group.tags.push(vehicle.never);
    }
  }
  for (const group of neverGroups.values()) {
    const [firstNever] = group.tags;
    if (firstNever === undefined) continue;
    const tagIds = firstNever.map((entry) => entry.tagId);
    if (group.agvIds.length === 1) {
      const agvId = group.agvIds[0] ?? "";
      groups.nunca.push(
        finding(
          `AGV ${agvId} · no lee nunca ${tagIds.length} ${tagWord(tagIds.length)} que el resto sí lee`,
          tagList(firstNever.map((entry) => `${entry.tagId}: 0 de ${entry.passes} pasadas`)),
          `Contado sobre las veces que pasó por cada punto.${extra(agvId)}`,
          ["agv-nunca", agvId],
        ),
      );
      continue;
    }
    const passesOf = (agvIndex: number): string => (group.tags[agvIndex] ?? []).map((entry) => `0 de ${entry.passes}`).join(" y ");
    const listed = group.agvIds.slice(0, 4).map((agvId, index) => `${agvId}: ${passesOf(index)}`);
    const rest = group.agvIds.length - listed.length;
    const card = finding(
      `${joinIds(group.agvIds)} · no leen nunca ${tagIds.length} ${tagWord(tagIds.length)} que el resto sí lee`,
      `${joinIds(tagIds)} · ${listed.join("; ")}${rest > 0 ? `; y ${rest} más` : ""}`,
      "Contado sobre las veces que cada uno pasó por cada punto.",
      ["agv-nunca", ...[...group.agvIds].sort()],
    );
    card.append(
      lazyTable(`${countedLabel("Ver", group.agvIds.length, false, "AGV", "AGV")} con sus pasadas`, () =>
        plainTable(
          ["AGV", ...tagIds],
          group.agvIds.map((agvId, index) => [agvId, ...(group.tags[index] ?? []).map((entry) => `0 de ${entry.passes}`)]),
        ),
      ),
    );
    groups.nunca.push(card);
  }
  for (const vehicle of vehicles) {
    if (vehicle.stopped.length > 0) {
      groups.desde.push(
        finding(
          `AGV ${vehicle.agvId} · dejó de leer ${vehicle.stopped.length} ${tagWord(vehicle.stopped.length)} que el resto sigue leyendo`,
          tagList(
            vehicle.stopped.map(
              (entry) => `${entry.tagId}: desde ${formatInstant(entry.sinceUtcMs)}, 0 de ${entry.passesSince} pasadas`,
            ),
          ),
          "Antes los leía con normalidad.",
          ["agv-desde", vehicle.agvId],
        ),
      );
    }
    if (vehicle.weak.length > 0) {
      const weak = [...vehicle.weak].sort((a, b) => a.hits / a.passes - b.hits / b.passes);
      const rates = weak.map((entry) => entry.hits / entry.passes).sort((a, b) => a - b);
      const median = rates[Math.floor(rates.length / 2)] ?? null;
      groups[vehicle.extent === "muchos" ? "muchos" : "pocos"].push(
        finding(
          vehicle.extent === "muchos"
            ? `AGV ${vehicle.agvId} · lee poco en ${weak.length} de ${vehicle.consideredTags} tags ` +
                `(${percent(weak.length / vehicle.consideredTags)})`
            : `AGV ${vehicle.agvId} · lee poco en ${weak.length} ${tagWord(weak.length)}`,
          (vehicle.extent === "muchos" ? `Mediana, ${percent(median)} de las pasadas. ` : "") +
            tagList(weak.map((entry) => `${entry.tagId}: ${percent(entry.hits / entry.passes)} (${entry.hits} de ${entry.passes})`)),
          `El resto de la flota lee esos tags con normalidad.${extra(vehicle.agvId)}`,
          ["agv-poco", vehicle.agvId],
        ),
      );
    }
  }
  if (folded > 0) {
    out.append(
      element(
        "p",
        "muted",
        `${folded} ${folded === 1 ? "AGV no lee nunca los mismos tags que ya tienen" : "AGV no leen nunca los mismos tags que ya tienen"} ` +
          "su tarjeta en «Lo que hay que mirar» (Tags): están dentro de ella.",
      ),
    );
  }
  let hidden = 0;
  for (const cards of Object.values(groups)) {
    for (const card of cards.slice(0, PER_KIND)) out.append(card);
    hidden += Math.max(0, cards.length - PER_KIND);
  }
  if (hidden > 0) out.append(element("p", "muted", `Y ${hidden} más en la tabla.`));
  out.append(
    lazyTable(`Ver la lectura de los ${vehicles.length} AGV que se apartan del resto`, () =>
      plainTable(
        ["AGV", "Nunca", "Dejó de leer", "Lee poco", "Tags comparados"],
        vehicles.map((vehicle) => [
          vehicle.agvId,
          String(vehicle.never.length),
          String(vehicle.stopped.length),
          String(vehicle.weak.length),
          String(vehicle.consideredTags),
        ]),
      ),
    ),
  );
}

/** Lectura de un AGV que cae de golpe o baja a lo largo del periodo, en todos sus tags (R-OPP-015). */
function renderVehicleTrends(matrix: Matrix): void {
  const broken = matrix.vehicles.filter((vehicle) => vehicle.changedAtUtcMs !== undefined);
  const declining = matrix.vehicles.filter((vehicle) => vehicle.trend === "bajando");
  if (broken.length === 0 && declining.length === 0) return;

  const panels = [...broken, ...declining]
    .map((row) => trendPanel("AGV", row.agvId, row))
    .filter((panel): panel is { readonly panel: TrendPanel; readonly drop: number } => panel !== null)
    .sort((a, b) => b.drop - a.drop);
  if (panels.length > 0) {
    out.append(
      trendMultiplesChart(panels.slice(0, TREND_PANELS).map((entry) => entry.panel), FORMATS, "Rotura y degradación de cada AGV, en el tiempo"),
    );
  }
  for (const vehicle of broken) {
    out.append(
      finding(
        `AGV ${vehicle.agvId}: su lectura cae de golpe`,
        `${percent(vehicle.rateBefore ?? null)} → ${percent(vehicle.rateAfter ?? null)} desde ` +
          formatInstant(vehicle.changedAtUtcMs as number),
        "En todos los tags a la vez.",
        ["agv-rotura", vehicle.agvId],
      ),
    );
  }
  for (const vehicle of declining) {
    out.append(
      finding(
        `AGV ${vehicle.agvId}: su lectura baja a lo largo del periodo`,
        (vehicle.segmentRates ?? []).map((rate) => percent(rate)).join(" → "),
        "En todos los tags, mientras el resto de la flota los lee bien.",
        ["agv-degradacion", vehicle.agvId],
      ),
    );
  }
}

/**
 * Cambios de tag (R-DAT-019, R-OPP-015): lo que más se va a ver en planta.
 *
 * Dentro del periodo cargado: un tag que deja de leerse y otro que empieza en su sitio, los que solo
 * dejan de leerse o solo empiezan, y las caídas de lectura de la matriz. Frente a cada tag nuevo, la
 * diferencia de cada AGV que no lo lee como el resto. Hechos con su hora y sus cifras; la causa la
 * pone una persona (R-EVI-006).
 */
function renderTagChanges(views: CircuitViews): void {
  const { adoption } = views.tagChanges;
  const tagsOfChange = (change: CircuitViews["tagChanges"]["changes"][number]): readonly string[] =>
    change.kind === "cambio" ? [change.oldTagId, change.newTagId] : [change.tagId];
  const carded = new Set(views.tagChanges.changes.flatMap(tagsOfChange));
  const pairs = new Set(
    views.tagChanges.changes.flatMap((change) => (change.kind === "cambio" ? [`${change.oldTagId}>${change.newTagId}`] : [])),
  );

  // Cambios de estructura dentro del fichero, por la suma entre anclas (R-DAT-021). Un tramo con tags
  // que no tienen tarjeta —un bloque cambiado en mantenimiento, cuyos vecinos también cambiaron— o con
  // una sustitución que el sitio no emparejó sale como una sola tarjeta y quita las de sus tags. Si no,
  // la suma se añade como una línea a la tarjeta que ya existe.
  const within = views.franjas.cohorts.flatMap((cohort) =>
    cohort.structure
      .filter((set) => set.source === "dentro-del-fichero")
      .flatMap((set) => set.gaps.map((gap) => ({ gap, atUtcMs: set.atUtcMs }))),
  );
  const absorbs = (gap: AnchorGapChange): boolean =>
    gap.changes.some((change) =>
      change.kind === "sustituido" ? !pairs.has(`${change.oldTagId}>${change.newTagId}`) : !carded.has(change.tagId),
    );
  const absorbed = within.filter((entry) => absorbs(entry.gap));
  const absorbedTags = new Set(absorbed.flatMap((entry) => changedTags(entry.gap)));
  const lineFor = (tagId: string): string => {
    const entry = within.find((candidate) => !absorbs(candidate.gap) && changedTags(candidate.gap).includes(tagId));
    return entry === undefined ? "" : gapLineFor(tagId, entry.gap, duration);
  };
  const changes = views.tagChanges.changes.filter((change) => !tagsOfChange(change).every((tagId) => absorbedTags.has(tagId)));

  const explained = new Set<string>([...carded, ...within.flatMap((entry) => changedTags(entry.gap))]);
  const trendTags = views.readMatrices.flatMap((matrix) =>
    matrix.tags.filter(
      (tag) => !explained.has(tag.tagId) && (tag.changedAtUtcMs !== undefined || tag.trend === "bajando"),
    ),
  );
  if (changes.length === 0 && absorbed.length === 0 && trendTags.length === 0) return;

  out.append(element("h3", undefined, "Cambios de tag"));
  out.append(
    element(
      "p",
      "muted",
      "Dentro del periodo cargado: tags que dejan de leerse, que empiezan a leerse o cuya lectura cae, " +
        "con la hora y lo que pasó después.",
    ),
  );

  const factText = (fact: CircuitViews["tagChanges"]["adoption"][number]["fact"]): string => {
    switch (fact.kind) {
      case "nunca":
        return `nunca (0 de ${fact.passes} pasadas)`;
      case "desde":
        return `desde ${formatInstant(fact.sinceUtcMs)}, 0 de ${fact.passesSince} pasadas`;
      case "tarde":
        return `empezó a leerlo ${formatInstant(fact.startedUtcMs)}, tras ${fact.passesBefore} pasadas sin leerlo`;
      case "poco":
        return `${percent(fact.hits / fact.passes)} (${fact.hits} de ${fact.passes})`;
    }
  };
  const adoptionLine = (tagId: string): string => {
    const issues = adoption.filter((issue) => issue.tagId === tagId);
    return issues.length === 0
      ? ""
      : ` AGV que no lo leen como el resto: ${tagList(issues.map((issue) => `${issue.agvId}, ${factText(issue.fact)}`))}.`;
  };

  const cards: HTMLElement[] = absorbed.map(({ gap, atUtcMs }) => {
    const text = describeGap(gap, duration);
    return finding(`${text.title}, ${formatInstant(atUtcMs)}`, text.figure, text.evidence, [
      "estructura",
      gap.fromAnchor,
      gap.toAnchor,
    ]);
  });
  for (const change of changes) {
    if (change.kind === "cambio") {
      cards.push(
        finding(
          `${change.oldTagId} → ${change.newTagId}`,
          `${change.oldTagId} dejó de leerse ${formatInstant(change.oldLastUtcMs)}; ${change.newTagId} empezó ` +
            formatInstant(change.newFirstUtcMs),
          `En el mismo sitio (mismo ${change.neighborSide}, ${change.sharedNeighbor}). Después, la flota pasó ` +
            `${change.passesAfterOld} veces sin leer ${change.oldTagId}.${adoptionLine(change.newTagId)}${lineFor(change.newTagId)}`,
          ["cambio-tag", change.oldTagId, change.newTagId],
        ),
      );
    } else if (change.kind === "deja") {
      cards.push(
        finding(
          `Tag ${change.tagId}: dejó de leerse`,
          `última lectura ${formatInstant(change.lastUtcMs)}`,
          `Después, la flota pasó ${change.passesAfter} veces por su sitio sin leerlo.${lineFor(change.tagId)}`,
          ["tag-deja", change.tagId],
        ),
      );
    } else {
      cards.push(
        finding(
          `Tag ${change.tagId}: empezó a leerse`,
          `primera lectura ${formatInstant(change.firstUtcMs)}`,
          `Antes, la flota pasó ${change.passesBefore} veces por su sitio sin leerlo.${adoptionLine(change.tagId)}${lineFor(change.tagId)}`,
          ["tag-empieza", change.tagId],
        ),
      );
    }
  }
  for (const card of cards.slice(0, HIGHLIGHTS)) out.append(card);
  const total = absorbed.length + changes.length;
  if (total > 0) {
    out.append(
      lazyTable(countedLabel("Ver", total, false, "cambio dentro del periodo", "cambios dentro del periodo"), () =>
        plainTable(["Tag", "Qué pasó", "Hora", "AGV que no lo leen como el resto"], [
          ...absorbed.map(({ gap, atUtcMs }) => [
            gap.changes
              .map((change) => (change.kind === "sustituido" ? `${change.oldTagId} → ${change.newTagId}` : change.tagId))
              .join(", "),
            describeGap(gap, duration).title,
            formatInstant(atUtcMs),
            "—",
          ]),
          ...changes.map((change) => {
            const tagId = change.kind === "cambio" ? `${change.oldTagId} → ${change.newTagId}` : change.tagId;
            const what = change.kind === "cambio" ? "cambio de tag" : change.kind === "deja" ? "dejó de leerse" : "empezó a leerse";
            const at =
              change.kind === "cambio"
                ? formatInstant(change.oldLastUtcMs)
                : formatInstant(change.kind === "deja" ? change.lastUtcMs : change.firstUtcMs);
            const newTag = change.kind === "cambio" ? change.newTagId : change.kind === "empieza" ? change.tagId : null;
            const issues = newTag === null ? [] : adoption.filter((issue) => issue.tagId === newTag);
            return [tagId, what, at, issues.length === 0 ? "—" : issues.map((issue) => issue.agvId).join(", ")];
          }),
        ]),
      ),
    );
  }

  renderTagTrends(trendTags);
}

/** Caídas de lectura de un tag que no son un cambio completo: de golpe o a lo largo del periodo (R-OPP-015). */
function renderTagTrends(tags: readonly TagRow[]): void {
  if (tags.length === 0) return;
  const broken = tags.filter((tag) => tag.changedAtUtcMs !== undefined);
  const declining = tags.filter((tag) => tag.trend === "bajando");

  const panels = [...broken, ...declining]
    .map((row) => trendPanel("Tag", row.tagId, row))
    .filter((panel): panel is { readonly panel: TrendPanel; readonly drop: number } => panel !== null)
    .sort((a, b) => b.drop - a.drop);
  if (panels.length > 0) {
    out.append(
      trendMultiplesChart(panels.slice(0, TREND_PANELS).map((entry) => entry.panel), FORMATS, "Rotura y degradación de cada tag, en el tiempo"),
    );
    if (panels.length > TREND_PANELS) {
      out.append(
        element("p", "muted", `Se dibujan los ${TREND_PANELS} cambios más marcados de ${panels.length}; el resto, en las tarjetas.`),
      );
    }
  }
  for (const tag of broken) {
    out.append(
      finding(
        `Tag ${tag.tagId}: su lectura cae de golpe`,
        `${percent(tag.rateBefore ?? null)} → ${percent(tag.rateAfter ?? null)} desde ` +
          formatInstant(tag.changedAtUtcMs as number),
        "Se leía con normalidad y desde ese momento casi no se lee.",
        ["tag-rotura", tag.tagId],
      ),
    );
  }
  for (const tag of declining) {
    out.append(
      finding(
        `Tag ${tag.tagId}: su lectura baja a lo largo del periodo`,
        (tag.segmentRates ?? []).map((rate) => percent(rate)).join(" → "),
        "Cada tramo de tiempo se lee peor que el anterior.",
        ["tag-degradacion", tag.tagId],
      ),
    );
  }
}

/**
 * Las calles de carga online, con lo notable de entrada (R-CO-002, R-CO-003, R-CO-007).
 *
 * Mismo criterio que la matriz: primero a qué mirar —una calle por la que no pasó nadie, un
 * vehículo al que se le saltó el turno, una permanencia muy por encima de su calle— y el detalle
 * por calle plegado. Lo que **no** aparece es una lista de las cargas normales: son la mayoría, y
 * enseñarlas es esconder lo otro.
 */
function renderCharging(views: CircuitViews): void {
  const charging = views.charging;
  if (charging === undefined) return;

  out.append(element("h3", undefined, "Calles de carga"));

  const zonas = views.zones ?? [];
  out.append(
    element(
      "p",
      "muted",
      `${charging.lanes.length} calles declaradas` +
        (zonas.length === 0
          ? ". Sin lista de zonas cargada."
          : `, y ${zonas.map((entry) => `${entry.tags} tags en zona ${zoneLabel(entry.zone)}`).join(" y ")}.`),
    ),
  );

  for (const problem of charging.problems) {
    out.append(finding("Configuración que no se pudo usar", "—", problem));
  }

  // Lo primero de la sección: los AGV que no entraron a cargar en toda la ventana.
  if (charging.neverCharged.length > 0) {
    const ids = charging.neverCharged.map((entry) => entry.agvId);
    const total = views.agvDossiers.length;
    out.append(
      finding(
        `${charging.neverCharged.length} de ${total} AGV no entraron en ninguna calle en toda la ventana`,
        ids.length > 12 ? `${ids.slice(0, 12).join(", ")}…` : ids.join(", "),
        "Ninguna lectura suya en una calle declarada. Cuánto tiempo estuvo presente cada uno, en el detalle.",
        ["sin-carga", "circuito"],
      ),
    );
    out.append(
      lazyTable(countedLabel("Ver", charging.neverCharged.length, false, "AGV que no entró a cargar", "AGV que no entraron a cargar"), () =>
        plainTable(
          ["AGV", "Primera lectura", "Última lectura", "Presente", "Lecturas"],
          charging.neverCharged.map((entry) => [
            entry.agvId,
            formatInstant(entry.firstUtcMs),
            formatInstant(entry.lastUtcMs),
            duration(entry.lastUtcMs - entry.firstUtcMs),
            entry.readings.toLocaleString("es-ES"),
          ]),
        ),
      ),
    );
  }

  if (charging.lanes.length > 0) out.append(laneOccupancyChart(charging.lanes, state.coverage, FORMATS));

  const sinServicio = charging.lanes.filter((lane) => !lane.served);
  for (const lane of sinServicio) {
    out.append(
      finding(
        `Nadie entró en «${lane.laneId}»`,
        "0 estancias",
        "¿Sigue en servicio? Sus tags no se pudieron leer, así que no se juzgan.",
        ["calle-sin-servicio", lane.laneId],
      ),
    );
  }

  // Las tres comprobaciones de planta (R-CO-009): que todos los AGV entren (arriba, «no entraron»),
  // que todas las calles se usen por igual, y que dentro de cada estancia se lean sus tags.
  for (const entry of charging.usage.filter((usage) => usage.verdict !== null)) {
    out.append(
      finding(
        `«${entry.laneId}» se usa ${entry.verdict === "menos" ? "menos" : "más"} que las demás`,
        `${entry.stays} estancias, el ${Math.round(entry.share * 100)} %`,
        `Con todas las calles servidas por igual le tocaría el ${Math.round(entry.expectedShare * 100)} %; ` +
          `una cuota así por azar sale ${formatChance(entry.chance)}. Qué la asigna o la evita lo decide planta.`,
        ["calle-uso", entry.laneId],
      ),
    );
  }
  for (const lane of charging.lanes.filter((entry) => entry.served)) {
    for (const tag of lane.tagReads.filter((entry) => entry.stays > 0 && entry.staysRead < entry.stays)) {
      const missed = tag.stays - tag.staysRead;
      out.append(
        finding(
          `${tag.tagId} (${tag.role}) de «${lane.laneId}» no se leyó en ${missed} de ${tag.stays} estancias`,
          `${tag.staysRead} de ${tag.stays}`,
          tag.staysRead === 0
            ? "Ningún AGV lo leyó estando dentro: comprobar el tag en planta (su clase, en el inventario)."
            : `Lo leyeron ${tag.vehicles} AGV distintos; los demás pasaron sin leerlo.`,
          ["calle-lectura", lane.laneId, tag.tagId],
        ),
      );
    }
  }

  for (const lane of charging.lanes) {
    // Solo las esperas más largas de cada calle. Dos vehículos cargando a la vez con duraciones
    // distintas invierten el orden de salida con toda normalidad, así que enseñarlas todas sería
    // enterrar la que importa entre las que no. El detalle plegado da el recuento completo.
    for (const breach of lane.outOfSeniority.slice(0, 2)) {
      out.append(
        finding(
          `A ${breach.waited} se le saltó el turno en «${lane.laneId}»`,
          duration(breach.waitedMs),
          `Entró antes que ${breach.overtakenBy.join(", ")} y salió después. Estancia habitual en la ` +
            `calle: ${duration(lane.medianStayMs)}.`,
          ["calle-espera", lane.laneId, breach.waited],
        ),
      );
    }
    for (const stay of lane.longStays) {
      out.append(
        finding(
          `${stay.agvId} permaneció en «${lane.laneId}» mucho más que el resto`,
          duration(stay.durationMs),
          `Lo habitual en esa calle: ${duration(lane.medianStayMs)}.`,
          ["calle-permanencia", lane.laneId, stay.agvId],
        ),
      );
    }
  }

  if (charging.startedInside.length > 0) {
    const desde =
      charging.coverageStartUtcMs === null
        ? "el inicio de la cobertura"
        : new Date(charging.coverageStartUtcMs).toISOString().slice(0, 16).replace("T", " ");
    out.append(
      finding(
        `${charging.startedInside.length} AGV ya estaban cargando al empezar los datos`,
        charging.startedInside.map((stay) => stay.agvId).join(", "),
        `Su primera lectura es la salida de una calle: desde ${desde} hasta que salieron, esas calles ` +
          "no estaban vacías.",
        ["arranque-en-frio", "circuito"],
      ),
    );
  }


  const usageOf = new Map(charging.usage.map((entry) => [entry.laneId, entry]));
  out.append(
    lazyTable(countedLabel("Detalle de", charging.lanes.length, true, "calle", "calles"), () =>
      plainTable(
        ["Calle", "Capacidad", "Estancias", "Cuota", "Estancia habitual", "Turnos saltados"],
        charging.lanes.map((lane) => {
          const usage = usageOf.get(lane.laneId);
          return [
            lane.laneId,
            lane.capacity === null ? "—" : String(lane.capacity),
            String(lane.stays),
            usage === undefined ? "—" : `${Math.round(usage.share * 100)} % (le tocaría ${Math.round(usage.expectedShare * 100)} %)`,
            duration(lane.medianStayMs),
            String(lane.outOfSeniority.length),
          ];
        }),
      ),
    ),
  );
  out.append(
    lazyTable("Qué se lee dentro de cada calle", () =>
      plainTable(
        ["Calle", "Tag", "Papel", "Leído en estancias", "AGV distintos"],
        charging.lanes.flatMap((lane) =>
          lane.tagReads.map((tag) => [lane.laneId, tag.tagId, tag.role, `${tag.staysRead} de ${tag.stays}`, String(tag.vehicles)]),
        ),
      ),
    ),
  );
}

/**
 * Comparación entre dos periodos distantes (R-DAT-016, R-AGV-013, R-DAT-017).
 *
 * Solo aparece cuando hay al menos dos periodos de cobertura separados lo bastante para tratarlos
 * como distantes (`DriftThresholds.minGapMs`): con una sola fuente cargada no hay con qué comparar,
 * y no mostrar nada es más honesto que un aviso permanente. El recorte es global —top 5 de cada
 * lista, con el detalle completo plegado—, misma razón que FIFO y los puntos críticos: el número de
 * tags o vehículos no está acotado.
 *
 * Dos hallazgos correlacionados, más allá de los tres binarios de la Parte 33: `sustitucion-candidata`
 * empareja un tag que desaparece con uno que ocupa su mismo hueco en la secuencia (R-DAT-017), y
 * `notAdoptedTags` señala, por vehículo, un tag nuevo ya adoptado por la flota que ese vehículo
 * concreto nunca ha leído.
 */
function renderDrift(views: CircuitViews): void {
  const drift = views.drift;
  if (drift === undefined) return;

  type TagDriftEntry = NonNullable<CircuitViews["drift"]>["tagDrifts"][number];

  const kindLabel: Readonly<Record<string, string>> = {
    desaparecido: "dejó de leerse",
    nuevo: "tag nuevo",
    "obsoleto-consolidado": "sin lecturas en ningún periodo",
    "sustitucion-candidata": "posible sustitución",
  };
  // Un desaparecido o un nuevo se afirma solo si la ausencia es improbable por azar (OQ-138); si no,
  // se enseña igual, «sin afirmar», con las pasadas por su sitio que hubo para leerlo.
  const azar = (entry: TagDriftEntry): string =>
    entry.affirmed === undefined
      ? ""
      : entry.affirmed
        ? ` (${entry.opportunities} pasadas por su sitio sin leerlo)`
        : ` — sin afirmar: ${entry.opportunities ?? 0} pasadas por su sitio, por azar saldría ${formatChance(entry.chance ?? 1)}`;
  const detailOf = (entry: TagDriftEntry): string =>
    entry.kind === "desaparecido"
      ? `${entry.readingsBefore} lecturas antes, 0 ahora${azar(entry)}`
      : entry.kind === "nuevo"
        ? `0 lecturas antes, ${entry.readingsAfter} ahora${azar(entry)}`
        : entry.kind === "sustitucion-candidata"
          ? `${entry.readingsBefore} lecturas de «${entry.tagId}» antes, ${entry.readingsAfter} de ` +
            `«${entry.nuevoTagId}» ahora, en el mismo sitio (junto a ${entry.sharedNeighbor})`
          : "0 lecturas en los dos periodos";
  const evidenceOf = (entry: TagDriftEntry): string =>
    entry.kind === "desaparecido"
      ? "Averiado, sustituido o retirado: comprobar en planta."
      : entry.kind === "nuevo"
        ? "Sustitución o instalación nueva."
        : entry.kind === "sustitucion-candidata"
          ? "Un tag desaparece y otro aparece en el mismo sitio a la vez. Confirmar en planta."
          : "Probablemente ya no está instalado. Confirmar en planta.";
  const tagCellOf = (entry: TagDriftEntry): string =>
    entry.kind === "sustitucion-candidata" ? `${entry.tagId} → ${entry.nuevoTagId}` : entry.tagId;

  out.append(element("h3", undefined, "Comparación entre dos periodos"));
  out.append(
    element(
      "p",
      "muted",
      `Periodo temprano: ${formatInstant(drift.earlyPeriod.from)} – ${formatInstant(drift.earlyPeriod.to)}. ` +
        `Periodo tardío: ${formatInstant(drift.latePeriod.from)} – ${formatInstant(drift.latePeriod.to)}. ` +
        "Dice qué cambió entre los dos periodos, no por qué.",
    ),
  );
  if (drift.tagDrifts.length > 0) out.append(driftChart(drift));

  // La suma entre anclas sitúa cada cambio (R-DAT-021): se añade como una línea, sin otra tarjeta.
  const gaps = views.franjas.cohorts.flatMap((cohort) =>
    [...cohort.structure].sort((a, b) => (a.source === b.source ? 0 : a.source === "entre-ficheros" ? -1 : 1)).flatMap((set) => set.gaps),
  );
  const gapLine = (tagId: string): string => {
    const gap = gaps.find((candidate) => changedTags(candidate).includes(tagId));
    return gap === undefined ? "" : gapLineFor(tagId, gap, duration);
  };
  const sortedTags = [...drift.tagDrifts].sort((a, b) => a.tagId.localeCompare(b.tagId));
  for (const entry of sortedTags.slice(0, 5)) {
    out.append(
      finding(`${entry.tagId}: ${kindLabel[entry.kind]}`, detailOf(entry), `${evidenceOf(entry)}${gapLine(entry.kind === "sustitucion-candidata" ? (entry.nuevoTagId ?? entry.tagId) : entry.tagId)}`, [
        "deriva",
        entry.kind,
        entry.tagId,
      ]),
    );
  }
  if (sortedTags.length > 0) {
    out.append(
      lazyTable(countedLabel("Detalle de", sortedTags.length, false, "tag con cambios", "tags con cambios"), () =>
        plainTable(
          ["Tag", "Cambio", "Antes", "Ahora"],
          sortedTags.map((entry) => [
            tagCellOf(entry),
            kindLabel[entry.kind] ?? entry.kind,
            String(entry.readingsBefore),
            String(entry.readingsAfter),
          ]),
        ),
      ),
    );
  }

  const sortedVehicles = [...drift.vehicleDrifts].sort(
    (a, b) => b.droppedTags.length + b.notAdoptedTags.length - (a.droppedTags.length + a.notAdoptedTags.length),
  );
  // Los AGV con exactamente los mismos tags dejados y no adoptados son una sola tarjeta, con el
  // conjunto como sujeto (UX_SPEC §4.3): siete tarjetas iguales salvo el número no son siete hechos.
  const vehicleGroups = new Map<string, { agvIds: string[]; sample: (typeof sortedVehicles)[number] }>();
  for (const entry of sortedVehicles) {
    const key = `${neverSetOf(entry.droppedTags)}|${neverSetOf(entry.notAdoptedTags)}`;
    const group = vehicleGroups.get(key);
    if (group === undefined) vehicleGroups.set(key, { agvIds: [entry.agvId], sample: entry });
    else group.agvIds.push(entry.agvId);
  }
  for (const { agvIds, sample: entry } of [...vehicleGroups.values()].slice(0, 5)) {
    const plural = agvIds.length > 1;
    const parts: string[] = [];
    if (entry.droppedTags.length > 0) {
      parts.push(`${plural ? "dejaron" : "dejó"} de leer ${entry.droppedTags.length} tags que sí ${plural ? "leían" : "leía"} antes`);
    }
    if (entry.notAdoptedTags.length > 0) {
      parts.push(
        `no ${plural ? "registran" : "registra"} ${entry.notAdoptedTags.length === 1 ? "1 tag nuevo" : `${entry.notAdoptedTags.length} tags nuevos`} que ya lee la mayoría de la flota`,
      );
    }
    const detail = [
      entry.droppedTags.length > 0
        ? `Dejados: ${entry.droppedTags.slice(0, 5).join(", ")}${entry.droppedTags.length > 5 ? "…" : ""}`
        : null,
      entry.notAdoptedTags.length > 0
        ? `No adoptados: ${entry.notAdoptedTags.slice(0, 5).join(", ")}${entry.notAdoptedTags.length > 5 ? "…" : ""}`
        : null,
    ]
      .filter((line): line is string => line !== null)
      .join(" — ");
    const shown = agvIds.length > 4 ? `${agvIds.slice(0, 4).join(", ")} y ${agvIds.length - 4} más` : joinIds(agvIds);
    out.append(
      finding(
        `${shown}: ${parts.join(", y ")}`,
        detail,
        `El resto de la flota lee esos tags con normalidad: revisar la memoria de ${plural ? "estos AGV" : "este AGV"}.`,
        ["deriva-agv", ...[...agvIds].sort()],
      ),
    );
  }
  if (sortedVehicles.length > 0) {
    out.append(
      lazyTable(countedLabel("Detalle de", sortedVehicles.length, false, "AGV con cambios", "AGV con cambios"), () =>
        plainTable(
          ["AGV", "Tags dejados de leer", "Tags nuevos no adoptados"],
          sortedVehicles.map((entry) => [
            entry.agvId,
            entry.droppedTags.join(", "),
            entry.notAdoptedTags.join(", "),
          ]),
        ),
      ),
    );
  }
}

/**
 * Candidatos a punto crítico (R-GRA-007), como firma estadística y no como función asignada.
 *
 * Cuatro clases con firma: bifurcación (reparto sostenido sin dominante), cruce (una bifurcación
 * cuyas ramas reconvergen en pocos saltos — un cruce físico, no un reparto que dure), parada precisa
 * (duración larga y de poca varianza) y semáforo (duración bimodal). El recorte es global, igual
 * razón que FIFO: el número de tags no está acotado.
 */
function renderCriticalPoints(views: CircuitViews): void {
  const problems = views.criticalPointsProblems ?? [];
  const candidates = views.criticalPoints.flatMap((cohort) => cohort.candidates);
  const atMinute = views.criticalPoints.some((cohort) => !cohort.timeSignatures);
  if (candidates.length === 0 && problems.length === 0 && !atMinute) return;

  type Candidate = NonNullable<CircuitViews["criticalPoints"]>[number]["candidates"][number];

  const kindLabel: Readonly<Record<Candidate["kind"], string>> = {
    bifurcacion: "posible bifurcación",
    cruce: "posible cruce",
    "parada-precisa": "posible parada precisa",
    semaforo: "posible semáforo",
  };
  const weightOf = (candidate: Candidate): number => candidate.samples ?? candidate.support ?? 0;
  const detailOf = (candidate: Candidate): string =>
    candidate.kind === "bifurcacion" || candidate.kind === "cruce"
      ? `${(candidate.support ?? 0).toLocaleString("es-ES")} pasadas`
      : `${(candidate.samples ?? 0).toLocaleString("es-ES")} pasadas`;
  const branchesOf = (candidate: Candidate): string =>
    (candidate.branches ?? []).map((branch) => `${branch.tagId} (${Math.round(branch.share * 100)} %)`).join(", ");

  out.append(element("h3", undefined, "Candidatos a punto crítico"));
  out.append(
    element(
      "p",
      "muted",
      `${candidates.length} tags se comportan como un punto crítico. Son propuestas para confirmar ` +
        "o descartar en planta; la función real la da la lista de críticos.",
    ),
  );
  if (atMinute) {
    out.append(
      element(
        "p",
        "muted",
        "La hora de esta fuente va al minuto: no se buscan paradas precisas ni semáforos, que se " +
          "reconocen por cuánto dura la espera y eso, al minuto, no se puede medir.",
      ),
    );
  }

  for (const problem of problems) {
    out.append(finding("Punto crítico declarado que no se pudo usar", "—", problem));
  }

  const sorted = [...candidates].sort((a, b) => weightOf(b) - weightOf(a));

  // Las firmas dibujadas antes que las tarjetas: la horquilla de los repartos y la distribución de
  // tiempos de las paradas y los semáforos.
  const forks: ForkData[] = sorted
    .filter((candidate) => (candidate.kind === "bifurcacion" || candidate.kind === "cruce") && candidate.branches !== undefined)
    .slice(0, FORK_DIAGRAMS)
    .map((candidate) => ({
      tagId: candidate.tagId,
      kind: candidate.kind === "cruce" ? "cruce" : "bifurcacion",
      support: candidate.support ?? 0,
      branches: candidate.branches ?? [],
      ...(candidate.reconvergesAt === undefined ? {} : { reconvergesAt: candidate.reconvergesAt }),
      ...(candidate.hops === undefined ? {} : { hops: candidate.hops }),
    }));
  if (forks.length > 0) {
    out.append(forkChart(forks, PROVISIONAL_CONFIG.criticalPoints.cruce.maxHopsToReconverge));
  }
  for (const cohort of views.criticalPoints) {
    const timed = cohort.candidates
      .filter((candidate) => (candidate.kind === "parada-precisa" || candidate.kind === "semaforo") && (candidate.durationsMs?.length ?? 0) > 0)
      .sort((a, b) => (b.samples ?? 0) - (a.samples ?? 0))
      .slice(0, DWELL_ROWS);
    if (timed.length === 0) continue;
    const reference = [...cohort.referenceDurationsMs].sort((a, b) => a - b);
    const rows: DwellRow[] = [
      {
        title: "Referencia",
        detail: "el resto del circuito",
        note: `muestra de ${reference.length.toLocaleString("es-ES")} de ${cohort.referenceTotal.toLocaleString("es-ES")} · mediana ${Math.round((reference[Math.floor(reference.length / 2)] ?? 0) / 1000)} s`,
        durationsMs: reference,
        isReference: true,
      },
      ...timed.map((candidate) => ({
        title: candidate.tagId,
        detail: kindLabel[candidate.kind],
        note:
          candidate.kind === "parada-precisa"
            ? `media ${Math.round((candidate.meanDurationMs ?? 0) / 1000)} s · CV ${(candidate.coefficientOfVariation ?? 0).toFixed(2)}`
            : `dos grupos: ${Math.round((candidate.lowClusterMeanMs ?? 0) / 1000)} s y ${Math.round((candidate.highClusterMeanMs ?? 0) / 1000)} s`,
        durationsMs: candidate.durationsMs ?? [],
        isReference: false,
      })),
    ];
    out.append(dwellChart(rows));
  }

  for (const candidate of sorted.slice(0, 5)) {
    out.append(
      finding(`${candidate.tagId}: ${kindLabel[candidate.kind]}`, detailOf(candidate), candidate.evidence, [
        "punto-critico",
        candidate.kind,
        candidate.tagId,
      ]),
    );
  }

  out.append(
    lazyTable(countedLabel("Detalle de", candidates.length, false, "candidato", "candidatos"), () =>
      plainTable(
        ["Tag", "Posible función", "Pasadas", "Detalle"],
        sorted.map((candidate) => [
          candidate.tagId,
          candidate.kind,
          String(weightOf(candidate)),
          candidate.kind === "bifurcacion" || candidate.kind === "cruce"
            ? branchesOf(candidate)
            : candidate.kind === "parada-precisa"
              ? `${Math.round((candidate.meanDurationMs ?? 0) / 1000)} s, cv ${(candidate.coefficientOfVariation ?? 0).toFixed(2)}`
              : `${Math.round((candidate.lowClusterMeanMs ?? 0) / 1000)} s / ${Math.round((candidate.highClusterMeanMs ?? 0) / 1000)} s`,
        ]),
      ),
    ),
  );
}

/**
 * FIFO en zona cargada (R-FLO-001), como candidato y no como avería.
 *
 * El recorte es **global** y no por tramo, a diferencia de las calles de carga: el número de calles
 * está acotado por diseño (R-CO-001), pero un anillo puede tener un número no acotado de tramos de
 * zona cargada. Mostrar los cinco de mayor margen de todo el circuito evita que un tramo con muchos
 * adelantamientos pequeños entierre el único que importa en otro tramo.
 */
function renderFifo(views: CircuitViews): void {
  const fifo = views.fifo;
  if (fifo === undefined) return;

  const spans = fifo.flatMap((cohort) => cohort.spans);
  const problems = fifo.flatMap((cohort) => cohort.problems);
  if (spans.length === 0 && problems.length === 0) return;

  out.append(element("h3", undefined, "Orden de paso en zona cargada (FIFO)"));
  out.append(
    element(
      "p",
      "muted",
      `${plural(spans.length, "tramo", "tramos")} de zona cargada, ${spans.filter((span) => span.evaluated).length} ` +
        "con pasadas suficientes. Un adelantamiento puede tener explicación (carga, maniobra " +
        "manual): hay que comprobarlo.",
    ),
  );

  for (const problem of problems) {
    out.append(finding("Tramo que no se pudo acotar", "—", problem));
  }

  // El adelantamiento con más vehículos por delante, dibujado: una línea que cruza a las demás.
  const focused = spans
    .filter((span) => span.focus.length > 0 && span.overtakes.length > 0)
    .map((span) => ({
      span,
      top: span.overtakes.reduce((best, overtake) => (overtake.overtakenBy.length > best.overtakenBy.length ? overtake : best)),
    }))
    .sort((a, b) => b.top.overtakenBy.length - a.top.overtakenBy.length)[0];
  if (focused !== undefined) out.append(fifoSlopeChart(focused.span, focused.top.overtaken, FORMATS));

  const overtakesFlat = spans.flatMap((span) =>
    span.overtakes.map((overtake) => ({ span, overtake })),
  );
  overtakesFlat.sort((a, b) => b.overtake.marginMs - a.overtake.marginMs);
  for (const { span, overtake } of overtakesFlat.slice(0, 5)) {
    out.append(
      finding(
        `${overtake.overtaken} fue adelantado en el tramo cargado «${span.spanId}»`,
        `por ${overtake.overtakenBy.join(", ")}, ${duration(overtake.marginMs)} de margen`,
        `Entró antes y salió después (tránsito de ${duration(overtake.transitMs)}). En zona ` +
          "cargada se espera que salgan en el orden en que entran.",
        ["fifo", span.spanId, overtake.overtaken],
      ),
    );
  }

  out.append(
    lazyTable(countedLabel("Detalle de", spans.length, false, "tramo", "tramos"), () =>
      plainTable(
        ["Tramo", "Tags", "Pasadas", "Tránsito habitual", "Adelantamientos"],
        spans.map((span) => [
          span.spanId,
          String(span.tagCount),
          String(span.passes),
          duration(span.medianTransitMs),
          String(span.overtakes.length),
        ]),
      ),
    ),
  );
}

/** Una duración en la unidad que se lee de un vistazo. `null` es «no se sabe», nunca cero. */
function duration(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 90_000) return `${Math.round(ms / 1000)} s`;
  const minutes = Math.round(ms / 60_000);
  if (minutes < 90) return `${minutes} min`;
  return `${(minutes / 60).toFixed(1)} h`;
}

/**
 * Un hallazgo, como tarjeta y no como fila.
 *
 * `UX_SPEC.md` §4 ya lo pedía así —conclusión, objeto, evidencia— y la razón se ve al mirarlo en un
 * móvil: una tabla de cinco columnas en 360 px parte los encabezados letra a letra. Cabe, y es
 * ilegible.
 */
/**
 * Una tarjeta de hallazgo. Con `review` —su tipo y su sujeto— lleva además los botones de revisión
 * en campo. Sin él es un aviso de configuración, que no se va a comprobar delante de ningún tag.
 */
/** Lo que planta declara de cada tag del análisis que se está enseñando (`views.tagInfo`). */
let currentTagInfo: Readonly<Record<string, string>> = {};
/** La batería de mediciones de cada incidencia (R-AGV-021), por clave «AGV tag instante». */
let currentBatteries: NonNullable<CircuitViews["incidents"]>["batteries"] = {};

/** Añade a la tarjeta de una incidencia su batería de mediciones, en el orden de siempre. */
function withBattery(card: HTMLElement, key: string): HTMLElement {
  const battery = currentBatteries[key];
  if (battery === undefined) return card;
  const list = document.createElement("ol");
  list.className = "battery";
  for (const text of battery.lines) list.append(element("li", undefined, text));
  card.append(element("p", "finding-figure", "Mediciones"), list);
  return card;
}

/**
 * Qué tags nombra una tarjeta, por su tipo de revisión. Solo los tipos cuyo sujeto es un tag: en los
 * de AGV, un identificador de vehículo que coincida con el de un tag no debe traer su función.
 */
function tagsOfReview(review: readonly string[] | undefined): readonly string[] {
  if (review === undefined) return [];
  const [kind, ...rest] = review;
  switch (kind) {
    case "tag-lectura":
    case "tag-deja":
    case "tag-empieza":
    case "tag-rotura":
    case "tag-degradacion":
    case "cuello-de-botella":
    case "zona-oscura":
    case "cambio-tag":
    case "tag-fuera-del-circuito":
      return rest;
    case "punto-conflictivo":
      return (rest[0] ?? "").split("+");
    case "deriva":
    case "punto-critico":
      return rest.slice(1);
    case "bloqueo":
    case "parada-sin-explicacion":
      return [(rest[0] ?? "").split(" ")[1] ?? ""];
    default:
      return [];
  }
}

function finding(title: string, figure: string, evidence: string, review?: readonly string[]): HTMLElement {
  const card = element("div", "finding");
  card.append(
    element("p", "finding-title", title),
    element("p", "finding-figure", figure),
    element("p", "muted", evidence),
  );
  // Lo declarado en planta de cada tag de la tarjeta: aporta al diagnóstico y no cambia nada.
  const declared = [...new Set(tagsOfReview(review))].filter((tagId) => currentTagInfo[tagId] !== undefined);
  if (declared.length > 0) {
    card.append(
      element(
        "p",
        "muted",
        `Declarado en planta: ${declared.map((tagId) => (declared.length === 1 ? currentTagInfo[tagId] : `${tagId}, ${currentTagInfo[tagId]}`)).join("; ")}.`,
      ),
    );
  }
  if (review !== undefined) {
    card.dataset["kind"] = review[0] ?? "";
    reviewSession?.attach(card, review, title, figure);
  }
  return card;
}

// --- Portada: la tira de cifras (UX_SPEC §2) -------------------------------------------------------

/**
 * Activa una pestaña y desplaza hasta el encabezado con ese texto (o hasta la figura con ese título),
 * dejándole el foco: es lo que hace cada tile y el enlace «El anillo está en Resumen».
 */
function jumpTo(tab: TabId, heading: string, alternative?: string): void {
  activateTab(tab);
  const panel = tabPanels.get(tab);
  const headings = [...(panel?.querySelectorAll<HTMLElement>("h2, h3") ?? [])];
  const target = headings.find((node) => node.textContent === heading) ?? headings.find((node) => node.textContent === alternative);
  const focusOn = target?.closest("figure") ?? target ?? panel;
  focusOn?.scrollIntoView({ block: "start" });
  if (focusOn instanceof HTMLElement) {
    focusOn.tabIndex = -1;
    focusOn.focus({ preventScroll: true });
  }
}

/** Un tile: etiqueta corta, cifra grande y la línea de contexto. Sin dato, «sin datos» en gris. */
function tile(
  label: string,
  value: string | null,
  note: string,
  go: () => void,
  options: { readonly accent?: boolean; readonly id?: string; readonly empty?: string } = {},
): HTMLElement {
  const item = element("div", "tile-item");
  item.setAttribute("role", "listitem");
  const button = element("button", "tile");
  button.type = "button";
  if (options.id !== undefined) button.dataset["tile"] = options.id;
  if (options.accent === true) button.classList.add("attn");
  button.append(
    element("p", "tile-label", label),
    element("p", value === null ? "tile-value tile-empty" : "tile-value", value ?? options.empty ?? "sin datos"),
    element("p", "tile-note", note),
  );
  button.addEventListener("click", go);
  item.append(button);
  return item;
}

/**
 * Las seis cifras de la portada, cada una con de dónde sale. Nada se calcula: se lee de las vistas que
 * ya llegan, y donde el dato no existe el tile dice «sin datos», nunca un cero inventado.
 */
function renderTiles(views: CircuitViews): void {
  tiles.replaceChildren();
  const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

  // AGV en el circuito: el último tramo del recuento N de M (DS-012). Es el más reciente, que es lo que
  // se pregunta al abrir el análisis; la ventana entera está en el gráfico de la flota.
  const fleet = views.fleet;
  const last = fleet.counts[fleet.counts.length - 1];
  tiles.append(
    tile(
      "AGV en el circuito",
      last === undefined ? null : `${last.inCircuit} de ${last.assigned}`,
      last === undefined
        ? "sin recuento de flota"
        : fleet.historyLoaded
          ? "según el historial de flota, al final de lo cargado"
          : "vistos en las lecturas, al final de lo cargado",
      () => jumpTo("agv", "Flota del circuito"),
      { id: "agv" },
    ),
  );

  // Tags en el anillo: el ciclo dominante del primer cohorte; fuera del anillo y declarados sin lecturas.
  const shape = views.shapes[0];
  const sinLecturas = views.circuitOrder?.summary["sin-lecturas"] ?? null;
  const tagNotes: string[] = [];
  if (shape !== undefined && shape.offRingTags.length > 0) tagNotes.push(`${shape.offRingTags.length} fuera del anillo`);
  if (sinLecturas !== null && sinLecturas > 0) tagNotes.push(plural(sinLecturas, "declarado sin lecturas", "declarados sin lecturas"));
  tiles.append(
    tile(
      "Tags en el anillo",
      shape === undefined ? null : String(shape.tags.length),
      shape === undefined ? "sin anillo reconocible" : tagNotes.length === 0 ? "todos en el recorrido" : tagNotes.join(", "),
      () => jumpTo("tags", "Lo que hay que mirar"),
      { id: "tags" },
    ),
  );

  // Cobertura: las horas cargadas (la unión de los tramos, R-DAT-007) y los ficheros acumulados.
  const coveredMs = state.coverage.reduce((sum, span) => sum + (span.to - span.from), 0);
  const first = state.coverage[0];
  const end = state.coverage[state.coverage.length - 1];
  const hours = coveredMs / 3_600_000;
  tiles.append(
    tile(
      "Cobertura",
      state.coverage.length === 0 ? null : `${(hours < 10 ? hours.toFixed(1) : String(Math.round(hours))).replace(".", ",")} h`,
      state.coverage.length === 0 || first === undefined || end === undefined
        ? "todavía ninguna"
        : `${plural(state.sources, "fichero", "ficheros")}, de ${formatTick(first.from)} a ${formatTick(end.to)}`,
      () => jumpTo("datos", "Cobertura cargada"),
      { id: "cobertura" },
    ),
  );

  // Hallazgos: se rellena aparte, porque cambia con cada marca de revisión.
  const findingsSlot = element("div", "tile-item");
  findingsSlot.setAttribute("role", "listitem");
  findingsSlot.dataset["slot"] = "hallazgos";
  tiles.append(findingsSlot);
  renderFindingsTile();

  // Vuelta: la mediana del último fichero medido (R-TIM-011), la misma que la tabla «Mediciones por
  // fichero» enseña como «Vuelta»; el anterior, para ver si se mueve.
  const cohort = views.franjas.cohorts[0];
  const measured = views.franjas.sources.filter((source) => source.duplicateOf === null);
  const laps = measured
    .map((source) => ({ source, measure: cohort?.measures.find((entry) => entry.sourceId === source.sourceId) }))
    .filter((entry) => entry.measure !== undefined && entry.measure.lapMs !== null);
  const lastLap = laps[laps.length - 1];
  const previousLap = laps[laps.length - 2];
  // Con más de una instantánea el tile lo dice y lleva a la figura de evolución; con una, a la tabla.
  const withSnapshot = views.snapshots.list.filter((entry) => entry.hasSnapshot).length;
  tiles.append(
    tile(
      "Vuelta",
      lastLap === undefined ? null : duration(lastLap.measure?.lapMs ?? null),
      lastLap === undefined
        ? "sin vuelta medida"
        : `fichero ${lastLap.source.fileName}` +
            (previousLap === undefined ? "" : `; el anterior, ${duration(previousLap.measure?.lapMs ?? null)}`) +
            (withSnapshot > 1 ? `; ${withSnapshot} instantáneas` : ""),
      () => (withSnapshot > 1 ? jumpTo("tiempos", "El circuito a lo largo de los ficheros", "Mediciones por fichero") : jumpTo("tiempos", "Mediciones por fichero")),
      { id: "vuelta" },
    ),
  );

  // Línea: paradas en producción y el tiempo sin paso (R-FLO-010), con la cadencia de mediana.
  const feed = views.lineFeed;
  const production = feed?.rhythm.find((rhythm) => rhythm.regime === "produccion");
  const lineStops = feed === undefined ? 0 : feed.stops.filter((stop) => stop.regime === "produccion").length;
  const minutes = (ms: number): string => `${Math.round(ms / 60_000).toLocaleString("es-ES")} min`;
  tiles.append(
    tile(
      "Línea",
      feed === undefined || !feed.evaluated || feed.cadence === null ? null : plural(lineStops, "parada", "paradas"),
      feed === undefined
        ? "sin línea declarada"
        : !feed.evaluated || feed.cadence === null
          ? (feed.reason ?? "sin medir")
          : (production === undefined ? "en producción" : `${minutes(production.aboveFenceMs)} sin paso; un AGV cada ${duration(production.cycleMs)} de mediana`),
      () => jumpTo("linea", "Alimentación de la línea"),
      { id: "linea" },
    ),
  );

  // Memoria (F4): la versión vigente de la memoria consolidada, o «sin consolidar». Se rellena aparte
  // porque cambia al consolidar o revocar sin que haya una importación nueva.
  const memorySlot = element("div", "tile-item");
  memorySlot.setAttribute("role", "listitem");
  memorySlot.dataset["slot"] = "memoria";
  tiles.append(memorySlot);
  renderMemoryTile(views.memory ?? null);
}

/** El tile de la memoria: «vN» de la vigente o «sin consolidar»; lleva a la pestaña Memoria. */
function renderMemoryTile(memory: MemoryViews | null): void {
  const slot = tiles.querySelector<HTMLElement>("[data-slot='memoria']");
  if (slot === null) return;
  const versions = memory?.versions.length ?? 0;
  const current = memory?.current ?? null;
  slot.replaceChildren(
    ...tile(
      "Memoria",
      current === null ? null : `v${current}`,
      state.circuitId === null
        ? "sin circuito"
        : current === null
          ? versions === 0
            ? "ninguna versión consolidada"
            : `${versions} ${versions === 1 ? "versión revocada" : "versiones, todas revocadas"}`
          : `${versions} ${versions === 1 ? "versión" : "versiones"}; vigente de ${memory?.versions.find((entry) => entry.version === current)?.basedOnFileName ?? "—"}`,
      () => jumpTo("memoria", "Memoria del circuito"),
      { id: "memoria", empty: "sin consolidar" },
    ).children,
  );
}

/**
 * El tile de hallazgos: pendientes de total, y cuántos de rango 1 siguen pendientes. Cuenta las
 * tarjetas de la bandeja, que es la unidad de revisión (R-EVI-007), y se rehace con cada marca.
 */
function renderFindingsTile(): void {
  const slot = tiles.querySelector<HTMLElement>("[data-slot='hallazgos']");
  if (slot === null) return;
  const cards = [...tray.querySelectorAll<HTMLElement>(".finding.reviewable")];
  const pendingOf = (list: readonly HTMLElement[]): number => list.filter((card) => (card.dataset["review"] ?? "pendiente") === "pendiente").length;
  const pending = pendingOf(cards);
  const critical = pendingOf(cards.filter((card) => card.closest<HTMLElement>(".tray-group")?.dataset["rank"] === "1"));
  slot.replaceChildren(
    ...tile(
      "Hallazgos",
      cards.length === 0 ? null : `${pending} de ${cards.length}`,
      cards.length === 0
        ? "ninguno que revisar"
        : pending === 0
          ? "revisión completa"
          : critical === 0
            ? "pendientes; ninguno puede parar la planta"
            : `pendientes; ${critical} ${critical === 1 ? "puede" : "pueden"} parar la planta`,
      () => {
        activateTab("resumen");
        trayPanel.scrollIntoView({ block: "start" });
        trayPanel.tabIndex = -1;
        trayPanel.focus({ preventScroll: true });
      },
      { id: "hallazgos", accent: critical > 0 },
    ).children,
  );
}

// --- Bandeja de hallazgos (UX_SPEC §4.5) ---------------------------------------------------------

/** El filtro por tema de la bandeja («» es todos). */
let trayTheme: Theme | "" = "";

/** Un identificador de encabezado a partir de su texto, único dentro de la página. */
function headingId(text: string, taken: Set<string>): string {
  const base = `sec-${text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}`;
  let id = base;
  for (let n = 2; taken.has(id); n += 1) id = `${base}-${n}`;
  taken.add(id);
  return id;
}

/**
 * Reúne todas las tarjetas revisables en la bandeja del Resumen, ordenadas por rango y agrupadas por
 * tema, y deja en cada sección una línea «N hallazgos de esta sección: ver en Resumen». Una sola
 * copia de cada tarjeta: es la unidad de revisión (R-EVI-007).
 */
/**
 * Los hallazgos de la instantánea del fichero de trabajo que ninguna sección ha pintado —cada sección
 * enseña los primeros de cada tipo y el resto en su tabla— van a la bandeja como tarjetas propias.
 * Son los que cuentan al consolidar: sin tarjeta no se podrían revisar y quedarían pendientes para
 * siempre (3.57.0). La clave es la de la instantánea, partida por su separador.
 */
function renderUncardedFindings(views: CircuitViews): void {
  const session = reviewSession;
  if (session === null) return;
  const missing = (views.snapshots.workingFindings ?? []).filter((item) => !session.has(item.key));
  if (missing.length === 0) return;
  const out = viewsOf.get("resumen") as HTMLElement;
  out.append(element("h3", undefined, "Más hallazgos del periodo"));
  out.append(
    element(
      "p",
      "muted",
      "Su sección enseña los primeros de cada tipo; estos también cuentan al consolidar y se revisan aquí.",
    ),
  );
  const cards = element("div", "findings");
  for (const item of missing) {
    const card = finding(item.title, item.figure, "Está en la tabla de su sección.", item.key.split("|"));
    // Nacen en el Resumen: no hay sección con evidencia a la que llevar desde su tarjeta.
    card.dataset["uncarded"] = "si";
    cards.append(card);
  }
  out.append(cards);
}

function buildTray(): void {
  interface Entry {
    readonly card: HTMLElement;
    readonly tab: TabId;
    readonly sectionId: string;
    readonly sectionTitle: string;
    readonly order: number;
  }
  const entries: Entry[] = [];
  const taken = new Set<string>();
  for (const tab of TAB_IDS) {
    const panel = viewsOf.get(tab) as HTMLElement;
    let section: { id: string; title: string } = { id: panel.id, title: TABS.find(([id]) => id === tab)?.[1] ?? tab };
    const firstOfSection = new Map<string, HTMLElement>();
    const countOf = new Map<string, number>();
    for (const node of panel.querySelectorAll<HTMLElement>("h3, .finding.reviewable")) {
      if (node.tagName === "H3") {
        // Los títulos de las figuras no abren sección: la tarjeta que sigue a un gráfico es de la
        // sección que lo contiene.
        if (node.closest("figure") !== null) continue;
        if (node.id === "") node.id = headingId(node.textContent ?? "", taken);
        section = { id: node.id, title: node.textContent ?? "" };
        continue;
      }
      entries.push({ card: node, tab, sectionId: section.id, sectionTitle: section.title, order: entries.length });
      countOf.set(section.id, (countOf.get(section.id) ?? 0) + 1);
      if (!firstOfSection.has(section.id)) firstOfSection.set(section.id, node);
    }
    renderProblemIndex(tab, panel, entries.filter((entry) => entry.tab === tab));
    // En cada sección, donde salían las tarjetas, la línea que lleva a la bandeja.
    for (const [sectionId, first] of firstOfSection) {
      const count = countOf.get(sectionId) ?? 0;
      const line = element("p", "findings-elsewhere");
      const link = element("a", undefined, "ver en Resumen");
      link.href = "#resumen";
      const theme = findingKindOf(first.dataset["kind"] ?? "").theme;
      link.addEventListener("click", (event) => {
        event.preventDefault();
        setTrayTheme(theme);
        activateTab("resumen");
        trayPanel.scrollIntoView({ block: "start" });
      });
      line.append(`${count} ${count === 1 ? "hallazgo de esta sección" : "hallazgos de esta sección"}: `, link);
      first.replaceWith(line);
    }
  }
  for (const entry of entries) entry.card.remove();

  // Ordenadas por rango; dentro del rango, por tema y por orden de aparición.
  const kindOf = (entry: Entry) => findingKindOf(entry.card.dataset["kind"] ?? "");
  entries.sort((a, b) => {
    const ka = kindOf(a);
    const kb = kindOf(b);
    if (ka.rank !== kb.rank) return ka.rank - kb.rank;
    if (ka.theme !== kb.theme) return THEMES.indexOf(ka.theme) - THEMES.indexOf(kb.theme);
    return a.order - b.order;
  });

  trayPanel.replaceChildren();
  trayPanel.hidden = false;
  trayPanel.append(element("h2", undefined, "Hallazgos"));
  trayPanel.append(
    element(
      "p",
      "muted",
      entries.length === 0
        ? "Ningún hallazgo que revisar en este análisis."
        : `${entries.length} ${entries.length === 1 ? "hallazgo" : "hallazgos"}, de mayor a menor gravedad. Cada tarjeta lleva a la ` +
            "sección que la explica; el estado de la revisión se guarda en este dispositivo.",
    ),
  );
  if (reviewSession !== null) trayPanel.append(reviewSession.panel);
  const chips = element("div", "seg theme-filter");
  chips.setAttribute("role", "group");
  chips.setAttribute("aria-label", "Tema");
  for (const [value, label] of [["", "Todos los temas"], ...THEMES.map((theme) => [theme, THEME_LABEL[theme]] as const)] as const) {
    const chip = element("button", undefined, label);
    chip.type = "button";
    chip.dataset["theme"] = value;
    chip.setAttribute("aria-pressed", String(value === trayTheme));
    chip.addEventListener("click", () => setTrayTheme(value));
    chips.append(chip);
  }
  trayPanel.append(chips, tray);

  let rank: FindingRank | null = null;
  let group: HTMLElement | null = null;
  for (const entry of entries) {
    const kind = kindOf(entry);
    if (kind.rank !== rank) {
      rank = kind.rank;
      group = element("div", "tray-group");
      group.dataset["rank"] = String(rank);
      if (rank === 3) {
        // Limpieza y contexto va plegado (propietario, 2026-10-17): lo que puede parar la planta o
        // degradar el circuito se ve entero; esto se abre a demanda, con su recuento en el pliegue.
        const fold = element("details", "tray-fold");
        const count = entries.filter((other) => kindOf(other).rank === 3).length;
        fold.append(element("summary", undefined, `${RANK_LABEL[rank]}: ${count} ${count === 1 ? "hallazgo" : "hallazgos"}`));
        fold.append(group);
        tray.append(fold);
      } else {
        group.append(element("h3", "tray-rank", RANK_LABEL[rank]));
        tray.append(group);
      }
    }
    const card = entry.card;
    card.dataset["theme"] = kind.theme;
    card.dataset["tab"] = entry.tab;
    const where = element("p", "finding-where");
    const chip = element("span", "chip", `${THEME_LABEL[kind.theme]} · ${kind.label}`);
    where.append(chip);
    // Las tarjetas de «Más hallazgos del periodo» nacen en el Resumen y su sección queda vacía al
    // llevarlas a la bandeja: «Ver evidencia» apuntaría a un título sin nada debajo, así que no se ofrece.
    if (card.dataset["uncarded"] !== "si") {
      const link = element("a", "evidence", "Ver evidencia");
      link.href = `#${entry.tab}`;
      link.setAttribute("aria-label", `Ver evidencia en «${entry.sectionTitle}»`);
      link.addEventListener("click", (event) => {
        event.preventDefault();
        activateTab(entry.tab);
        const target = document.getElementById(entry.sectionId);
        (target ?? tabPanels.get(entry.tab))?.scrollIntoView({ block: "start" });
        if (target instanceof HTMLElement) {
          target.tabIndex = -1;
          target.focus({ preventScroll: true });
        }
      });
      where.append(" ", link);
    }
    card.prepend(where);
    // El control de revisión cierra la tarjeta: lo que se añadió después (las mediciones de una
    // incidencia) va antes que él.
    const review = card.querySelector(":scope > .review");
    if (review !== null) card.append(review);
    group?.append(card);
  }
  reviewSession?.onChange(applyTrayFilter);
  applyTrayFilter();
}

/**
 * «Problemas detectados» (UX_SPEC §4.7): al principio de Tags, AGV y WiFi, el índice de lo que esa
 * pestaña encontró, por tipo —con su rango, cuántos y en qué sección—, para ir directo a una
 * anomalía sin recorrer la pestaña (propietario, 2026-10-17: «quiero orden para poder ir directo a
 * buscar una anomalía»). Las tarjetas siguen en la bandeja del Resumen; esto es el índice.
 */
function renderProblemIndex(
  tab: TabId,
  panel: HTMLElement,
  entries: readonly { readonly card: HTMLElement; readonly sectionId: string; readonly sectionTitle: string }[],
): void {
  if (tab !== "tags" && tab !== "agv" && tab !== "wifi") return;
  const heading = panel.querySelector(":scope > h2");
  if (heading === null) return;
  const block = element("div", "problem-index");
  block.append(element("h3", undefined, "Problemas detectados"));
  interface Row {
    readonly label: string;
    readonly rank: FindingRank;
    count: number;
    readonly sectionId: string;
    readonly sectionTitle: string;
  }
  const rows = new Map<string, Row>();
  for (const entry of entries) {
    const kindId = entry.card.dataset["kind"] ?? "";
    const kind = findingKindOf(kindId);
    const row = rows.get(kindId);
    if (row === undefined) rows.set(kindId, { label: kind.label, rank: kind.rank, count: 1, sectionId: entry.sectionId, sectionTitle: entry.sectionTitle });
    else row.count += 1;
  }
  if (rows.size === 0) {
    block.append(element("p", "muted", "Ningún problema detectado en esta pestaña con lo cargado."));
    heading.after(block);
    return;
  }
  const total = entries.length;
  block.append(
    element(
      "p",
      "muted",
      `${total} ${total === 1 ? "hallazgo" : "hallazgos"} en ${rows.size} ${rows.size === 1 ? "tipo" : "tipos"}, de mayor a menor gravedad. Cada línea lleva a la sección que lo detalla; las tarjetas están en el Resumen.`,
    ),
  );
  const list = element("ul", "problem-list");
  const sorted = [...rows.values()].sort((a, b) => a.rank - b.rank || b.count - a.count || a.label.localeCompare(b.label));
  for (const row of sorted) {
    const item = element("li");
    item.dataset["rank"] = String(row.rank);
    const strong = element("strong", undefined, `${row.count} × ${row.label}`);
    item.append(strong, ` — ${RANK_LABEL[row.rank].toLowerCase()} · `);
    const link = element("a", undefined, `ver en «${row.sectionTitle}»`);
    link.href = `#${tab}`;
    const sectionId = row.sectionId;
    link.addEventListener("click", (event) => {
      event.preventDefault();
      const target = document.getElementById(sectionId);
      (target ?? panel).scrollIntoView({ block: "start" });
      if (target instanceof HTMLElement) {
        target.tabIndex = -1;
        target.focus({ preventScroll: true });
      }
    });
    item.append(link);
    list.append(item);
  }
  block.append(list);
  heading.after(block);
}

function setTrayTheme(theme: Theme | ""): void {
  trayTheme = theme;
  for (const chip of trayPanel.querySelectorAll<HTMLButtonElement>(".theme-filter button")) {
    chip.setAttribute("aria-pressed", String((chip.dataset["theme"] ?? "") === theme));
  }
  applyTrayFilter();
}

/** Esconde las tarjetas que no pasan el filtro de tema ni el de estado, y los rangos que quedan vacíos. */
function applyTrayFilter(): void {
  const state = reviewSession?.filter() ?? "";
  for (const group of tray.querySelectorAll<HTMLElement>(".tray-group")) {
    let visible = 0;
    for (const card of group.querySelectorAll<HTMLElement>(".finding.reviewable")) {
      const shown =
        (trayTheme === "" || card.dataset["theme"] === trayTheme) && (state === "" || card.dataset["review"] === state);
      card.hidden = !shown;
      if (shown) visible += 1;
    }
    group.hidden = visible === 0;
    const fold = group.closest<HTMLElement>(".tray-fold");
    if (fold !== null) fold.hidden = visible === 0;
  }
}

// --- La frase de estado, los saltos y el informe (UX_SPEC §4.8) -----------------------------------

let statusHost: HTMLElement | null = null;
let currentStatus: Status | null = null;

/**
 * La conclusión antes de las cifras (FR-028), en una frase con el tono reservado al riesgo; debajo, el
 * contexto y los saltos locales —Avisos, Anillo, Hallazgos— y el botón del informe. Se rehace con cada
 * marca de revisión: lo pendiente es lo que cuenta.
 */
function renderStatus(views: CircuitViews): void {
  const host = statusHost;
  if (host === null) return;
  host.replaceChildren();
  const cards = [...tray.querySelectorAll<HTMLElement>(".finding.reviewable")];
  const pending = (rank: string): number =>
    cards.filter((card) => card.closest<HTMLElement>(".tray-group")?.dataset["rank"] === rank && (card.dataset["review"] ?? "pendiente") === "pendiente").length;
  const reviewed = cards.filter((card) => (card.dataset["review"] ?? "pendiente") !== "pendiente").length;
  const notices = noticesHost?.querySelectorAll(".notices li").length ?? 0;
  const coverageHours = state.coverage.length === 0 ? null : state.coverage.reduce((sum, span) => sum + Math.max(0, span.to - span.from), 0) / 3_600_000;
  const last = views.fleet.counts[views.fleet.counts.length - 1];
  const status = statusSentence({
    pendingRank1: pending("1"),
    pendingRank2: pending("2"),
    totalFindings: cards.length,
    reviewed,
    notices,
    coverageHours,
    agvInCircuit: last?.inCircuit ?? null,
    agvAssigned: last?.assigned ?? null,
  });
  currentStatus = status;
  const headline = element("p", `status status-${status.tone}`, status.headline);
  headline.setAttribute("role", "status");
  host.append(headline, element("p", "muted status-detail", status.detail));

  const jumps = element("nav", "local-jumps");
  jumps.setAttribute("aria-label", "Saltos del Resumen");
  const jump = (label: string, go: () => void): void => {
    const button = element("button", undefined, label);
    button.type = "button";
    button.addEventListener("click", go);
    jumps.append(button);
  };
  jump("Avisos", () => noticesHost?.scrollIntoView({ block: "start" }));
  jump("Anillo", () => jumpTo("resumen", "Anillo del circuito"));
  jump("Hallazgos", () => trayPanel.scrollIntoView({ block: "start" }));
  const report = element("button", "primary", "Generar informe…");
  report.type = "button";
  report.addEventListener("click", () =>
    openReportDialog(
      () => reportSource(views),
      (name) =>
        showMessage("info", "Informe generado", [
          `Descargado como «${name}». Se abre en cualquier navegador sin red; «Imprimir → Guardar como PDF» lo convierte en PDF.`,
        ]),
    ),
  );
  jumps.append(report);
  host.append(jumps);
}

/** Lo que el informe necesita de la página: las pestañas tal como están, la bandeja y la trazabilidad. */
function reportSource(views: CircuitViews): ReportSource {
  const panelOf = (id: TabId): HTMLElement => viewsOf.get(id) as HTMLElement;
  return {
    circuitId: state.circuitId ?? "circuito",
    appVersion: APP_VERSION,
    coverage: state.coverage,
    files: views.snapshots.list.map((entry) => entry.fileName),
    memoryVersion: views.memory?.current ?? null,
    status: currentStatus ?? statusSentence({ pendingRank1: 0, pendingRank2: 0, totalFindings: 0, reviewed: 0, notices: 0, coverageHours: null, agvInCircuit: null, agvAssigned: null }),
    tiles,
    notices: noticesHost,
    tray,
    sections: [
      { id: "tags", label: "Tags", panel: panelOf("tags"), available: true },
      { id: "agv", label: "AGV", panel: panelOf("agv"), available: true },
      { id: "wifi", label: "WiFi", panel: panelOf("wifi"), available: views.connections !== undefined },
      { id: "tiempos", label: "Tiempos", panel: panelOf("tiempos"), available: true },
      { id: "linea", label: "Línea y calles", panel: panelOf("linea"), available: true },
      { id: "memoria", label: "Memoria", panel: tabPanels.get("memoria") as HTMLElement, available: true },
    ],
    cases: state.cases?.cases ?? [],
    formatInstant,
  };
}

// --- Avisos del Resumen (UX_SPEC §4.6) -------------------------------------------------------------

let noticesHost: HTMLElement | null = null;

/**
 * Los avisos: lo que no es un hallazgo del circuito pero condiciona lo que se puede afirmar de él, y
 * que hasta 3.70.0 se quedaba dentro de cada sección. Dos clases, en una sola lista en el Resumen:
 *
 * - **la configuración que no se pudo usar**: las tarjetas sin clave de revisión que cada sección
 *   planta («Punto crítico declarado que no se pudo usar», «Corte de vuelta declarado…»), tal cual,
 *   con el enlace a la tarjeta;
 * - **lo que falta o contradice**: sin lista del circuito, sin registro de conexiones, sin historial
 *   de flota, varios circuitos en el fichero, y lo que la lista dice distinto de las lecturas
 *   (declarados sin lecturas, en otro sitio, leídos sin estar en la lista).
 *
 * Cada aviso lleva a su sitio (propietario, 2026-10-17: «cuando se quiera acceder a alguno te
 * mandará automáticamente a su ubicación»). No se inventa gravedad: la lista va en el orden en que
 * se recogen, y sin avisos lo dice.
 */
function renderNotices(views: CircuitViews): void {
  const host = noticesHost;
  if (host === null) return;
  host.replaceChildren();
  interface Notice {
    readonly text: string;
    readonly go: (() => void) | null;
    readonly where: string;
  }
  const notices: Notice[] = [];
  const taken = new Set<string>();
  // 1. Las tarjetas de configuración de cada sección, con su sección.
  for (const tab of TAB_IDS) {
    const panel = viewsOf.get(tab) as HTMLElement;
    const tabLabel = TABS.find(([id]) => id === tab)?.[1] ?? tab;
    for (const card of panel.querySelectorAll<HTMLElement>(".finding:not(.reviewable)")) {
      const title = card.querySelector(".finding-title")?.textContent ?? "";
      if (!/que no se pudo usar$/.test(title)) continue;
      const detail = [...card.querySelectorAll<HTMLElement>(":scope > p")].map((p) => p.textContent ?? "").filter((text) => text !== "" && text !== "—" && text !== title);
      if (card.id === "") card.id = headingId(`aviso ${title} ${detail.join(" ")}`, taken);
      const id = card.id;
      notices.push({
        text: `${title}: ${detail.join(" ")}`,
        where: tabLabel,
        go: () => {
          activateTab(tab);
          const target = document.getElementById(id);
          target?.scrollIntoView({ block: "center" });
          if (target instanceof HTMLElement) {
            target.tabIndex = -1;
            target.focus({ preventScroll: true });
          }
        },
      });
    }
  }
  // 2. Lo que falta o contradice, con las cifras de las vistas.
  if (views.cohorts.length > 1) {
    notices.push({ text: `${views.cohorts.length} circuitos detectados en el mismo fichero: cada uno se compara solo con los suyos.`, where: "Resumen", go: null });
  }
  const order = views.circuitOrder;
  if (order === undefined || !order.evaluated) {
    notices.push({ text: "Sin lista «circuito» cargada: no hay contraste con Vsystem ni orden declarado; el anillo sale solo de las lecturas.", where: "Datos", go: () => jumpTo("datos", "Listas del circuito") });
  } else {
    const n = (count: number, one: string, many: string): string => `${count} ${count === 1 ? one : many}`;
    const go = (): void => jumpTo("tags", "Orden del circuito según las lecturas");
    if (order.summary["sin-lecturas"] > 0) notices.push({ text: `${n(order.summary["sin-lecturas"], "tag declarado sin ninguna lectura", "tags declarados sin ninguna lectura")}: en la lista y en ningún AGV.`, where: "Tags", go });
    if (order.summary["otro-sitio"] > 0) notices.push({ text: `${n(order.summary["otro-sitio"], "tag en otro sitio que en la lista", "tags en otro sitio que en la lista")}: las lecturas lo colocan en otra posición.`, where: "Tags", go });
    if (order.summary["no-en-la-lista"] > 0) notices.push({ text: `${n(order.summary["no-en-la-lista"], "tag leído que la lista no tiene", "tags leídos que la lista no tiene")}.`, where: "Tags", go });
  }
  if (views.fleet.historySource !== "historial") {
    notices.push({
      text: views.fleet.historySource === "historial-vacio" ? "Historial de flota cargado sin ningún periodo válido: los AGV del circuito son los vistos en las lecturas." : "Sin historial de flota: los AGV del circuito son los vistos en las lecturas, y un asignado que no lee no se puede ver.",
      where: "AGV",
      go: () => jumpTo("agv", "Flota del circuito"),
    });
  }
  if (views.connections === undefined) {
    notices.push({ text: "Sin registro de conexiones: no hay evidencia directa de la WiFi. Se carga en «Listas del circuito», un fichero por AGV.", where: "Datos", go: () => jumpTo("datos", "Listas del circuito") });
  }

  host.append(element("h3", undefined, "Avisos"));
  if (notices.length === 0) {
    host.append(element("p", "muted", "Ningún aviso: toda la configuración cargada se pudo usar y nada falta de lo que el análisis sabe pedir."));
    return;
  }
  host.append(
    element("p", "muted", `${notices.length} ${notices.length === 1 ? "aviso" : "avisos"}: lo que falta, contradice o no se pudo usar. No son hallazgos del circuito; cada uno lleva a su sitio.`),
  );
  const list = element("ul", "notices");
  for (const notice of notices) {
    const item = element("li");
    item.append(notice.text);
    if (notice.go !== null) {
      const link = element("a", undefined, `ver en ${notice.where}`);
      link.href = "#";
      const go = notice.go;
      link.addEventListener("click", (event) => {
        event.preventDefault();
        go();
      });
      item.append(" ", link);
    }
    list.append(item);
  }
  host.append(list);
}

/** La frase que acompaña a cada patrón. Enuncia la pregunta; no la responde (R-EVI-006). */
function explain(tag: TagRow, readers: VehicleReadingView["tags"][number] | undefined): string {
  const few = (ids: readonly string[]): string => (ids.length > 3 ? `${ids.slice(0, 3).join(", ")}…` : ids.join(", "));
  let base: string;
  if (tag.pattern !== "bimodal-candidato") {
    base = "Todos los AGV lo leen poco";
  } else if (readers === undefined) {
    base = `${tag.lowReaders.length} AGV casi nunca lo leen (${few(tag.lowReaders)}) y ${tag.highReaders.length} casi siempre`;
  } else {
    // Quién no lo lee nunca, quién dejó de leerlo y quién lo lee poco: la diferencia, sin causa. «Nunca»
    // lleva sus pasadas —0 de 5 no pesa lo mismo que 0 de 41—, y el verbo concuerda con uno o varios.
    const verb = (count: number, one: string, many: string): string => `${count} ${count === 1 ? one : many}`;
    const parts: string[] = [];
    if (readers.never.length > 0) {
      const never = [...readers.never].sort((a, b) => b.passes - a.passes);
      const shown = never.slice(0, 3).map((entry) => `${entry.agvId}: 0 de ${entry.passes}`);
      parts.push(
        `${verb(readers.never.length, "AGV no lo lee nunca", "AGV no lo leen nunca")} (${shown.join(", ")}${never.length > 3 ? "…" : ""})`,
      );
    }
    if (readers.stopped.length > 0) {
      parts.push(`${verb(readers.stopped.length, "dejó de leerlo", "dejaron de leerlo")} (${few(readers.stopped)})`);
    }
    if (readers.weak.length > 0) {
      const weak = [...readers.weak].sort((a, b) => a.hits / a.passes - b.hits / b.passes);
      const shown = weak.slice(0, 3).map((entry) => `${entry.agvId}: ${percent(entry.hits / entry.passes)}`);
      parts.push(`${verb(readers.weak.length, "lo lee poco", "lo leen poco")} (${shown.join(", ")}${weak.length > 3 ? "…" : ""})`);
    }
    parts.push(verb(readers.good, "lo lee bien", "lo leen bien"));
    base = parts.join("; ");
  }
  // Cómo se probó el paso importa tanto como el porcentaje: una tasa sostenida por tiempo es más
  // débil que una sostenida por los vecinos, y el usuario tiene que poder verlo sin preguntar.
  const vias: string[] = [];
  if (tag.byTime > 0) vias.push(`${tag.byTime} ${tag.byTime === 1 ? "se deduce" : "se deducen"} por el tiempo`);
  if (tag.byOrder > 0) vias.push(`${tag.byOrder} por el orden de los AGV`);
  if (tag.unproven > 0) vias.push(`${tag.unproven} sin confirmar`);
  return vias.length === 0 ? base : `${base}. De las pasadas, ${vias.join(", ")}.`;
}

/** La matriz entera, plegada y construida solo si alguien la abre. */
function renderFullMatrix(matrix: Matrix): void {
  const vehicles = matrix.vehicles.map((vehicle) => vehicle.agvId);
  out.append(
    lazyTable(
      `Ver la matriz completa: ${matrix.tags.length} tags × ${vehicles.length} AGV`,
      () =>
        scrollBox(
          plainTable(
            ["Tag", ...vehicles],
            matrix.tags.map((tag) => {
              const byVehicle = new Map(tag.byVehicle.map((cell) => [cell.agvId, cell]));
              return [
                tag.tagId,
                ...vehicles.map((agvId) => {
                  const cell = byVehicle.get(agvId);
                  // Sin pasadas no es un cero: es que ese vehículo no pasó por ahí.
                  return cell === undefined ? "·" : `${Math.round((cell.hits / cell.passes) * 100)}`;
                }),
              ];
            }),
          ),
        ),
    ),
  );
  out.append(
    element(
      "p",
      "muted",
      "«·»: ese AGV no pasó por ese punto (no es un 0).",
    ),
  );
}

/**
 * Expediente por identificador (UX_SPEC §4.1): busca primero en AGV, después en tags, y muestra lo
 * que encuentre. Un identificador que no aparece en ninguno de los dos se dice, no se calla.
 */
function renderDossier(): void {
  const query = dossierInput.value.trim();
  dossierResult.replaceChildren();
  if (query === "" || state.views === null) {
    // Sin búsqueda no hay expediente que enseñar: el panel se queda fuera de la pestaña AGV.
    dossierPanel.hidden = true;
    return;
  }
  dossierPanel.hidden = false;

  const agv = state.views.agvDossiers.find((entry) => entry.agvId === query);
  const tag = state.views.tagDossiers.find((entry) => entry.tagId === query);

  if (agv === undefined && tag === undefined) {
    dossierResult.append(
      element("p", "muted", `Ningún AGV ni tag con el número «${query}» (se compara entero, con sus ceros).`),
    );
    return;
  }

  if (agv !== undefined) {
    const anchorLabel = "Vueltas";
    const rows: readonly (readonly [string, string])[] = [
      ["Grupo del circuito", agv.cohortId === null ? "sin grupo reconocible" : `${agv.cohortSize} AGV`],
      [
        "Lecturas",
        `${agv.readingCount.toLocaleString("es-ES")} — mediana del resto: ` +
          agv.cohortMedianReadings.toLocaleString("es-ES"),
      ],
      [
        anchorLabel,
        `${agv.laps.completas} completas, ${agv.laps.parciales} parciales, ${agv.laps.desconocidas} sin determinar`,
      ],
      [
        "Última lectura",
        agv.lastReading === null
          ? "ninguna"
          : `${agv.lastReading.tagId} — ${formatInstant(agv.lastReading.utcMs)}`,
      ],
      [
        "Sin leer hasta el final",
        agv.openSilenceSinceUtcMs === null
          ? "no"
          : `sí, desde ${formatInstant(agv.openSilenceSinceUtcMs)}`,
      ],
    ];
    const list = element("dl", "facts");
    for (const [term, value] of rows) list.append(element("dt", undefined, term), element("dd", undefined, value));
    dossierResult.append(element("h3", undefined, `AGV ${agv.agvId}`), list);
    const activity = state.views?.activity;
    const activityRow = activity?.rows.find((row) => row.agvId === agv.agvId);
    if (activity !== undefined && activityRow !== undefined) {
      dossierResult.append(
        agvTimelineChart(
          {
            agvId: agv.agvId,
            bins: activityRow.bins,
            binStarts: activity.binStarts,
            binWidthMs: activity.binWidthMs,
            uncoveredBins: activity.uncoveredBins,
            inactivity: agv.inactivity,
            coverage: state.coverage,
          },
          FORMATS,
        ),
      );
    }
    if (agv.inactivity.length > 0) {
      const cargas = agv.inactivity.filter((period) => period.cause === "carga-online").length;
      dossierResult.append(
        element(
          "p",
          "muted",
          `${agv.inactivity.length} periodos de inactividad` +
            (cargas === 0
              ? ". "
              : `, ${cargas} de ellos cargas en calle. `) +
            "Para cada uno, por dónde se fue y por dónde volvió.",
        ),
      );
      dossierResult.append(
        plainTable(
          ["Desde", "Hasta", "Duración", "Se fue por", "Volvió por", "Qué fue"],
          agv.inactivity.map((period) => [
            formatInstant(period.fromUtcMs),
            formatInstant(period.toUtcMs),
            `${Math.round(period.durationMs / 60_000)} min`,
            period.lastTagBefore,
            period.firstTagAfter,
            period.cause === "carga-online"
              ? `carga en «${period.laneId ?? "?"}» (inferido)`
              : period.lastTagBefore === period.firstTagAfter
                ? "silencio, reapareció en el mismo tag"
                : "silencio, reapareció más adelante",
          ]),
        ),
      );
    }
  }

  if (tag !== undefined) {
    dossierResult.append(
      element("h3", undefined, `Tag ${tag.tagId}`),
      element("p", "muted", `${tag.totalReadings.toLocaleString("es-ES")} lecturas en total.`),
    );
    if (tag.criticalFunction !== null) {
      dossierResult.append(
        element(
          "p",
          undefined,
          `Punto crítico declarado: ${criticalFunctionLabel(tag.criticalFunction)}` +
            // Qué es esa función en planta, en palabras del propietario, cuando el nombre no lo dice solo.
            (FUNCTION_MEANING[tag.criticalFunction] === undefined ? "" : ` (${FUNCTION_MEANING[tag.criticalFunction]})`) +
            ". Se cambia en el fichero de listas y volviéndolo a cargar.",
        ),
      );
    }
    const declaredInfo = currentTagInfo[tag.tagId];
    if (declaredInfo !== undefined) dossierResult.append(element("p", undefined, `Declarado en planta: ${declaredInfo}.`));
    dossierResult.append(
      plainTable(
        ["AGV", "Última lectura"],
        tag.readers.map((reader) => [
          reader.agvId,
          reader.lastReadUtcMs === null ? "nunca" : formatInstant(reader.lastReadUtcMs),
        ]),
      ),
    );
  }
}

/** Prepara el control temporal del replay para el rango de fotogramas recibido. */
function renderReplaySkeleton(): void {
  const frames = state.views?.replay ?? [];
  replayPanel.hidden = frames.length === 0;
  if (frames.length === 0) return;
  replaySlider.max = String(frames.length - 1);
  replaySlider.value = "0";
  renderReplayFrame();
}

/** Dibuja el fotograma seleccionado: qué tag o qué tramo, y con qué estado de verdad. */
function renderReplayFrame(): void {
  const frames = state.views?.replay ?? [];
  const index = Math.min(frames.length - 1, Math.max(0, Number.parseInt(replaySlider.value, 10)));
  const frame = frames[index];
  replayTable.replaceChildren();
  if (frame === undefined) return;

  replayTime.textContent = formatInstant(frame.atUtcMs);
  // Primero lo que tiene posición, después lo que no: ordenar por identificador enterraba a los
  // pocos vehículos en movimiento entre decenas de filas sin nada que mirar.
  const RANK = { "en-transito": 0, "en-tag": 1, silencio: 2, "sin-datos": 3 } as const;
  const rows = [...frame.vehicles]
    .sort(([agvA, a], [agvB, b]) =>
      RANK[a.kind] !== RANK[b.kind] ? RANK[a.kind] - RANK[b.kind] : agvA.localeCompare(agvB),
    )
    .map(([agvId, vehicleState]): readonly string[] => {
      switch (vehicleState.kind) {
        case "en-tag":
          return [agvId, vehicleState.tagId, "—", truthLabel(vehicleState.truth)];
        case "en-transito":
          return [
            agvId,
            vehicleState.fromTagId,
            `→ ${vehicleState.toTagId} (${Math.round(vehicleState.fraction * 100)} %)`,
            vehicleState.truth,
          ];
        case "silencio":
          return [
            agvId,
            vehicleState.lastTagId,
            `silencio desde ${formatInstant(vehicleState.sinceUtcMs)}`,
            truthLabel("unknown"),
          ];
        default:
          // Antes de su primera lectura no hay silencio que diagnosticar: hay ausencia de datos
          // para ese vehículo, y lo que sí se sabe es cuándo deja de haberla.
          return [
            agvId,
            "—",
            `aún sin lecturas; la primera, a las ${formatInstant(vehicleState.firstReadingUtcMs)}`,
            "sin datos",
          ];
      }
    });
  replayTable.append(plainTable(["AGV", "Tag", "Tránsito / silencio", "Estado"], rows));
}


exportButton.addEventListener("click", () => {
  exportsInFlight += 1;
  syncUpdateBusy();
  void (async () => {
    const circuitId = circuitInput.value.trim();
    if (circuitId === "") {
      showMessage("warn", "Falta el circuito", ["Escribe el nombre del circuito que quieres exportar."]);
      return;
    }
    if (!isAvailable()) {
      showMessage("error", "No hay almacén local", [
        "Este navegador no permite guardar datos, así que no hay nada que exportar.",
      ]);
      return;
    }
    const circuit = await loadCircuit(circuitId);
    if (circuit === undefined) {
      showMessage("warn", "Ese circuito no existe todavía", [
        "Importa primero un fichero de lecturas en ese circuito.",
      ]);
      return;
    }
    // Sin el bruto, por ADR-0012: el dispositivo acumula las lecturas y el fichero lleva el
    // proyecto. Meter doscientas mil lecturas en cada exportación haría el fichero inmanejable
    // sin añadir nada que el dispositivo de destino no pueda volver a importar.
    // La revisión en campo viaja con el proyecto: es trabajo humano, lo único que no se puede
    // volver a calcular importando otra vez las fuentes. Sin marcas, el fichero queda como antes.
    const reviews = [...(await loadReviews(circuitId)).values()];
    // Y las instantáneas de cada fichero (ADR-0015 §5): el circuito viaja con su evolución y sin su bruto.
    const snapshots = await loadSnapshots(circuitId);
    // Y la memoria consolidada (F4, §10-§11): las versiones con su cadena de hashes, para que el
    // destino clasifique el linaje al abrirlo. Sin versiones, la sección no viaja.
    const memoria = await exportProjectMemory(circuitId);
    // Y el plano físico (ADR-0016, esquema 4): sus eventos append-only. Sin plano, la sección no viaja.
    const plano = await exportProjectPlan(circuitId);
    // Y los valores de planta confirmados (OQ-140, esquema 5). Sin ninguno, la sección no viaja.
    const valores = await exportProjectPlantValues(circuitId);
    // Y los expedientes de incidencia con sus recortes (OQ-174, esquema 6): la copia para recuperarlos.
    const incidencias = await exportProjectIncidents(circuitId);
    const bytes = await writeProject(
      circuitId,
      {
        circuito: { id: circuit.circuitId, nombre: circuit.name, zona: circuit.zone },
        fuentes: circuit.sources,
        cobertura: circuit.coverage,
        instantaneas: snapshots,
        ...(reviews.length === 0 ? {} : { revision: reviews }),
        ...(memoria === undefined ? {} : { memoria }),
        ...(plano === undefined ? {} : { plano }),
        ...(valores === undefined ? {} : { valores }),
        ...(incidencias === undefined ? {} : { incidencias }),
      },
      Date.now(),
    );
    // La confirmación se muestra **antes** de disparar la descarga, no después. Al revés, esta
    // línea se ejecuta cuando el navegador ya ha entregado el fichero, y si para entonces el
    // usuario ya ha hecho otra cosa —abrir un proyecto, por ejemplo— le pisa su mensaje con uno
    // que corresponde a la acción anterior.
    showMessage("info", "Proyecto exportado", [
      `${circuit.sources.length} ficheros y sus periodos, con ${snapshots.length} ${snapshots.length === 1 ? "instantánea" : "instantáneas"}. ` +
        "Las lecturas se quedan en este dispositivo.",
      ...(reviews.length === 0 ? [] : [`Incluye ${reviews.length} hallazgos revisados en campo.`]),
      ...(memoria === undefined ? [] : [`Incluye la memoria consolidada: ${memoria.versiones.length} ${memoria.versiones.length === 1 ? "versión" : "versiones"}.`]),
      ...(plano === undefined ? [] : [`Incluye el plano del circuito: ${plano.eventos.length} ${plano.eventos.length === 1 ? "cambio registrado" : "cambios registrados"}.`]),
      ...(valores === undefined ? [] : [`Incluye los valores de planta confirmados: ${valores.eventos.length} ${valores.eventos.length === 1 ? "valor" : "valores"}.`]),
      ...(incidencias === undefined
        ? []
        : (() => {
            const cases = new Set(incidencias.revisiones.map((revision) => revision.caseId)).size;
            return [`Incluye ${cases === 1 ? "el expediente de incidencia" : `los ${cases} expedientes de incidencia`}, con sus revisiones y recortes.`];
          })()),
    ]);
    const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "application/zip" }));
    const link = element("a");
    link.href = url;
    link.download = `${circuitId}.agvproj`;
    link.click();
    URL.revokeObjectURL(url);
  })().finally(() => {
    exportsInFlight -= 1;
    syncUpdateBusy();
  });
});

projectInput.addEventListener("change", () => {
  void (async () => {
    const file = projectInput.files?.[0];
    if (file === undefined) return;
    clearMessages();
    try {
      const project = await readProject(new Uint8Array(await file.arrayBuffer()));
      const circuito = project.sections["circuito"] as { nombre?: string } | undefined;
      const fuentes = project.sections["fuentes"] as readonly unknown[] | undefined;
      const cobertura = project.sections["cobertura"] as
        | readonly { from: number; to: number }[]
        | undefined;
      const revision = project.sections["revision"] as readonly { state?: string }[] | undefined;
      const instantaneas = project.sections["instantaneas"] as readonly unknown[] | undefined;
      // La memoria consolidada que trae (F4, §10): su forma se comprueba antes de tocar nada, y la
      // relación con la local —idéntica, local por delante, entrante por delante (se adopta) o
      // bifurcada (hay que elegir)— la clasifica y la guarda la persistencia. Nada se fusiona.
      const memoria = readMemorySection(project);
      const imported = isAvailable() ? await importProjectMemory(project.manifest.circuit_id, memoria) : null;
      const lineage = imported?.relation ?? null;
      // Y el plano físico (ADR-0016): su forma se comprueba antes de tocar nada; solo entran eventos
      // si el plano local es un prefijo exacto del entrante. Dos planos distintos no se mezclan.
      const plano = readPlanSection(project);
      const planImport = isAvailable() ? await importProjectPlan(project.manifest.circuit_id, plano) : null;
      // Y los valores de planta confirmados (OQ-140): mismo criterio que el plano, nada se mezcla.
      const valores = readPlantValuesSection(project);
      const valuesImport = isAvailable() ? await importProjectPlantValues(project.manifest.circuit_id, valores) : null;
      // Y los expedientes (OQ-174): expediente a expediente, sin mezclar dos historiales.
      const incidencias = readIncidentsSection(project);
      const casesImport = isAvailable() ? await importProjectIncidents(project.manifest.circuit_id, incidencias) : null;
      showMessage("info", `Proyecto «${circuito?.nombre ?? project.manifest.circuit_id}»`, [
        `${fuentes?.length ?? 0} fuentes declaradas, exportado el ${formatInstant(project.manifest.exported_at)}.`,
        // Un proyecto del esquema 1 no traía instantáneas: se abre igual y se dice (ADR-0015 §5).
        instantaneas === undefined
          ? "Sin instantáneas: el proyecto es de una versión anterior."
          : `${instantaneas.length} ${instantaneas.length === 1 ? "instantánea" : "instantáneas"} del circuito.`,
        memoria === undefined
          ? "Sin memoria consolidada: el proyecto es de una versión anterior o el circuito no tenía versiones."
          : `Memoria consolidada: ${memoria.versiones.length} ${memoria.versiones.length === 1 ? "versión" : "versiones"}.` +
            (lineage === null
              ? " Sin almacén local no se puede comparar con la de este dispositivo."
              : imported?.pendingForkBlocked === true
                ? // Con una bifurcación pendiente aquí, el proyecto no entra ni sustituye al linaje que espera decisión.
                  " Aquí ya hay una bifurcación sin resolver: este proyecto no se ha adoptado ni sustituye al linaje que espera decisión. Resuélvela en la pestaña Memoria y vuelve a abrir el proyecto."
                : ` ${LINEAGE_LABEL[lineage]}` +
                  (lineage === "bifurcada"
                    ? " Elige cuál sigue en la pestaña Memoria, al importar las lecturas del circuito."
                    : lineage === "entrante-adelantada"
                      ? " Se verá al volver a importar las lecturas del circuito."
                      : "")) +
            (imported === null || imported.archived === 0
              ? ""
              : ` ${imported.archived} ${imported.archived === 1 ? "linaje archivado del otro dispositivo, añadido como archivado" : "linajes archivados del otro dispositivo, añadidos como archivados"}.`),
        planImport === null
          ? plano === undefined
            ? "El proyecto no traía plano del circuito."
            : "Trae el plano del circuito. Sin almacén local no se puede comparar con el de este dispositivo."
          : planRelationText(planImport.relation, planImport.added) +
            (planImport.added > 0 ? " Se verá al volver a importar las lecturas del circuito." : ""),
        valuesImport === null
          ? valores === undefined
            ? "El proyecto no traía valores de planta confirmados."
            : "Trae valores de planta confirmados. Sin almacén local no se pueden comparar con los de este dispositivo."
          : plantValuesRelationText(valuesImport.relation, valuesImport.added) +
            (valuesImport.added > 0 ? " Se aplicarán al volver a importar las lecturas del circuito." : ""),
        ...(casesImport === null
          ? incidencias === undefined
            ? []
            : ["Trae expedientes de incidencia. Sin almacén local no se pueden incorporar."]
          : [
              ...caseImportText(casesImport.plans, casesImport.evidenceRejected),
              ...(casesImport.plans.some((plan) => plan.missing.length > 0) ? ["Los expedientes se verán al volver a importar las lecturas del circuito."] : []),
            ]),
        // Una revocación que llega cambia la versión vigente: se dice, versión por versión (OQ-144).
        ...(imported?.revocations ?? []).map(
          (entry) => `El proyecto trae la revocación de v${entry.version} (${formatInstant(entry.at)}): ${entry.reason}. Aquí queda revocada.`,
        ),
        ...(imported === null || imported.events === 0
          ? []
          : [`${imported.events} ${imported.events === 1 ? "elección de linaje hecha" : "elecciones de linaje hechas"} en otro dispositivo, añadidas al historial.`]),
        cobertura === undefined || cobertura.length === 0
          ? "Sin cobertura declarada."
          : `Cobertura: ${cobertura.map((span) => formatSpan(span.from, span.to)).join("  ·  ")}`,
        ...(revision === undefined || revision.length === 0
          ? []
          : [
              `Revisión en campo: ${revision.length} hallazgos marcados (` +
                ["confirmado", "descartado", "pospuesto"]
                  .map((state) => `${revision.filter((entry) => entry.state === state).length} ${state}s`)
                  .join(", ") +
                ").",
            ]),
        "Integridad verificada: manifiesto y todas sus secciones coinciden con sus hashes.",
      ]);
    } catch (error) {
      if (error instanceof ProjectError) {
        showMessage("error", "No se pudo abrir el proyecto", [error.reason, error.recovery]);
        return;
      }
      if (isStorageFull(error)) {
        showMessage("error", "No hay espacio para abrir el proyecto", [
          "No queda espacio en este dispositivo para guardar: lo de esta operación no se ha escrito.",
          "Libera espacio —exporta y borra los circuitos que ya no uses o libera espacio del dispositivo— y vuelve a abrirlo.",
        ]);
        return;
      }
      showMessage("error", "No se pudo abrir el proyecto", [
        "El fichero no tiene la forma de un `.agvproj`.",
        "Comprueba que es el fichero correcto. El almacén local no se ha tocado.",
      ]);
    }
  })();
});

// --- Almacén: la migración pendiente se hace al abrir, no a mitad de la primera importación --------

{
  let deleted: string | null = null;
  try {
    deleted = sessionStorage.getItem(DELETED_NOTICE);
    sessionStorage.removeItem(DELETED_NOTICE);
  } catch {
    deleted = null;
  }
  if (deleted !== null) {
    showMessage("info", `Circuito «${deleted}» borrado`, ["Se ha borrado entero de este dispositivo. Las copias exportadas (.agvproj) no se tocan."]);
  }
}

if (isAvailable()) {
  void ensureStore().catch(() => {
    // Sin almacén utilizable la aplicación sigue analizando; la primera acumulación dirá que no pudo guardar.
  });
}

// --- Sin red y actualización ------------------------------------------------

// El registro del service worker y el aviso de versión nueva viven en `update.ts` (NFR-011): la
// versión nueva espera a que la persona la pida y no se activa con un trabajo en curso.
